import { resolveSceneModel } from "../utils/sceneModel";
import { scenePreparationIssue, scenePreparationMessage } from "../utils/scenePreparationIssue";
import { buildRows } from "../utils/sceneRows";
import { groundLayoutFromRows, geographicBoundary, groundLayoutFields, editGroundRow, rowPitchStats, groundRowsValid, groundFieldArea } from "../utils/groundRowEditing";
import { moduleQuads, alignModulesToEdge, rotateModulesToAxis, roofModuleArray, resolveRoofModuleOffsets, legacyRoofModuleArray } from "../utils/moduleLayout";
import { modulePositions } from "../utils/moduleShading";
import { invalidSceneEdits, sceneEditFields, sceneFormWithEdits, scenePlaneWithEdits } from "../utils/sharedSceneEdits";
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Snackbar,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Tooltip,
  IconButton,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  PanelShell,
  PanelShellActions,
  PanelShellContent,
  PanelShellTitle,
} from "./PanelShell";
import ShadingScene3D from "./ShadingScene3D";
import RoofSiteEditor from "./RoofSiteEditor";
import { usableRoofAreaM2 } from "./config/orientations";
import { effectiveOrientation } from "../utils/mounting";
import { siteGeometryErrors, obstacleFootprint, roofFootprint, roofHeightRange } from "../utils/siteGeometry";
import { closeRoofVertices, eaveFace, edgeSteps, facingEdge, sharedEdgeIndices, fitRoofJoints, surfaceZ } from "../utils/roofFaces";
import { validPolygon } from "../utils/footprint";
import { sceneOffset } from "../utils/projectScene";
import { tablesOrigin, localXY } from "../utils/contextPlanes";
import SunPathDialog from "./pv/SunPathDialog";
import { fetchLayoutPdf, analyzeParcel, getModuleDetails, getInverterDetails, fitStringsToPlan } from "../services/api";
import StringLayoutMap from "./StringLayoutMap";
import { ringFromParcel, ringAreaM2, describeArea } from "../utils/parcelGeometry";
import { displayName } from "../utils/labels";
import { toFiniteNumber, toFieldValue } from "../utils/formValue";
import { deriveField, describeRows, estimateFieldArea } from "../utils/fieldGeometry";
import { roofInitialGrid, roofLayoutSource, hasSavedRoofLayout, fitRoofRowSpacing } from "../utils/roofInitialGrid";
import { physicalLayoutDesignKey, physicalLayoutCount, layoutDesignChanged, updateLayoutFromDesign, excludedSlotsOf } from "../utils/physicalLayoutDesign";
import { planStringAssignments } from "../utils/planStringAssignments";
import { stringFitErrorMessage } from "../utils/stringFitError";
import { editModulePlan, planEditFields, panelTurnAllowed } from "../utils/editModulePlan";
import { useIsGreek } from "../context/LanguageContext";
import { AccessibleSelectTextField } from "./form/AccessibleSelectFields";

/**
 * The design's strings as parcel-fit slots: how many, and the columns the
 * widest one occupies on a row `tiers` high. A string never crosses a table,
 * so the parcel is fitted in these slots rather than in bare positions --
 * 1,800 positions held 1,800 modules and still left 7 of 72 strings of 25 on
 * two-high rows without a row. Mirrors pv_core.string_layout.string_slots.
 */
function stringSlots(systemDesign, tiers) {
  const t = Math.max(1, Math.round(toFiniteNumber(tiers) || 1));
  let count = 0;
  let columns = 0;
  Object.values(systemDesign || {}).forEach((mppts) =>
    (mppts || []).forEach((m) =>
      (m?.inputs || []).forEach((i) => {
        const n = toFiniteNumber(i?.modules_per_string ?? i?.modulesPerString);
        if (n && n > 0) { count += 1; columns = Math.max(columns, Math.ceil(n / t)); }
      })
    )
  );
  return { count, columns };
}

function CompactLayoutField({ label, helperText, inputProps, unit, InputProps, ...props }) {
  const greek = useIsGreek();
  const id = useId();
  // Tight: the title sits right on its box and the box is a line high, so
  // five of these make a row rather than a form.
  return <Box sx={{ minWidth: 0 }}>
    <Stack direction="row" alignItems="center" spacing={0.25} sx={{ mb: 0.1, minHeight: 20 }}>
      <Typography component="label" htmlFor={id} title={label} noWrap sx={{ fontSize: 12, fontWeight: 600, color: "primary.main", minWidth: 0 }}>
        {label}
      </Typography>
      {helperText && <Tooltip title={helperText} arrow describeChild>
        <IconButton size="small" aria-label={`${greek ? "Πληροφορίες" : "Information"}: ${label}`} sx={{ p: 0.15, flexShrink: 0 }}>
          <InfoOutlinedIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Tooltip>}
    </Stack>
    <TextField {...props} id={id} size="small" inputProps={{ ...inputProps, "aria-label": label }}
      InputProps={unit ? { ...InputProps, endAdornment: <InputAdornment position="end" sx={{ "& p": { fontSize: 12 } }}>{unit}</InputAdornment> } : InputProps}
      sx={{ width: "100%", "& .MuiInputBase-input": { py: 0.55, fontSize: 13 } }} />
  </Box>;
}

function formatMetric(value, digits = 2, unit = "") {
  const num = toFiniteNumber(value);
  if (num == null) return "—";
  return `${num.toFixed(digits)}${unit}`;
}

const layoutSaveFailureMessage = (error, greek, shading = false) =>
  (shading ? "" : (greek ? "Η φυσική διάταξη δεν αποθηκεύτηκε. " : "The physical layout was not saved. ")) +
  scenePreparationMessage(error, greek, { saved: shading });

// Mirrors BESS_feasibility/Configuration/array_geometry.py. Keep the two in
// step: the dialog and the report must not describe the same layout differently.
const SOLAR_DECLINATION_DEG = 23.44;
const DENSE_GCR_FIXED = 0.7;
const TIGHT_PITCH_FRACTION = 0.85;
// A pitch within 1% of the winter-noon requirement is treated as meeting it:
// the criterion is a design rule of thumb, not a physical boundary.
const SHADE_FREE_TOLERANCE = 0.99;
const MIN_TILT_FOR_SHADING_DEG = 2;

function winterNoonElevationDeg(latitudeDeg) {
  const lat = Math.abs(Number(latitudeDeg));
  if (!Number.isFinite(lat)) return null;
  const elevation = 90 - lat - SOLAR_DECLINATION_DEG;
  return elevation > 5 ? elevation : null;
}

/**
 * Advisory notes on a row layout: what it means physically, and whether it
 * shades itself. Returns [] when there is not enough to measure. Never blocks -
 * a dense field can be a deliberate choice, and the report says the same thing
 * about the same numbers.
 */
function evaluateRowSpacing({ gcr, pitchM, tiltDeg, latitudeDeg, mountType, greek = false }) {
  const g = Number(gcr);
  const pitch = Number(pitchM);
  if (!Number.isFinite(g) || !Number.isFinite(pitch) || g <= 0 || pitch <= 0) {
    return { notes: [], severity: null };
  }
  const tilt = Number.isFinite(Number(tiltDeg)) ? Number(tiltDeg) : 0;
  const isFixed = !String(mountType || "fixed").toLowerCase().includes("track");

  const collectorWidth = g * pitch;
  const rad = (tilt * Math.PI) / 180;
  const footprint = collectorWidth * Math.cos(rad);
  const rise = collectorWidth * Math.sin(rad);
  const groundGap = pitch - footprint;

  const elevation = winterNoonElevationDeg(latitudeDeg);
  let shadeFree = null;
  if (elevation != null) {
    shadeFree =
      tilt < MIN_TILT_FOR_SHADING_DEG
        ? footprint
        : footprint + rise / Math.tan((elevation * Math.PI) / 180);
  }
  const fraction = shadeFree && shadeFree > 0 ? pitch / shadeFree : null;

  const notes = [];
  let severity = null;

  if (groundGap <= 0) {
    severity = "error";
    notes.push(
      greek ? `Οι σειρές επικαλύπτονται: η οριζόντια προβολή είναι ${footprint.toFixed(2)} m και το βήμα ${pitch.toFixed(2)} m.` : `The rows overlap: a ${collectorWidth.toFixed(2)} m row at ${tilt}\u00B0 covers ` +
        `${footprint.toFixed(2)} m of ground but the pitch is only ${pitch.toFixed(2)} m.`
    );
  } else if (isFixed && fraction != null && fraction < SHADE_FREE_TOLERANCE) {
    severity = fraction < TIGHT_PITCH_FRACTION ? "warning" : "info";
    notes.push(
      greek ? `Οι σειρές αλληλοσκιάζονται τον χειμώνα. Για αποφυγή σκίασης στο ηλιακό μεσημέρι του χειμερινού ηλιοστασίου απαιτείται βήμα ${shadeFree.toFixed(2)} m (GCR ${(collectorWidth / shadeFree).toFixed(2)}). Το επιλεγμένο βήμα είναι ${pitch.toFixed(2)} m, με καθαρό κενό ${groundGap.toFixed(2)} m. Η προσομοίωση θα συμπεριλάβει την αντίστοιχη απώλεια ακτινοβολίας.` : `These rows shade each other in winter. Clearing the row in front at solar noon on ` +
        `the winter solstice needs a pitch of ${shadeFree.toFixed(2)} m ` +
        `(GCR ${(collectorWidth / shadeFree).toFixed(2)}); this layout uses ` +
        `${pitch.toFixed(2)} m, ${(fraction * 100).toFixed(0)}% of it, leaving ` +
        `${groundGap.toFixed(2)} m of clear ground between rows ${rise.toFixed(2)} m tall. ` +
        `The study will model it as entered and report the reduced plane-of-array irradiance.`
    );
  }

  if (isFixed && g > DENSE_GCR_FIXED) {
    if (severity !== "error" && severity !== "warning") severity = "warning";
    notes.push(
      greek ? `Ο GCR ${g.toFixed(3)} αντιστοιχεί σε πυκνή διάταξη σταθερών πλαισίων. Σε πλαίσια διπλής όψης η πυκνή διάταξη περιορίζει και την ανακλώμενη ακτινοβολία που φτάνει στην πίσω όψη.` : `A GCR of ${g.toFixed(3)} is dense for a fixed-tilt field (0.3-0.5 is typical at ` +
        `utility scale, up to about 0.7 on a constrained roof). On a bifacial module it ` +
        `also suppresses the rear-side gain, because little reflected light reaches the ` +
        `back of a row that nearly touches the next one.`
    );
  }

  return { notes, severity };
}

function buildSpacingInfo(
  source,
  moduleLength,
  moduleWidth,
  totalModules,
  incrementFactor,
  portrait = true
) {
  const spacingInfo = source && typeof source === "object" ? source : {};
  const pitch = toFiniteNumber(
    spacingInfo.minimum_distance_m ?? spacingInfo.pitch_m ?? spacingInfo.pitch
  );
  const rowVerticalModulesRaw = toFiniteNumber(
    spacingInfo.row_vertical_modules ?? spacingInfo.rowVerticalModules
  );
  const rowVerticalModules =
    rowVerticalModulesRaw != null &&
    Number.isInteger(rowVerticalModulesRaw) &&
    rowVerticalModulesRaw > 0
      ? rowVerticalModulesRaw
      : null;
  const moduleLen = toFiniteNumber(moduleLength);
  const moduleWid = toFiniteNumber(moduleWidth);
  const modulesCount = toFiniteNumber(totalModules);
  const marginFactor = toFiniteNumber(incrementFactor) ?? 1.1;
  // The slant height is the module edge that climbs the slope: its length
  // standing up, its width lying down. GCR, the pitch check and the terrace
  // area all hang off this number, and the engine has resolved it this way
  // since Site_spacing_info was corrected - so a landscape array shown here at
  // the portrait height would disagree with its own run by a factor of two.
  const alongSlope = portrait === false ? moduleWid : moduleLen;
  const totalRowHeight =
    alongSlope != null ? alongSlope * rowVerticalModules : null;
  const gcr =
    totalRowHeight != null && pitch > 0 ? totalRowHeight / pitch : null;
  const computedFootprint = estimateFieldArea({
    totalModules: modulesCount,
    moduleLength: moduleLen,
    moduleWidth: moduleWid,
    verticalModules: rowVerticalModules,
    fieldRows: spacingInfo.field_rows,
    pitch,
    tiltDeg: spacingInfo.surface_tilt_deg,
    portrait,
    marginFactor,
  });
  const footprint = toFiniteNumber(
    spacingInfo.terrace_area_m2 ?? spacingInfo.terrace_area
  );

  return {
    mode: spacingInfo.mode || "manual",
    terrace_area_m2: footprint,
    minimum_distance_m: pitch,
    pitch_m: pitch,
    row_vertical_modules: rowVerticalModules,
    gcr,
    computed_terrace_area_m2: computedFootprint,
    increment_factor: marginFactor,
    // Carried through, so a saved row count survives a round trip. Null when
    // unset, which is what tells the viewer to assume a square field.
    field_rows: (() => {
      const n = toFiniteNumber(spacingInfo.field_rows ?? spacingInfo.fieldRows);
      return Number.isInteger(n) && n > 0 ? n : null;
    })(),
    ...(spacingInfo.source ? { source: spacingInfo.source } : {}),
  };
}

function PhysicalLayoutSchema({ spacingInfo, moduleLength, totalModules, groundClearance, onRoof = false, flush = false, roofGapCm = null, singleRow = false }) {
  const theme = useTheme();
  const isGreek = useIsGreek();
  const isDark = theme.palette.mode === "dark";

  const svgTextColor = isDark ? "#e2e8f0" : "#0f172a";
  const svgSecondaryColor = isDark ? "#94a3b8" : "#475569";
  const svgLabelBoxFill = isDark ? "#1e293b" : "#ffffff";
  const svgLabelBoxOpacity = isDark ? "0.96" : "0.97";
  const svgMeasureColor = isDark ? "#e2e8f0" : "#0f172a";
  const svgPitchLabelBoxFill = isDark ? "#1e293b" : "#ffffff";
  const svgSkyStart = isDark ? "rgba(30,58,138,0.22)" : "rgba(191,219,254,0.55)";
  const svgSkyEnd = isDark ? "rgba(15,23,42,0)" : "rgba(255,255,255,0)";

  const pitchLabel = formatMetric(spacingInfo.minimum_distance_m, 2, " m");
  const footprintLabel = formatMetric(spacingInfo.terrace_area_m2, 1, " m²");
  const moduleLengthLabel = formatMetric(moduleLength, 2, " m");
  // One row has nothing to show but the row itself, so the section is a small
  // card: a short, wide sketch with its one sentence beside the figures, not a
  // drawing the height of the form it sits next to.
  // The card hugs the sketch instead of stretching across its column.
  if (singleRow) return <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3, alignSelf: "flex-start", justifySelf: "start", width: "fit-content", maxWidth: "100%" }}>
    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{isGreek ? "Διατομή μίας σειράς" : "Single-row section"}</Typography>
    <Box component="svg" viewBox="0 0 600 170" role="img" aria-label={isGreek ? "Μία σειρά Φ/Β" : "One PV row"}
      sx={{ width: "min(100%, 520px)", display: "block", bgcolor: isDark ? "#1e293b" : "#edf3fa", borderRadius: 2 }}>
      <path d={flush ? "M60 140 L540 62" : "M60 140 H540"} stroke="#94a3b8" strokeWidth="10" />
      {!flush && <path d="M250 140 V94 M380 140 V42" stroke="#475569" strokeWidth="7" />}
      <path d={flush ? "M200 108 L400 75" : "M210 103 L410 27"} stroke="#3975c9" strokeWidth="13" />
      <text x="300" y="163" textAnchor="middle" fontSize="13" fill={svgSecondaryColor}>{isGreek ? "Σχηματική απεικόνιση, όχι σε κλίμακα" : "Schematic view, not to scale"}</text>
    </Box>
    <Typography variant="caption" sx={{ display: "block", mt: 0.5, maxWidth: 520 }}>
      {isGreek ? "Μία σειρά — δεν εφαρμόζεται βήμα μεταξύ σειρών." : "One row — no inter-row pitch."}
      {" "}{isGreek ? "Πλαίσια" : "Modules"}: {totalModules} · {isGreek ? "Ύψος στήριξης" : "Clearance"}: {groundClearance ?? 0.5} m
    </Typography>
  </Paper>;
  if (flush) {
    return (
      <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          {isGreek ? "Αρχική διάταξη παράλληλα στη στέγη" : "Flush roof layout — initial grid"}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          <Chip label={`${isGreek ? "Διάκενο σειρών" : "Gap between rows"} ${formatMetric(roofGapCm, 1, " cm")}`} size="small" variant="outlined" />
          <Chip label={`${isGreek ? "Ύψος από τη στέγη" : "Clearance above roof"} ${formatMetric(groundClearance, 2, " m")}`} size="small" variant="outlined" />
          <Chip label={`${isGreek ? "Μήκος πλαισίου" : "Module length"} ${moduleLengthLabel}`} size="small" variant="outlined" />
          <Chip label={`${isGreek ? "Πλαίσια" : "Modules"} ${Number.isFinite(Number(totalModules)) ? Number(totalModules) : "—"}`} size="small" variant="outlined" />
        </Stack>
        {/* Two modules on rails, parallel to the roof: the two dimensions this
            card sets, the gap between rows along the slope and the clearance
            above the roof. Exaggerated, but the gap is drawn as a gap and not
            as a step, and the clearance is shown at all. */}
        <Box component="svg" viewBox="0 0 900 300" role="img"
          aria-label={isGreek ? "Σχηματική τομή: τα πλαίσια είναι παράλληλα στην κεκλιμένη στέγη" : "Schematic section: modules parallel to the sloped roof"}
          sx={{ width: "100%", maxWidth: 880, display: "block", mx: "auto", borderRadius: 2, bgcolor: isDark ? "#1e293b" : "#edf3fa" }}>
          <polygon points="70,225 830,75 830,145 70,295" fill={isDark ? "#64748b" : "#a5b4c7"} />
          <polyline points="70,225 830,75" fill="none" stroke={isDark ? "#cbd5e1" : "#64748b"} strokeWidth="4" />
          <path d="M227,194 L222.3,170.5 M423.2,155.3 L418.5,131.7 M511.5,137.9 L506.8,114.3 M707.7,99.1 L703.1,75.6" stroke="#475569" strokeWidth="4" />
          <polygon points="192.9,176.3 448,125.9 445.6,114.2 190.6,164.5" fill="#3975c9" />
          <polygon points="477.4,120.1 732.5,69.8 730.2,58 475.1,108.4" fill="#3975c9" />
          <g stroke={svgMeasureColor} strokeWidth="1.6">
            <line x1="441.4" y1="92.6" x2="470.8" y2="86.8" />
            <line x1="444.1" y1="106.3" x2="439.8" y2="84.7" />
            <line x1="473.5" y1="100.5" x2="469.3" y2="78.9" />
            <line x1="732.5" y1="69.8" x2="760" y2="64.4" strokeDasharray="3 3" />
            <line x1="756.8" y1="89.5" x2="752.1" y2="65.9" />
            <line x1="748.9" y1="91" x2="764.6" y2="87.9" />
            <line x1="744.3" y1="67.5" x2="760" y2="64.4" />
          </g>
          <text x="455" y="70" textAnchor="middle" fontSize="16" fontWeight="600" fill={svgTextColor}>{formatMetric(roofGapCm, 1, " cm")}</text>
          <text x="770" y="84" textAnchor="start" fontSize="16" fontWeight="600" fill={svgTextColor}>{formatMetric(groundClearance, 2, " m")}</text>
          <text x="628" y="165" textAnchor="middle" fontSize="16" fill={svgTextColor}>{isGreek ? "Στέγη" : "Roof"}</text>
          <text x="450" y="282" textAnchor="middle" fontSize="14" fill={svgSecondaryColor}>{isGreek ? "Σχηματική απεικόνιση, όχι σε κλίμακα" : "Schematic view, not to scale"}</text>
        </Box>
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
          {isGreek
            ? "Οι σειρές ορίζουν θέσεις πλαισίων και καλωδίωση. Τα συνεπίπεδα πλαίσια δεν σκιάζουν το ένα το άλλο."
            : "Grid rows define module positions and wiring. Coplanar modules do not cause row-to-row shading."}
        </Typography>
      </Paper>
    );
  }
  const basePanelTopLeft = { x: 572, y: 246 };
  const basePanelTopRight = { x: 754, y: 136 };
  const panelDx = basePanelTopRight.x - basePanelTopLeft.x;
  const panelDy = basePanelTopRight.y - basePanelTopLeft.y;
  const panelLen = Math.hypot(panelDx, panelDy) || 1;

  // Ground clearance, drawn rather than assumed.
  //
  // The drawing already had a clearance in it - 40 px of it - but as a number
  // typed into a polygon, so raising the array a foot changed the yield and
  // changed nothing here. The scale comes from the panel itself, the one
  // dimension the whole picture is built around, so the clearance and the
  // module length cannot end up drawn at two different scales.
  const PX_PER_M = panelLen / (Number(moduleLength) > 0 ? Number(moduleLength) : 2.28);
  const GROUND_Y = 308;                        // top of the grass
  const BASE_LOW_Y = 268;                      // low edge, as the drawing was authored
  const clearanceM = toFiniteNumber(groundClearance) != null
    ? Math.max(0, toFiniteNumber(groundClearance))
    : 0.5;
  // Past about a metre and a half the rows climb out of the frame, so the
  // drawing stops there and says so instead of quietly lying about the height.
  const DRAW_MAX_M = 1.6;
  const drawnClearanceM = Math.min(clearanceM, DRAW_MAX_M);
  const clearanceClipped = clearanceM > DRAW_MAX_M + 1e-9;
  const dy = Math.round(drawnClearanceM * PX_PER_M - (GROUND_Y - BASE_LOW_Y));
  const lowEdgeY = BASE_LOW_Y - dy;            // where the low edge now sits
  const sy = (y) => y - dy;
  const clearanceLabel = `${clearanceM.toFixed(2)} m${clearanceClipped ? (isGreek ? " (όχι σε κλίμακα)" : " (not to scale)") : ""}`;
  // Below about 15 px the two arrowheads meet and the dimension reads as a
  // blot; a row sitting on the ground gets the label alone.
  const clearanceGapPx = GROUND_Y - lowEdgeY;
  const showClearanceArrows = clearanceGapPx >= 15;

  const secondPanelTopLeft = { x: basePanelTopLeft.x, y: sy(basePanelTopLeft.y) };
  const secondPanelTopRight = { x: basePanelTopRight.x, y: sy(basePanelTopRight.y) };
  const ux = panelDx / panelLen;
  const uy = panelDy / panelLen;
  const nx = -uy;
  const ny = ux;
  const lNormalSign = -1;
  const lLineOffset = 26;
  const lLabelOffset = 18;
  const lStart = {
    x: secondPanelTopLeft.x + nx * lLineOffset * lNormalSign,
    y: secondPanelTopLeft.y + ny * lLineOffset * lNormalSign,
  };
  const lEnd = {
    x: secondPanelTopRight.x + nx * lLineOffset * lNormalSign,
    y: secondPanelTopRight.y + ny * lLineOffset * lNormalSign,
  };
  const lMid = {
    x: (lStart.x + lEnd.x) / 2 + nx * lLabelOffset * lNormalSign,
    y: (lStart.y + lEnd.y) / 2 + ny * lLabelOffset * lNormalSign,
  };
  const arrowSize = 12;
  const arrowHalfWidth = 5;
  const lStartArrow = `${lStart.x},${lStart.y} ${lStart.x + ux * arrowSize + nx * arrowHalfWidth},${lStart.y + uy * arrowSize + ny * arrowHalfWidth} ${lStart.x + ux * arrowSize - nx * arrowHalfWidth},${lStart.y + uy * arrowSize - ny * arrowHalfWidth}`;
  const lEndArrow = `${lEnd.x},${lEnd.y} ${lEnd.x - ux * arrowSize + nx * arrowHalfWidth},${lEnd.y - uy * arrowSize + ny * arrowHalfWidth} ${lEnd.x - ux * arrowSize - nx * arrowHalfWidth},${lEnd.y - uy * arrowSize - ny * arrowHalfWidth}`;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.25, md: 2.75 },
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        background: isDark
          ? undefined
          : "linear-gradient(180deg, rgba(241,245,249,0.88) 0%, rgba(255,255,255,1) 65%)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          mb: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {isGreek ? "Προεπισκόπηση διατομής" : "Cross-section preview"}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={`${isGreek ? "Βήμα" : "Pitch"} ${pitchLabel}`} size="small" color="primary" variant="outlined" />
          <Chip label={`${isGreek ? "Μήκος πλαισίου" : "Module length"} ${moduleLengthLabel}`} size="small" variant="outlined" />
          <Chip
            label={`${isGreek ? "Πλαίσια" : "Modules"} ${Number.isFinite(Number(totalModules)) ? Number(totalModules) : "—"}`}
            size="small"
            variant="outlined"
          />
        </Stack>
      </Box>

      <Box
        sx={{
          position: "relative",
          borderRadius: 2.5,
          overflow: "hidden",
          background: isDark
            ? "linear-gradient(180deg, rgba(15,23,42,0.94) 0%, rgba(30,41,59,0.88) 100%)"
            : "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(226,232,240,0.85) 100%)",
          border: "1px solid",
          borderColor: isDark ? "rgba(100,116,139,0.3)" : "rgba(148,163,184,0.28)",
          width: "100%",
          maxWidth: 880,
          mx: "auto",
          aspectRatio: "960 / 444",
          minHeight: 240,
          px: { xs: 1, sm: 2 },
        }}
      >
        <Box
          component="svg"
          viewBox="0 0 960 444"
          preserveAspectRatio="xMidYMid meet"
          sx={{ width: "100%", height: "100%", display: "block" }}
        >
          <defs>
            <linearGradient id="panelFill" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#1d4ed8" />
              <stop offset="100%" stopColor="#60a5fa" />
            </linearGradient>
            <linearGradient id="groundFill" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#b9c17f" />
              <stop offset="50%" stopColor="#8f9b58" />
              <stop offset="100%" stopColor="#66723d" />
            </linearGradient>
            <linearGradient id="skyGlow" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={svgSkyStart} />
              <stop offset="100%" stopColor={svgSkyEnd} />
            </linearGradient>
          </defs>

          <rect x="0" y="0" width="960" height="444" fill="url(#skyGlow)" />
          <rect x="72" y="314" width="816" height="48" rx="6" fill={onRoof ? "#94a3b8" : "url(#groundFill)"} />
          <rect x="72" y="308" width="816" height="8" rx="4" fill={onRoof ? "#64748b" : "#adb873"} opacity="0.88" />
          {onRoof ? <text x="790" y="344" textAnchor="middle" fontSize="17" fill="#0f172a">{isGreek ? "Στέγη" : "Roof"}</text> : null}

          <rect x="244" y={sy(200)} width="20" height={114 + dy} fill="#111827" rx="4" />
          <polygon
            points={`142,${sy(246)} 324,${sy(136)} 338,${sy(158)} 156,${lowEdgeY}`}
            fill="url(#panelFill)"
          />
          <text x="116" y={sy(208)} fontSize="24" fontFamily="Arial, sans-serif" fill={svgTextColor} fontWeight="600">
            {isGreek ? "Σειρά Φ/Β" : "PV row"}
          </text>

          <rect x="674" y={sy(200)} width="20" height={114 + dy} fill="#111827" rx="4" />
          <polygon
            points={`572,${sy(246)} 754,${sy(136)} 768,${sy(158)} 586,${lowEdgeY}`}
            fill="url(#panelFill)"
          />
          <line x1="156" y1={lowEdgeY} x2="156" y2="402" stroke="#64748b" strokeWidth="1.8" strokeDasharray="5 5" />
          <line x1="586" y1={lowEdgeY} x2="586" y2="402" stroke="#64748b" strokeWidth="1.8" strokeDasharray="5 5" />

          {/* Clearance, measured where it is: from the grass to the low edge of
              the row, in the gap between the two rows where nothing else is. */}
          <line
            x1="530" y1={lowEdgeY} x2="586" y2={lowEdgeY}
            stroke="#64748b" strokeWidth="1.4" strokeDasharray="4 4"
          />
          <line
            x1="530" y1={lowEdgeY} x2="530" y2={GROUND_Y}
            stroke={svgMeasureColor} strokeWidth="2.4" strokeLinecap="round"
          />
          {showClearanceArrows && (
            <>
              <polygon
                points={`530,${lowEdgeY} 525,${lowEdgeY + 11} 535,${lowEdgeY + 11}`}
                fill={svgMeasureColor}
              />
              <polygon
                points={`530,${GROUND_Y} 525,${GROUND_Y - 11} 535,${GROUND_Y - 11}`}
                fill={svgMeasureColor}
              />
            </>
          )}
          <text
            x="516"
            y={Math.min(GROUND_Y - 20, (lowEdgeY + GROUND_Y) / 2 - 4)}
            textAnchor="end"
            fontSize="14"
            fontFamily="Arial, sans-serif"
            fill={svgSecondaryColor}
          >
            {onRoof ? (isGreek ? "Ύψος από τη στέγη" : "Roof clearance") : (isGreek ? "Ύψος από το έδαφος" : "Ground clearance")}
          </text>
          <text
            x="516"
            y={Math.min(GROUND_Y + 1, (lowEdgeY + GROUND_Y) / 2 + 15)}
            textAnchor="end"
            fontSize="16"
            fontWeight="700"
            fontFamily="Arial, sans-serif"
            fill={svgTextColor}
          >
            {clearanceLabel}
          </text>
          <line x1="156" y1="388" x2="586" y2="388" stroke="#2563eb" strokeWidth="3" />
          <polygon points="156,388 170,381 170,395" fill="#2563eb" />
          <polygon points="586,388 572,381 572,395" fill="#2563eb" />
          <circle cx="156" cy="388" r="4.5" fill="#2563eb" />
          <circle cx="586" cy="388" r="4.5" fill="#2563eb" />
          <rect x="326" y="370" width="90" height="34" rx="17" fill={svgPitchLabelBoxFill} opacity="0.98" />
          <text
            x="371"
            y="392"
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="18"
            fontWeight="700"
            fontFamily="Arial, sans-serif"
            fill={svgTextColor}
          >
            {pitchLabel}
          </text>
          <text
            x="371"
            y="436"
            textAnchor="middle"
            fontSize="22"
            fontWeight="700"
            fontFamily="Arial, sans-serif"
            fill={svgTextColor}
          >
            {isGreek ? "Βήμα σειρών" : "Pitch"}
          </text>

          <rect x="18" y="18" width="188" height="66" rx="16" fill={svgLabelBoxFill} opacity={svgLabelBoxOpacity} />
          <text x="36" y="40" fontSize="15" fontFamily="Arial, sans-serif" fill={svgSecondaryColor}>
            {isGreek ? "Έκταση Φ/Β πεδίου" : "PV field area"}
          </text>
          <text x="36" y="68" fontSize="27" fontWeight="700" fontFamily="Arial, sans-serif" fill={svgTextColor}>
            {footprintLabel}
          </text>
          <rect x="698" y="18" width="248" height="60" rx="16" fill={svgLabelBoxFill} opacity={svgLabelBoxOpacity} />
          <text x="714" y="38" fontSize="15" fontFamily="Arial, sans-serif" fill={svgSecondaryColor}>
            {isGreek ? "Ύψος σειράς" : "Row height"}
          </text>
          <text x="714" y="58" fontSize="12.5" fontWeight="700" fontFamily="Arial, sans-serif" fill={svgTextColor}>
            {/* Before a layout exists row_vertical_modules is null, and the
                template printed it literally as "null vertical modules". */}
            {Number(spacingInfo.row_vertical_modules) > 0
              ? `L = ${moduleLengthLabel} × ${spacingInfo.row_vertical_modules} ${isGreek ? "πλαίσια καθ’ ύψος" : "vertical modules"}`
              : (isGreek ? "Δεν ορίστηκε" : "Not set")}
          </text>

          <line
            x1={lStart.x}
            y1={lStart.y}
            x2={lEnd.x}
            y2={lEnd.y}
            stroke={svgMeasureColor}
            strokeWidth="2.8"
            strokeLinecap="round"
          />
          <polygon points={lStartArrow} fill={svgMeasureColor} />
          <polygon points={lEndArrow} fill={svgMeasureColor} />
          <text
            x={lMid.x}
            y={lMid.y + 4}
            textAnchor="middle"
            fontSize="22"
            fontWeight="700"
            fontFamily="Arial, sans-serif"
            fill={svgTextColor}
          >
            L
          </text>
        </Box>
      </Box>
    </Paper>
  );
}

export default function PhysicalLayoutDialog({
  reportBranding,
  reportLanguage = "en",
  inline = false,
  open,
  onClose,
  onDiscard,
  onSave,
  automaticShading = false,
  initialSpacingInfo: storedSpacingInfo,
  moduleName,
  inverterName,
  moduleLength,
  moduleWidth,
  moduleGeometryLoading = false,
  totalModules,
  incrementFactor = 1.1,
  showUnsavedMarker = false,
  // Indices (1-based) of the other faces of the shared roof scene whose own
  // drafts are unsaved. When this face is clean, the banner names them
  // instead of claiming that this layout changed.
  unsavedElsewhere = [],
  surfaceTilt = null,
  latitude = null,
  mountType = "fixed",
  // Where the array stands. The site model is placed differently on a roof:
  // the array's "ground" is the roof, not the street the building rises from.
  installationType = "ground",
  surfaceType = "grass",
  roofPitch = 0,
  roofAzimuth = 180,
  // Sets the active face's azimuth from the plan (the arrow, an edge click).
  // The orientation owns that angle, so the page decides where it lands.
  onRoofAzimuthChange = null,
  // Roof faces hand their pitch and facing to their orientations:
  // ([{ planeId (null: this one), pitchDeg?, azimuthDeg? }]) => void.
  onFaceAnglesChange = null,
  // A face of the scene gets modules (becomes an orientation of its own) or
  // another orientation loses them (becomes a face of this scene). The page
  // owns the orientations; the draft here follows. onSelectPlane switches
  // the face being edited, as the picker above the dialog does.
  onPromoteRoofFace = null,
  onDemoteRoofFace = null,
  // A PV face deleted: its orientation goes too (planeId null: this one).
  onRemoveFace = null,
  onSelectPlane = null,
  // Everything below feeds the 3D shading view. All of it is already decided
  // elsewhere in the project - orientation on the site tab, the module's own
  // construction on the equipment tab - and none of it is editable here: the
  // view reports the design, it does not become a second place to set it.
  longitude = null,
  surfaceAzimuth = null,
  groundClearance = null,
  horizonEnabled = true,
  onHorizonEnabledChange = null,
  slopeTilt = null,
  slopeAzimuth = null,
  modulePortrait = true,
  moduleHalfCut = true,
  moduleBypassDiodes = 3,
  moduleTopology = null,
  detailedShadingEnabled = false,
  detailedSceneReady = false,
  nearShadingModel = "bypass",
  nearShadingElectricalEffect = 1,
  axisOrientation = "North-South",
  maxAngle = 60,
  backTrack = false,
  utcOffsetHours = null,
  timeZoneLabel = null,
  // The measured obstacle-shading table already stored with the project. A
  // new one is stored by the layout's Save (onSave's 4th argument), on the
  // pv_design like the rest of the layout, because that is what it describes.
  obstacleShading = null,
  // Keys this machine's cache of the imported model. The STL never leaves the
  // browser, so the cache is per project and per machine.
  projectId = null,
  // Several orientations: every face's shading is calculated at once
  // (onCalculateShadingAll) and kept by the page as one draft
  // ({ key, own, count }), which holds while sceneShadingKeyFor gives the same
  // key for what this face's Save would store. The Save then stores them all.
  onCalculateShadingAll = null,
  sceneShadingKeyFor = null,
  sceneShadingDraft = null,
  onDiscardSceneShading = null,
  shadingFaceCount = 1,
  horizonProfile = null,
  projectName = null,
  elevationM = null,
  moduleWp = null,
  capacityDcKw = null,
  // A ref the parent fills from here with {download, busy, ready, error}, so
  // the design report can be started from the Design actions row: the sheet
  // describes the whole design, not only this tab.
  reportRef = null,
  reportLayouts = null,
  onReportState = null,
  // The cell temperatures the electrical design is checked at (auto-design
  // settings); the engine's defaults apply when absent.
  designTemps = null,
  // The plane the rows are drawn at, with how it was arrived at (tilt from
  // the ground composed with the slope, or the typed tilt as is).
  mountingPlane = null,
  accessToken = null,
  // The site boundary as drawn on the location map (GeoJSON), if any.
  parcel = null,
  // The electrical design, for the string map.
  systemDesign = null,
  // Applies strings refitted to the plan's module count to the electrical
  // design (the editor's state and the project), through the same save the
  // electrical tab uses. Absent, the fit button is not offered.
  onFitStringsToPlan = null,
  // The cell temperatures the voltage window is checked at, as the auto-design
  // dialog has them; the fit's validation uses the same ones.
  designCellTemperatures = null,
  // Told whenever the form differs from what is saved, so the tab can
  // show the asterisk and the status chip before Save is pressed.
  onDirtyChange = null,
  // Which of the plant's orientations this layout is, when it has several:
  // { index, count, name }. Printed on the sheet, so a plane's drawing says
  // which plane it draws. Null on a single-plane plant.
  orientationLabel = null,
  // The plant's other orientations with a fitted layout, for the 3D view:
  // [{ label, tilt, azimuth, tiers, clearance, moduleAlongSlope, tables }].
  contextPlanes = null,
  workspaceId = null,
  workspaceDraftsRef = null,
  workspaceSceneEditsRef = null,
  workspaceViewRef = null,
  defaultSceneFrame = null,
}) {
  const isGreek = useIsGreek();
  const tr = useCallback((el, en) => isGreek ? el : en, [isGreek]);
  const onRoof = String(installationType).startsWith("roof");
  const initialSpacingInfo = useMemo(() => roofLayoutSource(storedSpacingInfo, installationType),
    [storedSpacingInfo, installationType]);
  const [sunPathOpen, setSunPathOpen] = useState(false);
  const sceneSnapshotRef = useRef(null);
  const parcelRing = useMemo(() => ringFromParcel(parcel), [parcel]);
  const [parcelFit, setParcelFit] = useState(null);
  const [parcelBusy, setParcelBusy] = useState(false);
  const [parcelError, setParcelError] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState("");

  /**
   * The layout sheet, built from what this tab is holding.
   *
   * It sends the FORM values rather than the saved project, so an engineer can
   * try a pitch, print the sheet and see what that pitch does to the land
   * before committing it. And it needs no run: everything on the sheet is an
   * input or geometry, which is why it can exist weeks before anyone presses
   * Run on a simulation.
   */
  /**
   * The array at the hours that decide the spacing.
   *
   * The pitch is solved for no row-to-row shadow between 09:00 and 15:00 solar
   * time on the winter solstice, so those are the pictures a reviewer wants:
   * the edges of that window and its middle, plus midsummer for contrast.
   * Drawn off-screen from the saved geometry through the same routine as the
   * live view, at the camera angle the user has set. Local clock time, and the
   * caption says so.
   */
  /**
   * Rows fitted to the drawn boundary.
   *
   * The geometry runs on the server -- one implementation, shared with the
   * layout sheet -- and comes back as a list of tables with the module columns
   * each one holds. Applying it replaces the typed row count with the land's
   * own answer; the module total those rows hold is then compared with what
   * the strings add up to, and any difference is reported rather than hidden.
   */
  const fitRowsToParcel = async () => {
    if (!parcelRing) return;
    setParcelBusy(true);
    setParcelError("");
    try {
      const alongRow = modulePortrait === false ? toFiniteNumber(moduleLength) : toFiniteNumber(moduleWidth);
      const tiers = toFiniteNumber(form.row_vertical_modules) || 1;
      const alongSlope = modulePortrait === false ? toFiniteNumber(moduleWidth) : toFiniteNumber(moduleLength);
      const tilt = toFiniteNumber(draftSurfaceTilt) ?? 0;
      const footprint = alongSlope ? alongSlope * tiers * Math.cos((tilt * Math.PI) / 180) : 0;
      const slots = stringSlots(systemDesign, tiers);
      const out = await analyzeParcel({
        polygon: parcel,
        pitch_m: toFiniteNumber(form.minimum_distance_m),
        azimuth_deg: toFiniteNumber(surfaceAzimuth) ?? 180,
        module_width_m: alongRow,
        tiers,
        footprint_m: footprint,
        setback_m: toFiniteNumber(form.setback_m) ?? 0,
        // "Where do these modules go" rather than "how many would fit": stop
        // once the rows hold the design, trimming the last table.
        max_modules: form.fit_to_design && Number(totalModules) > 0 ? Math.round(Number(totalModules)) : null,
        // ... and in whole strings when the design has them: a string never
        // crosses a table, so positions alone can leave strings without a row.
        ...(form.fit_to_design && slots.count > 0 && slots.columns > 0
          ? { string_columns: slots.columns, num_strings: slots.count }
          : {}),
      }, accessToken);
      setParcelFit(out);
    } catch (error) {
      setParcelError(error?.message || "The parcel could not be analysed.");
    } finally {
      setParcelBusy(false);
    }
  };

  const applyParcelRows = () => {
    if (!parcelFit || !Array.isArray(parcelFit.row_modules)) return;
    // The tables' positions and the boundary travel with the rows, so the 3D
    // view can place every table where it stands and draw the fence -- and so
    // a reopened layout still shows the parcel without fitting it again.
    const tables = (parcelFit.rows || []).flatMap((r) => r.tables || []).map((t) => ({
      label: t.label, start: t.start, end: t.end, columns: t.columns, length_m: t.length_m,
      start_lonlat: t.start_lonlat, end_lonlat: t.end_lonlat,
    }));
    if (!onRoof && groundOrigin && tables.every(t => t.start_lonlat && t.end_lonlat)) {
      const toPair = point => { const p = localXY(point, groundOrigin); return [p.x, p.y]; };
      applyGroundRows({ tables: tables.map(t => ({ ...t, start: toPair(t.start_lonlat), end: toPair(t.end_lonlat) })),
        outline: { boundary_xy: (parcelRing || []).map(toPair), frame_origin_xy: [0, 0], frame_origin_lonlat: groundOrigin } });
      return;
    }
    setForm((prev) => ({
      ...prev,
      field_rows: toFieldValue(parcelFit.tables),
      row_modules: parcelFit.row_modules,
      row_labels: parcelFit.row_labels || null,
      parcel_tables: tables,
      parcel_outline: { boundary_xy: parcelFit.boundary_xy, usable_xy: parcelFit.usable_xy },
    }));
  };

  const clearParcelRows = () => {
    setForm((prev) => ({
      ...prev, row_modules: null, row_labels: null, parcel_tables: null, parcel_outline: null,
    }));
  };

  /**
   * Rows set one by one.
   *
   * The model has always allowed rows of different lengths -- the parcel fit
   * produces them -- but the only hand input was a single count shared out
   * equally. This edits the per-row list directly. Editing a parcel-fitted row
   * keeps its position and shortens the table; editing typed rows turns them
   * into an explicit list, and the string map reads whichever is current.
   */
  const setRowColumns = (index, value) => {
    const columns = toFiniteNumber(value);
    if (!onRoof && groundRows && columns > 0) {
      changeGroundRow({ type: "resize", index, columns });
      return;
    }
    setForm((prev) => {
      const base = Array.isArray(prev.row_modules) && prev.row_modules.length
        ? [...prev.row_modules]
        : [...rowsForStrings];
      if (index >= base.length) return prev;
      base[index] = columns != null && columns > 0 ? Math.round(columns) : base[index];
      const tables = Array.isArray(prev.parcel_tables)
        ? prev.parcel_tables.map((t, i) => (i === index ? { ...t, columns: base[i] } : t))
        : prev.parcel_tables;
      return { ...prev, row_modules: base, field_rows: toFieldValue(base.length), parcel_tables: tables };
    });
  };

  const addRow = () => {
    if (!onRoof && groundRows) {
      changeGroundRow({ type: "add", index: groundRows.layout.tables.length - 1 });
      return;
    }
    setForm((prev) => {
      const base = Array.isArray(prev.row_modules) && prev.row_modules.length
        ? [...prev.row_modules]
        : [...rowsForStrings];
      base.push(base.length ? base[base.length - 1] : 1);
      return { ...prev, row_modules: base, field_rows: toFieldValue(base.length),
               // a new row has no position on the parcel; the picture falls back
               // to evenly spaced rows until it is fitted again
               parcel_tables: null, parcel_outline: null };
    });
  };

  const removeRow = (index) => {
    if (!onRoof && groundRows) { changeGroundRow({ type: "remove", index }); return; }
    setForm((prev) => {
      const base = Array.isArray(prev.row_modules) && prev.row_modules.length
        ? [...prev.row_modules]
        : [...rowsForStrings];
      if (base.length <= 1) return prev;
      base.splice(index, 1);
      const tables = Array.isArray(prev.parcel_tables)
        ? prev.parcel_tables.filter((_, i) => i !== index)
        : prev.parcel_tables;
      const labels = Array.isArray(prev.row_labels) ? prev.row_labels.filter((_, i) => i !== index) : prev.row_labels;
      return { ...prev, row_modules: base, field_rows: toFieldValue(base.length), parcel_tables: tables, row_labels: labels };
    });
  };


  const captureSceneViews = (options = {}) => {
    const snap = sceneSnapshotRef.current;
    if (typeof snap !== "function") return [];
    const at = (label, month, day, hour) => {
      const shot = snap({
        month, day, minutes: hour * 60,
        // Framed on the array, with the type carried up to survive the
        // reduction to a two-up cell on paper. In the panel's own framing the
        // sky dome is the widest thing in the picture and the layout came out
        // at about a fifth of it, with 3 pt compass letters.
        frame: "array", typeScale: 2,
        ...options,
      });
      return shot ? {
        label, image: shot.image,
        shaded_pct: shot.shadedPct,
        shading_scope: shot.shadingScope,
        sun_elevation_deg: shot.sunElevation,
      } : null;
    };
    return [
      at("21 December, 09:00", 12, 21, 9),
      at("21 December, 12:00", 12, 21, 12),
      at("21 December, 15:00", 12, 21, 15),
      at("21 June, 12:00", 6, 21, 12),
    ].filter(Boolean);
  };

  const downloadLayoutPdf = async () => {
    if (Object.keys(form.context_frames).length || Object.keys(sharedSceneEditsRef.current).length) {
      setPdfError(tr("Αποθηκεύστε τη διάταξη πριν εξαγάγετε τις μετακινημένες πλευρές στέγης.", "Save the layout before exporting the moved roof surfaces."));
      return;
    }
    setPdfBusy(true);
    setPdfError("");
    try {
      // The catalogue records, so the sheet can check the strings against the
      // inverter's limits. A record that cannot be fetched leaves that section
      // out rather than failing the sheet.
      const [moduleDetails, inverterDetails] = await Promise.all([
        moduleName ? getModuleDetails(moduleName, accessToken).catch(() => null) : null,
        inverterName ? getInverterDetails(inverterName, accessToken).catch(() => null) : null,
      ]);
      const draftPayload = {
        reportBranding,
        reportLanguage,
        reportStage: finalReportReady ? "reviewed" : "draft",
        projectName,
        sceneViews: captureSceneViews(),
        moduleDetails, inverterDetails,
        minCellC: designTemps?.minCellC ?? null,
        maxCellC: designTemps?.maxCellC ?? null,
        latitude, longitude, elevationM, timeZoneLabel,
        moduleName, inverterName,
        moduleLength, moduleWidth, moduleWp, modulePortrait,
        totalModules, capacityDcKw,
        mountType, installationType, surfaceTilt: draftSurfaceTilt, surfaceAzimuth: draftSurfaceAzimuth,
        roofPitch: siteGeometry?.roof?.pitchDeg ?? roofPitch,
        roofAzimuth: siteGeometry?.roof?.azimuthDeg ?? roofAzimuth,
        slopeTilt: onRoof ? (siteGeometry?.roof?.pitchDeg ?? roofPitch) : slopeTilt,
        slopeAzimuth: onRoof ? (siteGeometry?.roof?.azimuthDeg ?? roofAzimuth) : slopeAzimuth,
        groundClearance: toFiniteNumber(form.ground_clearance_m),
        axisOrientation, maxAngle, backTrack,
        spacingInfo: spacingInfoForExport(),
        ...(orientationLabel && orientationLabel.count > 1 ? { orientation: orientationLabel } : {}),
        // The boundary and the electrical design, so the sheet can draw the
        // plan and the string schedule from the same fit and fill the tab shows.
        parcel: parcelRing ? parcel : null,
        systemDesign,
        nearShadingModel, nearShadingElectricalEffect,
        obstacleShading: sceneShading,
        horizonProfile: horizonEnabled === false ? null : horizonProfile,
      };
      const planes = reportLayouts?.length > 1 ? reportLayouts.map((saved) =>
        saved.orientation.index === orientationLabel?.index
          ? draftPayload
          : { ...draftPayload, ...saved }
      ) : null;
      const { blob, filename } = await fetchLayoutPdf({
        ...draftPayload, ...(planes ? { planes } : {}),
      }, accessToken);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setPdfError(error?.message || "The layout sheet could not be generated.");
    } finally {
      setPdfBusy(false);
    }
  };

  // The builder itself, refreshed every render so the button never fires a
  // stale closure over the geometry. A ref carries it because it is a function
  // to call, not state to render.
  useEffect(() => {
    if (!reportRef) return undefined;
    reportRef.current = { download: downloadLayoutPdf, busy: pdfBusy, ready: reportReady, error: pdfError };
    return () => { if (reportRef) reportRef.current = null; };
  });


  const normalizedInitial = useMemo(
    () =>
      buildSpacingInfo(
        { ...roofInitialGrid(initialSpacingInfo, {
          installationType, moduleLength, moduleWidth, portrait: modulePortrait, tilt: surfaceTilt,
        }), surface_tilt_deg: surfaceTilt },
        moduleLength,
        moduleWidth,
        totalModules,
        incrementFactor,
        modulePortrait
      ),
    [incrementFactor, initialSpacingInfo, moduleLength, moduleWidth, totalModules,
     modulePortrait, installationType, surfaceTilt]
  );

  /**
   * The row count the viewer would assume if this field stayed blank.
   *
   * Blank does not mean "unused": the 3D view and every obstacle fraction are
   * measured against SOME field, and with nothing set that field is the one
   * that comes out square. Leaving the box empty therefore hides a number that
   * is being used anyway. It is proposed here instead, so it can be seen,
   * checked against the real plant and corrected - and it is only a proposal
   * until the layout is saved, like every other value in this panel.
   */
  const assumedFieldRows = useMemo(() => {
    const alongRow = modulePortrait === false
      ? toFiniteNumber(moduleLength)
      : toFiniteNumber(moduleWidth);
    const field = deriveField({
      totalModules: toFiniteNumber(totalModules),
      pitch: toFiniteNumber(normalizedInitial.minimum_distance_m),
      moduleWidth: alongRow,
      verticalModules: toFiniteNumber(normalizedInitial.row_vertical_modules),
      fieldRows: null,
    });
    return field ? field.rows : null;
  }, [totalModules, moduleLength, moduleWidth, modulePortrait,
      normalizedInitial.minimum_distance_m, normalizedInitial.row_vertical_modules]);

  const initialFieldRows =
    toFiniteNumber(normalizedInitial.field_rows) != null
      ? normalizedInitial.field_rows
      : assumedFieldRows;

  const initialAreaMode = (() => {
    if (onRoof) return "manual";
    if (initialSpacingInfo?.terrace_area_mode === "auto" || initialSpacingInfo?.terrace_area_mode === "manual") {
      return initialSpacingInfo.terrace_area_mode;
    }
    const stored = toFiniteNumber(normalizedInitial.terrace_area_m2);
    if (stored == null) return "auto";
    const pitch = toFiniteNumber(normalizedInitial.minimum_distance_m);
    const tiers = toFiniteNumber(normalizedInitial.row_vertical_modules);
    const legacy = pitch > 0 && tiers > 0 && Number(totalModules) > 0 && Number(moduleWidth) > 0
      ? Number(moduleWidth) * Number(totalModules) * pitch * Number(incrementFactor || 1.1) / tiers
      : null;
    const matches = (candidate) => candidate != null && Math.abs(stored - candidate) <= Math.max(0.11, candidate * 0.0001);
    // Older automatically filled projects have no mode. Recognise both their
    // saved pitch formula and the current estimate, but retain distinct user
    // values as manual overrides.
    return matches(legacy) || matches(normalizedInitial.computed_terrace_area_m2) ? "auto" : "manual";
  })();

  // 0.5 m is the value the engine falls back to, so it is what the field
  // shows when the project has never set one - not a blank that saves a null.
  const DEFAULT_CLEARANCE_M = 0.5;
  const buildInitialForm = () => ({
    terrace_area_m2: toFieldValue(normalizedInitial.terrace_area_m2),
    terrace_area_mode: initialAreaMode,
    minimum_distance_m: toFieldValue(normalizedInitial.minimum_distance_m),
    row_vertical_modules: toFieldValue(normalizedInitial.row_vertical_modules),
    ground_clearance_m: toFieldValue(
      toFiniteNumber(groundClearance) ?? DEFAULT_CLEARANCE_M
    ),
    field_rows: toFieldValue(initialFieldRows),
    array_offset_east_m: toFieldValue(toFiniteNumber(initialSpacingInfo?.array_offset_east_m) ?? 0),
    array_offset_north_m: toFieldValue(toFiniteNumber(initialSpacingInfo?.array_offset_north_m) ?? 0),
    module_offsets: initialSpacingInfo?.module_offsets || {},
    module_array: initialSpacingInfo?.module_array || null,
    // Grid slots the plan holds no module in, taken out by hand in the plan.
    excluded_slots: Array.isArray(initialSpacingInfo?.excluded_slots) ? initialSpacingInfo.excluded_slots : [],
    module_assignments: initialSpacingInfo?.module_assignments ?? (initialSpacingInfo?.excluded_slots?.length ? initialSpacingInfo.module_positions || [] : null),
    manual_layout_edited: Boolean(initialSpacingInfo?.manual_layout_edited || initialSpacingInfo?.excluded_slots?.length),
    setback_m: toFieldValue(toFiniteNumber(initialSpacingInfo?.setback_m) ?? 0),
    // Per-table module columns when the rows came from the parcel; null means
    // the rows are the typed count shared out.
    row_modules: Array.isArray(initialSpacingInfo?.row_modules) ? initialSpacingInfo.row_modules : null,
    row_labels: Array.isArray(initialSpacingInfo?.row_labels) ? initialSpacingInfo.row_labels : null,
    parcel_tables: Array.isArray(initialSpacingInfo?.parcel_tables) ? initialSpacingInfo.parcel_tables : null,
    parcel_outline: initialSpacingInfo?.parcel_outline || null,
    fit_to_design: initialSpacingInfo?.fit_to_modules != null ? true : initialSpacingInfo?.fit_to_design !== false,
    string_overrides: initialSpacingInfo?.string_overrides || null,
    electrical_design_key: initialSpacingInfo?.electrical_design_key || null,
    string_routing: initialSpacingInfo?.string_routing || "continuous",
    shading_samples_per_module: initialSpacingInfo?.shading_samples_per_module ?? (obstacleShading?.table?.samplesPerModule === 64 ? 64 : 16),
    site_geometry: defaultSceneFrame
      ? { version: 2, roof: null, ...initialSpacingInfo?.site_geometry,
          obstacles: initialSpacingInfo?.site_geometry?.obstacles ?? [],
          frame: initialSpacingInfo?.site_geometry?.frame || defaultSceneFrame }
      : initialSpacingInfo?.site_geometry || null,
    context_frames: {},
  });
  const initialFormKey = JSON.stringify(buildInitialForm());
  const compatibleDraft = () => {
    const cached = workspaceDraftsRef?.current?.[workspaceId];
    return cached?.initialFormKey === initialFormKey ? cached : null;
  };
  const localSceneEditsRef = useRef({});
  const sharedSceneEditsRef = workspaceSceneEditsRef || localSceneEditsRef;
  const [sceneEdits, setSceneEdits] = useState(sharedSceneEditsRef.current);
  const restoreWorkspaceForm = () => sceneFormWithEdits(compatibleDraft()?.form || buildInitialForm(), sharedSceneEditsRef.current[workspaceId]);
  const [form, setForm] = useState(restoreWorkspaceForm);
  const [loadedFormKey, setLoadedFormKey] = useState(initialFormKey);
  const draftContextPlanes = useMemo(() => contextPlanes?.map((original) => {
    const plane = scenePlaneWithEdits(original, sceneEdits[original.id]);
    return form.context_frames[plane.id]
      ? { ...plane, siteGeometry: { ...plane.siteGeometry, frame: form.context_frames[plane.id] } } : plane;
  }), [contextPlanes, form.context_frames, sceneEdits]);
  const [roofStatus, setRoofStatus] = useState({ outside: 0, intersecting: 0, belowRoof: 0 });
  const [stringLayout, setStringLayout] = useState(null);
  const electricalDesignKey = physicalLayoutDesignKey(systemDesign);
  const electricalPositions = useMemo(() => stringLayout?.manualPlan
    ? stringLayout.strings.flatMap(s => s.module_positions || [])
    : modulePositions(stringLayout, Number(form.row_vertical_modules) || 1), [stringLayout, form.row_vertical_modules]);
  const siteGeometry = useMemo(() => {
    const value = form.site_geometry;
    if (!value?.roof || !onRoof) return value;
    // The orientation owns the roof angles. Saved scene geometry contributes
    // dimensions and obstacles, never a second, potentially stale roof slope.
    return { ...value, roof: { ...value.roof,
      pitchDeg: installationType === "roof_flush" ? surfaceTilt : roofPitch,
      azimuthDeg: installationType === "roof_flush" ? surfaceAzimuth : roofAzimuth,
    } };
  }, [form.site_geometry, installationType, onRoof, surfaceTilt, surfaceAzimuth, roofPitch, roofAzimuth]);
  const draftMountingPlane = onRoof && mountingPlane?.reference === "ground"
    ? effectiveOrientation(mountingPlane.designTilt, mountingPlane.designAzimuth, {
      reference: "ground", slopeTilt: siteGeometry?.roof?.pitchDeg ?? roofPitch,
      slopeAzimuth: siteGeometry?.roof?.azimuthDeg ?? roofAzimuth,
    }) : mountingPlane;
  const draftSurfaceTilt = draftMountingPlane?.tilt ?? surfaceTilt;
  const draftSurfaceAzimuth = draftMountingPlane?.azimuth ?? surfaceAzimuth;
  const isFlushRoof = installationType === "roof_flush";
  // On a roof the PV area is read off the roof drawn in 3D (in plan, inside
  // the edge setback) instead of being typed: a typed number stayed put when
  // the roof or its setback changed, and the report printed it anyway.
  const roofUsableAreaM2 = onRoof ? usableRoofAreaM2(siteGeometry?.roof) : null;
  const roofCoveredAreaM2 = (() => {
    const n = Number(totalModules);
    const l = toFiniteNumber(moduleLength);
    const w = toFiniteNumber(moduleWidth);
    if (!onRoof || !(n > 0) || !(l > 0) || !(w > 0)) return null;
    return n * l * w * Math.cos(((toFiniteNumber(draftSurfaceTilt) ?? 0) * Math.PI) / 180);
  })();
  // Every face together. Each face with PV is its own orientation and is laid
  // out on its own, so the figures above are this face only; the report adds
  // the faces up, and so does this line.
  const allFacesArea = (() => {
    if (!onRoof || roofUsableAreaM2 == null || !Array.isArray(contextPlanes) || contextPlanes.length === 0) return null;
    let usable = roofUsableAreaM2;
    let covered = roofCoveredAreaM2 ?? 0;
    let faces = 1;
    for (const plane of contextPlanes) {
      const area = usableRoofAreaM2(plane?.siteGeometry?.roof);
      if (area == null) continue;
      faces += 1;
      usable += area;
      const n = Number(plane.totalModules);
      const a = toFiniteNumber(plane.moduleAlongSlope);
      const b = toFiniteNumber(plane.moduleWidth);
      if (n > 0 && a > 0 && b > 0) covered += n * a * b * Math.cos(((toFiniteNumber(plane.tilt) ?? 0) * Math.PI) / 180);
    }
    return faces > 1 ? { faces, usable, covered } : null;
  })();
  const savedRoofLayout = hasSavedRoofLayout(initialSpacingInfo, totalModules, electricalDesignKey);
  const hasIndividualPositions = Object.keys(form.module_array?.individualOffsets ?? form.module_offsets ?? {}).length > 0;
  // Keep the persisted horizontal pitch; expose the clear gap along the roof.
  // Existing studies retain their geometry until the engineer edits the gap.
  const roofModuleLength = toFiniteNumber(modulePortrait === false ? moduleWidth : moduleLength);
  const roofCos = Math.cos(Number(draftSurfaceTilt) * Math.PI / 180);
  const roofGridPitch = toFiniteNumber(form.minimum_distance_m);
  const roofTiers = toFiniteNumber(form.row_vertical_modules);
  const roofGapCm = roofModuleLength > 0 && roofCos > 1e-6 && roofGridPitch != null && roofTiers > 0
    ? Math.round(((Number.isFinite(form.module_array?.rowSpacingM) ? Math.abs(form.module_array.rowSpacingM)
      : roofGridPitch / roofCos) - roofModuleLength * roofTiers) * 1000000) / 10000 : null;

  // The tables on the map: from the fit just made, or from the layout as
  // saved (the auto-design's or an earlier session's), which carries the same
  // table list with its coordinates.
  const rowSlots = useMemo(
    () => stringSlots(systemDesign, form.row_vertical_modules),
    [systemDesign, form.row_vertical_modules]
  );
  // What the form looked like when it was last loaded from the project. The
  // difference between this and `form` IS the unsaved state.
  const baselineRef = useRef(compatibleDraft()?.baseline || JSON.stringify(buildInitialForm()));
  const effectiveForm = useMemo(() => siteGeometry === form.site_geometry
    ? form : { ...form, site_geometry: siteGeometry }, [form, siteGeometry]);
  // Draft reports must not reuse rays measured against different receivers
  // or obstacles, even before the user saves the changed layout.
  const baseline = JSON.parse(baselineRef.current);
  const receiverKeys = ["minimum_distance_m", "row_vertical_modules", "ground_clearance_m",
    "field_rows", "row_modules", "parcel_tables", "site_geometry", "string_overrides", "string_routing",
    "array_offset_east_m", "array_offset_north_m", "context_frames", "module_offsets", "module_array", "excluded_slots", "module_assignments", "manual_layout_edited", "shading_samples_per_module", "electrical_design_key"];
  const measurementDraftChanged = layoutDesignChanged(form, totalModules, electricalDesignKey) || receiverKeys.some((key) =>
    JSON.stringify(effectiveForm[key]) !== JSON.stringify(baseline[key]));
  const contextHasDrafts = Boolean(draftContextPlanes?.some((plane) => plane.previewOnly));
  const effectiveObstacleShading = obstacleShading && (measurementDraftChanged || contextHasDrafts)
    ? { ...obstacleShading, stale: true }
    : obstacleShading;
  // Shading calculated in the 3D view is part of this layout's draft, like
  // any edit: the layout's own Save stores it together with the layout it was
  // made on, and nothing reaches the project before that. It holds only for
  // the layout it was calculated on -- this form, the shared scene's moves,
  // the other faces and the plane's angles -- so it is keyed by all of them
  // and drops out the moment any of them changes. { payload: null } is the
  // stored table cleared (an imported model removed), saved the same way.
  const shadingDraftKey = useMemo(() => JSON.stringify([receiverKeys.map((key) => effectiveForm[key]), sceneEdits,
    draftContextPlanes, surfaceTilt, surfaceAzimuth, roofPitch, roofAzimuth, groundClearance, totalModules, electricalDesignKey]),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [effectiveForm, sceneEdits, draftContextPlanes, surfaceTilt, surfaceAzimuth, roofPitch, roofAzimuth, groundClearance, totalModules, electricalDesignKey]);
  const [draftShading, setDraftShading] = useState(() => compatibleDraft()?.shading || null);
  const draftShadingApplies = Boolean(draftShading) && (draftShading.payload === null || draftShading.key === shadingDraftKey);
  // Whether the page's all-faces draft holds for this layout as it stands
  // (worked out below, once the layout to save can be built).
  const [sceneDraftApplies, setSceneDraftApplies] = useState(false);
  // An imported model's placement on this face, as the 3D view has it.
  const [meshPlacement, setMeshPlacement] = useState(undefined);
  const sceneShading = sceneDraftApplies && sceneShadingDraft ? sceneShadingDraft.own
    : draftShadingApplies ? draftShading.payload : effectiveObstacleShading;
  const shadingUnsaved = (sceneDraftApplies && Boolean(sceneShadingDraft)) || draftShadingApplies;
  // Shading WAS calculated, on an earlier state of this layout: a draft the
  // layout has since moved away from, or a stored one a later change (or a
  // Save without recalculating) has made out of date. Said as such, so it is
  // not read as "never calculated" -- and never applied as if it still held.
  const shadingOutdated = !(sceneShading?.table && !sceneShading.stale) && Boolean(
    (draftShading && !draftShadingApplies) || (sceneShadingDraft && !sceneDraftApplies)
    || (effectiveObstacleShading?.table && effectiveObstacleShading.stale));

  // Reload from the project when what it holds for this plane CHANGED (a
  // Save, another plane's edit of it), or when the panel is opened. A commit
  // elsewhere in the project hands down the same content as a new object
  // (giving a face PV, say, rebuilds the plane list); that must not rebuild
  // the form, which would bring back what the draft had just dropped or
  // drop what it had just drawn.
  const openedRef = useRef(false);
  useEffect(() => {
    if (!open) { openedRef.current = false; return; }
    const reopened = !openedRef.current;
    openedRef.current = true;
    if (!reopened && initialFormKey === loadedFormKey) return;
    const cached = compatibleDraft();
    const next = restoreWorkspaceForm();
    setForm(next);
    baselineRef.current = cached?.baseline || JSON.stringify(buildInitialForm());
    setLoadedFormKey(initialFormKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, normalizedInitial, groundClearance, initialFieldRows, initialSpacingInfo]);

  const autoAreaEstimate = useMemo(() => (!onRoof && form.parcel_tables?.length
    ? groundFieldArea(form.parcel_tables, (modulePortrait === false ? moduleWidth : moduleLength) * Number(form.row_vertical_modules) * Math.cos(Number(draftSurfaceTilt) * Math.PI / 180), draftSurfaceAzimuth, incrementFactor)
    : null) ?? estimateFieldArea({
    totalModules,
    moduleLength,
    moduleWidth,
    verticalModules: form.row_vertical_modules,
    fieldRows: form.field_rows,
    pitch: form.minimum_distance_m,
    tiltDeg: draftSurfaceTilt,
    portrait: modulePortrait,
    marginFactor: incrementFactor,
  }), [totalModules, moduleLength, moduleWidth, form.row_vertical_modules, form.field_rows,
    form.minimum_distance_m, draftSurfaceTilt, draftSurfaceAzimuth, modulePortrait, incrementFactor, onRoof, form.parcel_tables]);
  const automaticAreaM2 = autoAreaEstimate == null ? null : Math.round(autoAreaEstimate * 100) / 100;
  const automaticAreaChanged = !onRoof && form.terrace_area_mode === "auto" && automaticAreaM2 != null
    && Math.abs((toFiniteNumber(baseline.terrace_area_m2) ?? 0) - automaticAreaM2) > 0.11;
  const ownDirty = JSON.stringify(effectiveForm) !== baselineRef.current || Object.keys(sceneEdits).length > 0 || shadingUnsaved || automaticAreaChanged || layoutDesignChanged(form, totalModules, electricalDesignKey);
  // This face is clean; the tab is marked unsaved only because of other faces.
  const onlyElsewhere = Boolean(onRoof && !ownDirty && unsavedElsewhere.length > 0);

  /**
   * The rows the strings are placed on: the parcel's tables when the boundary
   * has been fitted, otherwise the field model's rows -- the same list the 3D
   * scene draws, so the map and the picture cannot disagree about the rows.
   */
  const rowsForStrings = useMemo(() => {
    if (Array.isArray(form.row_modules) && form.row_modules.length) return form.row_modules;
    const alongRow = modulePortrait === false ? toFiniteNumber(moduleLength) : toFiniteNumber(moduleWidth);
    const field = deriveField({
      totalModules: toFiniteNumber(totalModules),
      pitch: toFiniteNumber(form.minimum_distance_m) || 1,
      moduleWidth: alongRow || 1,
      verticalModules: toFiniteNumber(form.row_vertical_modules) || 1,
      fieldRows: toFiniteNumber(form.field_rows),
    });
    return field ? field.rowModules : [];
  }, [form.row_modules, form.minimum_distance_m, form.row_vertical_modules, form.field_rows,
      modulePortrait, moduleLength, moduleWidth, totalModules]);

  const validGroundCoordinates = toFiniteNumber(latitude) != null && toFiniteNumber(longitude) != null;
  const groundOrigin = siteGeometry?.frame
    ? (validGroundCoordinates ? [Number(longitude) + (Number(siteGeometry.frame.eastM) || 0) / (111320 * Math.cos(Number(latitude) * Math.PI / 180)),
      Number(latitude) + (Number(siteGeometry.frame.northM) || 0) / 111320] : null)
    : form.parcel_outline?.frame_origin_lonlat || tablesOrigin(form.parcel_tables)
      || (validGroundCoordinates ? [Number(longitude), Number(latitude)] : null);
  const groundOriginKey = JSON.stringify(groundOrigin);
  const groundRows = useMemo(() => {
    if (onRoof) return null;
    const scene = resolveSceneModel({ latitude, longitude, totalModules, surfaceTilt: draftSurfaceTilt,
      surfaceAzimuth: draftSurfaceAzimuth, pitch: Number(form.minimum_distance_m),
      rowVerticalModules: form.row_vertical_modules, moduleLength, moduleWidth,
      groundClearance: form.ground_clearance_m, fieldRows: form.field_rows, rowModules: rowsForStrings,
      parcelTables: form.parcel_tables, parcelOutline: form.parcel_outline, portrait: modulePortrait,
      arrayOffsetEast: form.array_offset_east_m, arrayOffsetNorth: form.array_offset_north_m,
      mountType, installationType, siteGeometry, slopeTilt, slopeAzimuth });
    if (!scene || !scene.field) return null;
    const rows = buildRows(scene, draftSurfaceTilt, form.parcel_tables?.length || scene.field.rowsUsed,
      scene.field.rowLength, scene.field.rows, scene.field.rowModules).rows;
    const origin = JSON.parse(groundOriginKey);
    const boundary = parcelRing ? geographicBoundary(parcelRing, origin) : scene.parcel?.boundary || [];
    return { layout: groundLayoutFromRows(rows, { origin, boundary, usable: scene.parcel?.usable || [] }),
      depth: scene.collectorWidth * Math.cos(Number(draftSurfaceTilt) * Math.PI / 180), moduleWidth: scene.moduleWidth,
      tiers: scene.verticalModules, azimuth: scene.axisAzimuth + 90, pitch: scene.pitch,
      setback: Number(form.setback_m) || 0 };
  }, [onRoof, latitude, longitude, totalModules, draftSurfaceTilt, draftSurfaceAzimuth, form.minimum_distance_m,
    form.row_vertical_modules, moduleLength, moduleWidth, form.ground_clearance_m, form.field_rows, rowsForStrings,
    form.parcel_tables, form.parcel_outline, modulePortrait, mountType, installationType, siteGeometry, slopeTilt, slopeAzimuth,
    groundOriginKey, parcelRing, form.setback_m, form.array_offset_east_m, form.array_offset_north_m]);
  const [groundEditError, setGroundEditError] = useState("");
  const [commonPitchDraft, setCommonPitchDraft] = useState(null);
  const groundStats = groundRows ? rowPitchStats(groundRows.layout.tables, groundRows.azimuth) : null;
  const applyGroundRows = (layout) => {
    setGroundEditError("");
    setCommonPitchDraft(null);
    setForm(prev => ({ ...prev, ...groundLayoutFields(layout, groundRows.azimuth) }));
  };
  const changeGroundRow = operation => {
    const next = editGroundRow(groundRows.layout, operation, groundRows);
    if (next) applyGroundRows(next);
    else setGroundEditError(tr("Η αλλαγή δεν χωρά στο γεωτεμάχιο ή επικαλύπτει άλλη σειρά. Μετακινήστε τη σειρά ή μειώστε το μήκος/βήμα.",
      "This change crosses the parcel boundary or overlaps another row. Move the row or reduce its length/pitch."));
  };
  const commonPitchAction = !onRoof && groundRows && <Button size="small" variant="outlined" sx={{ mt: 0.75 }}
    disabled={groundRows.layout.tables.length < 2 || !(Number(commonPitchDraft ?? form.minimum_distance_m) > 0)}
    onClick={() => changeGroundRow({ type: "pitch", pitch: Number(commonPitchDraft ?? form.minimum_distance_m) })}>
    {tr("Εφαρμογή σε όλες τις σειρές", "Apply to all rows")}
  </Button>;
  const groundGeometryInvalid = groundRows && !groundRowsValid(groundRows.layout, groundRows.depth, groundRows.setback);

  // The holes the plan has, kept only where the grid still has those slots:
  // a row that shrank takes its holes with it.
  const excludedSlots = useMemo(() => excludedSlotsOf({ ...form, row_modules: rowsForStrings }),
    [form.excluded_slots, form.row_vertical_modules, rowsForStrings]);  // eslint-disable-line react-hooks/exhaustive-deps
  const physicalCount = physicalLayoutCount({ ...form, row_modules: rowsForStrings });
  const manualWiring = useMemo(() => form.manual_layout_edited ? planStringAssignments({ systemDesign,
    rowModules: rowsForStrings, tiers: Number(form.row_vertical_modules) || 1,
    excludedSlots, assignments: form.module_assignments || [] }) : null,
  [form.manual_layout_edited, form.module_assignments, systemDesign, rowsForStrings, form.row_vertical_modules, excludedSlots]);
  const electricalLayoutChanged = onRoof && Boolean(electricalDesignKey || form.electrical_design_key) &&
    (layoutDesignChanged({ ...form, row_modules: rowsForStrings }, totalModules, electricalDesignKey) || Boolean(manualWiring && !manualWiring.check.consistent));
  const missingModules = manualWiring?.check.missing_modules || 0;
  const unassignedModules = manualWiring?.unassigned.length || 0;
  // Nothing to reconcile: the banner becomes a confirmation, without an action.
  const layoutAgrees = !electricalLayoutChanged && physicalCount === Number(totalModules)
    && !(manualWiring && !manualWiring.check.consistent)
    && !(!onRoof && stringLayout?.check?.consistent === false);
  useEffect(() => {
    onDirtyChange?.(ownDirty, { id: workspaceId || "o1", physical: physicalCount,
      electrical: Number(totalModules) || 0, missing: missingModules, unassigned: unassignedModules,
      inconsistent: Boolean(form.manual_layout_edited && (physicalCount !== Number(totalModules) || missingModules || unassignedModules)) });
  }, [ownDirty, onDirtyChange, workspaceId, physicalCount, totalModules, missingModules, unassignedModules, form.manual_layout_edited]);


  /**
   * The rows as they would really be built, for the helper line.
   *
   * The old text said "about 58 modules per row", which is a row the plant
   * does not have: 1728 over 30 rows is 29 rows of 58 and one of 46. The
   * viewer draws that split, so the helper has to name it, and both read it
   * from the same function rather than each rounding on its own.
   */
  const rowSplit = useMemo(() => {
    const n = Number(totalModules);
    const rows = toFiniteNumber(form.field_rows);
    const high = toFiniteNumber(form.row_vertical_modules);
    if (!(n > 0) || !(rows > 0) || !(high > 0)) return "";
    // Pitch and module width only scale the row length, which this line does
    // not use; the split depends on the counts alone.
    const f = deriveField({
      totalModules: n, fieldRows: rows, verticalModules: high,
      rowModules: form.row_modules,
      pitch: 1, moduleWidth: 1,
    });
    // More rows than the modules can fill is a typo, and the viewer ignores it
    // and assumes a square field. Describing a split it will not draw would be
    // the helper telling a different story than the picture.
    return f && f.rowsGiven ? describeRows(f) : "";
  }, [totalModules, form.field_rows, form.row_vertical_modules, form.row_modules]);

  // True while the box still shows the proposal rather than a number that came
  // from the project or from the user.
  const proposedRows =
    toFiniteNumber(normalizedInitial.field_rows) == null &&
    assumedFieldRows != null &&
    toFiniteNumber(form.field_rows) === assumedFieldRows;

  const previewSpacingInfo = useMemo(
    () => ({
      ...buildSpacingInfo(
        {
          mode: "manual",
          terrace_area_m2: roofUsableAreaM2 ?? (form.terrace_area_mode === "auto" && automaticAreaM2 != null ? automaticAreaM2 : form.terrace_area_m2),
          minimum_distance_m: form.minimum_distance_m,
          pitch_m: form.minimum_distance_m,
          row_vertical_modules: form.row_vertical_modules,
          field_rows: form.field_rows,
          surface_tilt_deg: draftSurfaceTilt,
        },
        moduleLength,
        moduleWidth,
        totalModules,
        incrementFactor,
        modulePortrait
      ),
      ...(!onRoof && form.parcel_tables?.length ? { computed_terrace_area_m2: automaticAreaM2 } : {}),
    }),
    [form, incrementFactor, moduleLength, moduleWidth, totalModules, modulePortrait, roofUsableAreaM2, automaticAreaM2, draftSurfaceTilt, onRoof]
  );

  const groundRowsHelp = form.parcel_tables?.length && !onRoof ? tr(
    "Οι υπάρχουσες σειρές κρατούν τη θέση και το μήκος τους. Αύξηση προσθέτει σειρές δίπλα στην τελευταία· μείωση αφαιρεί τις τελευταίες. Η αντιστοίχιση με τον ηλεκτρολογικό σχεδιασμό ελέγχεται ξανά.",
    "Existing rows keep their position and length. Increasing adds rows beside the last; decreasing removes the last rows. Mapping to the electrical design is checked again.") : tr(
    `${Number(totalModules) > 0 ? `Τα ${Number(totalModules)} πλαίσια κατανέμονται` : "Τα πλαίσια κατανέμονται"} σε αυτό το πλήθος σειρών. Η τιμή καθορίζει το μήκος κάθε σειράς στην τρισδιάστατη σκηνή και στη μέτρηση σκιάσεων.${proposedRows ? " Η προτεινόμενη τιμή προκύπτει από περίπου τετράγωνο πεδίο· αλλάξτε την αν γνωρίζετε τον πραγματικό αριθμό σειρών." : ""}`,
    rowSplit
      ? `How many rows the ${Number(totalModules)} modules are divided between: ${rowSplit}.${proposedRows ? " Proposed from a square field - set the real row count if you know it." : ""}`
      : "How many rows the modules are divided between. It fixes the row length the 3D view and the imported-model measurement use. Leave blank to assume a square field."
  );
  const groundFootprintHelp = tr(
    Number(form.field_rows) === 1
      ? "Εκτίμηση έκτασης μόνο για την τρέχουσα διάταξη/προσανατολισμό, όχι εμβαδόν πλαισίων ή ολόκληρου γηπέδου. Για μία σειρά: μήκος × οριζόντιο βάθος σειράς × 1,10. Δεν χρησιμοποιείται βήμα μεταξύ σειρών. Οι άλλες πλευρές καταγράφονται χωριστά."
      : `Εκτίμηση έκτασης μόνο για την τρέχουσα διάταξη/προσανατολισμό, μαζί με τα κενά μεταξύ σειρών και περιθώριο 10%· όχι εμβαδόν πλαισίων ή ολόκληρου γηπέδου.${automaticAreaM2 != null ? ` Η τρέχουσα γεωμετρία δίνει περίπου ${formatMetric(automaticAreaM2, 1, " m²")}.` : ""} Οι εκτάσεις άλλων πλευρών καταγράφονται χωριστά και αθροίζονται στη συνολική αναφορά.`,
    Number(form.field_rows) === 1
      ? "Estimated area for this layout/orientation only, not the module area or the entire parcel. For one row: row length × horizontal row depth × 1.10. Inter-row pitch is not used. Other faces are recorded separately."
      : automaticAreaM2 != null
        ? `Estimated area for this layout/orientation, including row gaps and a 10% margin; not the module area or entire parcel. Current geometry suggests about ${formatMetric(automaticAreaM2, 1, " m²")}. Other faces are recorded separately and added in the overall report.`
        : "Estimated area for this layout/orientation, including row gaps and a 10% margin; not the module area or entire parcel. Other faces are recorded separately and added in the overall report."
  );

  const hasGeometryInput = useMemo(
    () =>
      [form.minimum_distance_m, form.row_vertical_modules].some(
        (value) => String(value ?? "").trim() !== ""
      ),
    [form.minimum_distance_m, form.row_vertical_modules]
  );

  const invalidSharedFaces = useMemo(() => invalidSceneEdits(draftContextPlanes, sceneEdits), [draftContextPlanes, sceneEdits]);
  const [roofRepairError, setRoofRepairError] = useState("");
  const roofFitAnchorRef = useRef("active");

  // ---- The roof as drawn ---------------------------------------------------
  // Resolve each requested plane from its eave, then fit connected faces
  // together along their complete shared edges. The last edited face anchors
  // the fit; its neighbours adapt their slopes and, if necessary, eave heights.
  // PV orientation, saved geometry and shading must use the same fitted plane.
  const roofModel = useMemo(() => {
    const roof = siteGeometry?.roof;
    const num = (v) => toFiniteNumber(v) ?? 0;
    // Just drawn, the face has no height yet; an eave height typed for it
    // is enough to place it.
    const h = toFiniteNumber(roof?.heightM) ?? (toFiniteNumber(roof?.eaveHeightM) != null ? 0 : null);
    // A rectangle still missing a dimension has no outline to resolve.
    const drawn = (ring) => ring.length >= 3 && ring.every((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
    if (!onRoof || !siteGeometry) return null;
    const elevA = num(siteGeometry.frame?.elevationM);
    // A face given by width, depth and bearing has no outline of its own: its
    // rectangle turns with its bearing, so its eave is the edge facing that
    // bearing and cannot be another (that would turn the rectangle again).
    // The roof may be drawn before any face has PV: then there is no active face.
    const faces = roof && h != null && drawn(roofFootprint(roof)) ? [{ key: "active", kind: "active", pv: true, label: null, ring: roofFootprint(roof), origin: { x: 0, y: 0 },
      ground: elevA, pitchDeg: num(roof.pitchDeg), current: { azimuthDeg: num(roof.azimuthDeg), zAtOrigin: h },
      rect: !roof.footprint, eaveEdge: roof.eaveEdge, eaveHeightM: roof.eaveHeightM, freeAzimuth: roof.freeAzimuth, azimuthSource: roof.azimuthSource }] : [];
    (siteGeometry.obstacles || []).filter((o) => o.kind === "roof" && o.base !== "roof").forEach((o) => faces.push({
      key: `own:${o.id}`, kind: "own", obstacle: o, label: o.name || null, ring: obstacleFootprint(o), rect: !o.footprint,
      origin: { x: num(o.eastM), y: num(o.northM) }, ground: elevA, pitchDeg: num(o.pitchDeg),
      current: { azimuthDeg: num(o.rotationDeg), zAtOrigin: num(o.baseHeightM) + num(o.heightM) },
      eaveEdge: o.eaveEdge, eaveHeightM: o.eaveHeightM, freeAzimuth: o.freeAzimuth, azimuthSource: o.azimuthSource }));
    if (workspaceId) (draftContextPlanes || []).forEach((plane) => {
      const g = plane.siteGeometry;
      const off = sceneOffset({ siteGeometry }, plane);
      const gh = toFiniteNumber(g?.roof?.heightM);
      if (!off || !g) return;
      const elevO = num(g.frame?.elevationM);
      const shift = (ring) => ring.map((p) => ({ x: p.x + off.x, y: p.y + off.y }));
      if (g.roof && gh != null) faces.push({ key: `plane:${plane.id}`, kind: "plane", pv: true, planeId: plane.id, label: plane.label,
        rect: !g.roof.footprint, ring: shift(roofFootprint(g.roof)), origin: { x: off.x, y: off.y }, ground: elevO, pitchDeg: num(g.roof.pitchDeg),
        current: { azimuthDeg: num(g.roof.azimuthDeg), zAtOrigin: gh }, eaveEdge: g.roof.eaveEdge, eaveHeightM: g.roof.eaveHeightM, freeAzimuth: g.roof.freeAzimuth, azimuthSource: g.roof.azimuthSource });
      (g.obstacles || []).filter((o) => o.kind === "roof" && o.base !== "roof").forEach((o) => faces.push({
        key: `pob:${plane.id}:${o.id}`, kind: "pob", planeId: plane.id, obstacle: o, label: o.name || plane.label,
        rect: !o.footprint, ring: shift(obstacleFootprint(o)), origin: { x: off.x + num(o.eastM), y: off.y + num(o.northM) }, ground: elevO,
        pitchDeg: num(o.pitchDeg), current: { azimuthDeg: num(o.rotationDeg), zAtOrigin: num(o.baseHeightM) + num(o.heightM) },
        eaveEdge: o.eaveEdge, eaveHeightM: o.eaveHeightM, freeAzimuth: o.freeAzimuth, azimuthSource: o.azimuthSource }));
    });
    // Eaves are found on the building's outline: an edge shared with another
    // face is a ridge, hip or valley, never an eave. The shared edges are
    // read in the project frame, so they hold the same for every face.
    for (let i = faces.length - 1; i >= 0; i -= 1) if (!drawn(faces[i].ring)) faces.splice(i, 1);
    if (!faces.length) return null;
    const shared = sharedEdgeIndices(faces.map((f) => f.ring));
    const resolved = faces.map((f, i) => {
      // Edge indices are the face's own outline's, whatever the frame.
      const eaveEdge = f.rect ? facingEdge(f.ring, f.current.azimuthDeg) : f.eaveEdge;
      const local = eaveFace(f.ring, f.origin, { ...f, eaveEdge, sharedEdges: shared[i] });
      return local ? { ...f, ...local } : null;
    }).filter(Boolean);
    // Keep drawn outlines fixed. The connected planes, including PV faces,
    // must agree at BOTH endpoints of EVERY shared edge, also after edits.
    const surfaces = resolved.map(f => ({ ...f, zAtOrigin: f.ground + f.zAtOrigin }));
    const fitted = fitRoofJoints(surfaces, roofFitAnchorRef.current);
    const joined = resolved.map(f => {
      const plane = fitted.updates[f.key];
      if (!plane) return f;
      const a = f.ring[f.eaveEdge], b = f.ring[(f.eaveEdge + 1) % f.ring.length];
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      return { ...f, ...plane, zAtOrigin: plane.zAtOrigin - f.ground,
        eaveHeightM: surfaceZ({ ...f, ...plane }, mid) - f.ground,
        freeAzimuth: true, azimuthSource: "auto", fitted: true };
    });
    return { faces: joined, steps: fitted.mismatches };
  }, [onRoof, siteGeometry, draftContextPlanes, workspaceId]);

  // Angles to the orientations; geometry to this draft and the other faces'.
  const pushFaceAngles = (list) => {
    if (!list.length) return;
    if (onFaceAnglesChange) onFaceAnglesChange(list);
    else list.filter((u) => u.planeId == null && u.azimuthDeg != null).forEach((u) => onRoofAzimuthChange?.(u.azimuthDeg));
  };
  useEffect(() => {
    if (!roofModel) return;
    const near = (a, b, t) => toFiniteNumber(a) != null && Math.abs(Number(a) - Number(b)) <= t;
    const azGap = (a, b) => Math.abs((((Number(a) - Number(b)) % 360) + 540) % 360 - 180);
    const planePrecision = (v) => Math.round(v * 1e9) / 1e9;
    const planeBearing = (v) => ((planePrecision(v) % 360) + 360) % 360;
    const differs = (source, patch) => Object.entries(patch).some(([key, value]) =>
      typeof value === "number" ? !near(source?.[key], value, 1e-7)
        : JSON.stringify(source?.[key]) !== JSON.stringify(value));
    const facePatch = f => ({ eaveEdge: f.eaveEdge, eaveHeightM: planePrecision(f.eaveHeightM),
      ...(f.fitted ? { freeAzimuth: true, azimuthSource: "auto",
        ...(f.rect ? { footprint: f.ring.map(p => ({ x: p.x - f.origin.x, y: p.y - f.origin.y })) } : {}) } : {}) });
    const angles = [];
    let ownRoof = null;
    const ownObstacles = {};
    const planeRoofs = {}, planeObstacles = {};
    // Only a face whose plane moves is written; its eave goes with it, so
    // later changes turn it about that eave. A face that already lies on its
    // eave's plane is left as saved: opening the layout changes nothing.
    roofModel.faces.forEach((f) => {
      if (f.kind === "active" || f.kind === "plane") {
        const src = f.kind === "active" ? siteGeometry.roof : draftContextPlanes.find((p) => p.id === f.planeId).siteGeometry.roof;
        const pitchChanged = !near(src.pitchDeg, f.pitchDeg, 1e-7), azimuthChanged = azGap(f.azimuthDeg, f.current.azimuthDeg) > 1e-7;
        if (pitchChanged || azimuthChanged) angles.push({ planeId: f.planeId ?? null,
          ...(pitchChanged ? { pitchDeg: planePrecision(f.pitchDeg) } : {}), ...(azimuthChanged ? { azimuthDeg: planeBearing(f.azimuthDeg) } : {}) });
        if (!f.fitted && !pitchChanged && !azimuthChanged && near(src.heightM, f.zAtOrigin, 1e-7)) return;
        const patch = { heightM: planePrecision(f.zAtOrigin), ...facePatch(f),
          ...(f.fitted ? { pitchDeg: planePrecision(f.pitchDeg), azimuthDeg: planeBearing(f.azimuthDeg) } : {}) };
        const stored = f.kind === "active" ? form.site_geometry?.roof
          : sharedSceneEditsRef.current[f.planeId]?.site_geometry?.roof || src;
        if (differs(stored, patch)) {
          if (f.kind === "active") ownRoof = patch; else planeRoofs[f.planeId] = patch;
        }
      } else {
        const o = f.obstacle;
        const heightM = planePrecision(f.zAtOrigin - (Number(o.baseHeightM) || 0));
        if (!f.fitted && near(o.heightM, heightM, 1e-7) && near(o.pitchDeg, f.pitchDeg, 1e-7)
          && azGap(o.rotationDeg, f.azimuthDeg) <= 1e-7) return;
        const patch = { rotationDeg: planeBearing(f.azimuthDeg), pitchDeg: planePrecision(f.pitchDeg), heightM, ...facePatch(f) };
        if (differs(o, patch)) {
          if (f.kind === "own") ownObstacles[o.id] = patch;
          else (planeObstacles[f.planeId] = planeObstacles[f.planeId] || {})[o.id] = patch;
        }
      }
    });
    if (ownRoof || Object.keys(ownObstacles).length) {
      handleField("site_geometry", { ...siteGeometry,
        ...(ownRoof ? { roof: { ...siteGeometry.roof, ...ownRoof } } : {}),
        obstacles: (siteGeometry.obstacles || []).map((o) => ownObstacles[o.id] ? { ...o, ...ownObstacles[o.id] } : o) });
    }
    const planeIds = [...new Set([...Object.keys(planeRoofs), ...Object.keys(planeObstacles)])];
    if (planeIds.length && workspaceId) {
      const next = { ...sharedSceneEditsRef.current };
      planeIds.forEach((id) => {
        const plane = draftContextPlanes.find((p) => p.id === id);
        const g = plane.siteGeometry;
        next[id] = { ...plane.spacing, site_geometry: { ...g,
          roof: g.roof ? { ...g.roof, ...(planeRoofs[id] || {}) } : g.roof,
          obstacles: (g.obstacles || []).map((o) => planeObstacles[id]?.[o.id] ? { ...o, ...planeObstacles[id][o.id] } : o) } };
      });
      sharedSceneEditsRef.current = next;
      setSceneEdits(next);
    }
    pushFaceAngles(angles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roofModel]);
  // A face about to change pitch keeps its eave where it is: the eave edge
  // and height it has now are stored first, so the face turns about them.
  const pinEave = (key) => {
    const face = roofModel?.faces.find((f) => f.key === key);
    if (!face) return;
    if (key === "active") {
      const roof = siteGeometry.roof;
      if (roof.eaveEdge === face.eaveEdge && toFiniteNumber(roof.eaveHeightM) != null) return;
      handleField("site_geometry", { ...siteGeometry, roof: { ...roof, eaveEdge: face.eaveEdge, eaveHeightM: face.eaveHeightM } });
    } else if (face.kind === "plane") {
      const roof = draftContextPlanes.find((p) => p.id === face.planeId)?.siteGeometry?.roof;
      if (!roof || (roof.eaveEdge === face.eaveEdge && toFiniteNumber(roof.eaveHeightM) != null)) return;
      editContextRoof(face.planeId, { eaveEdge: face.eaveEdge, eaveHeightM: face.eaveHeightM });
    }
  };
  // Apply only a common eave height; each face keeps the pitch needed by its
  // own roof plane and PV orientation.
  const applyRoofToAllFaces = ({ eaveHeightM }) => {
    if (!roofModel) return;
    roofFitAnchorRef.current = "active";
    const e = toFiniteNumber(eaveHeightM);
    // Each face keeps the eave it has; the height given, or the one it has.
    const pin = (key) => {
      const f = roofModel.faces.find((x) => x.key === key);
      return f ? { eaveEdge: f.eaveEdge, eaveHeightM: e ?? f.eaveHeightM } : (e != null ? { eaveHeightM: e } : {});
    };
    const face = (o, key) => (o.kind === "roof" ? { ...o, ...pin(key) } : o);
    handleField("site_geometry", { ...siteGeometry,
      ...(siteGeometry.roof ? { roof: { ...siteGeometry.roof, ...pin("active") } } : {}),
      obstacles: (siteGeometry.obstacles || []).map((o) => face(o, `own:${o.id}`)) });
    if (workspaceId) {
      const next = { ...sharedSceneEditsRef.current };
      (draftContextPlanes || []).forEach((plane) => {
        const g = plane.siteGeometry;
        if (!g) return;
        next[plane.id] = { ...plane.spacing, site_geometry: { ...g,
          ...(g.roof ? { roof: { ...g.roof, ...pin(`plane:${plane.id}`) } } : {}),
          obstacles: (g.obstacles || []).map((o) => face(o, `pob:${plane.id}:${o.id}`)) } };
      });
      sharedSceneEditsRef.current = next;
      setSceneEdits(next);
    }
  };
  // Repair an existing hand-drawn hip/ridge by moving only common INNER
  // vertices to the intersection of the existing roof planes. Eaves, pitch and
  // PV orientation stay fixed. Refuse a partial repair: it would merely move a
  // visible step elsewhere in the shared roof.
  const closeRoofJoints = () => {
    setRoofRepairError("");
    if (!roofModel?.steps.length) return;
    const surfaces = roofModel.faces.map((f) => ({ ...f,
      zAtOrigin: f.ground + f.zAtOrigin, fixed: f.rect }));
    const rings = closeRoofVertices(surfaces);
    const proposed = surfaces.map((f) => ({ ...f, ring: rings[f.key] || f.ring }));
    if (!Object.keys(rings).length || Object.values(rings).some((ring) => !validPolygon(ring)) || edgeSteps(proposed).length) {
      setRoofRepairError(tr("Οι κοινές κορυφές δεν αρκούν για να κλείσει η στέγη χωρίς σκαλί. Ελέγξτε τα ύψη γείσου, τις κλίσεις και τη φορά των πλευρών.",
        "Moving the shared corners cannot close this roof without a step. Check the eave heights, pitches and face bearings."));
      return;
    }
    const round = (n) => Math.round(n * 1000) / 1000;
    const localRing = (key) => {
      const f = roofModel.faces.find((face) => face.key === key);
      return rings[key]?.map((p) => ({ x: round(p.x - f.origin.x), y: round(p.y - f.origin.y) }));
    };
    const ownRoof = localRing("active");
    const ownObstacles = (siteGeometry.obstacles || []).map((o) => {
      const ring = localRing(`own:${o.id}`);
      return ring ? { ...o, footprint: ring } : o;
    });
    if (ownRoof || ownObstacles.some((o, i) => o !== (siteGeometry.obstacles || [])[i])) {
      handleField("site_geometry", { ...siteGeometry,
        ...(ownRoof ? { roof: { ...siteGeometry.roof, footprint: ownRoof } } : {}),
        obstacles: ownObstacles });
    }
    if (workspaceId) {
      const next = { ...sharedSceneEditsRef.current };
      (draftContextPlanes || []).forEach((plane) => {
        const g = plane.siteGeometry;
        const roofRing = localRing(`plane:${plane.id}`);
        const obstacles = (g?.obstacles || []).map((o) => {
          const ring = localRing(`pob:${plane.id}:${o.id}`);
          return ring ? { ...o, footprint: ring } : o;
        });
        if (!roofRing && obstacles.every((o, i) => o === (g.obstacles || [])[i])) return;
        next[plane.id] = { ...plane.spacing, site_geometry: { ...g,
          ...(roofRing ? { roof: { ...g.roof, footprint: roofRing } } : {}), obstacles } };
      });
      if (Object.keys(next).some((key) => next[key] !== sharedSceneEditsRef.current[key])) {
        sharedSceneEditsRef.current = next;
        setSceneEdits(next);
      }
    }
  };
  // The common scene's objects are stored in the geometry record where they
  // were first added, but they are presented and edited as one shared set.
  const sharedObstacles = useMemo(() => (workspaceId ? (draftContextPlanes || []).flatMap((plane) => {
    const off = sceneOffset({ siteGeometry }, plane);
    if (!off) return [];
    const geometry = plane.siteGeometry || {};
    return (geometry.obstacles || []).map((o) => ({
      planeId: plane.id, label: plane.label,
      obstacle: { ...o, eastM: (Number(o.eastM) || 0) + off.x, northM: (Number(o.northM) || 0) + off.y },
      sceneGroundZM: off.z - (Number(geometry.roof?.heightM) || 0),
    }));
  }) : null), [workspaceId, draftContextPlanes, siteGeometry]);
  // The other PV faces, for the common roof table: one row each.
  const sharedFaces = useMemo(() => (workspaceId ? (draftContextPlanes || []).flatMap((plane) => {
    const roof = plane.siteGeometry?.roof;
    const range = roofHeightRange(roof);
    if (!roof || !range) return [];
    return [{ planeId: plane.id, label: plane.label, primary: Boolean(plane.primary), roof, range,
      elevationM: Number(plane.siteGeometry?.frame?.elevationM) || 0 }];
  }) : null), [workspaceId, draftContextPlanes]);
  // Non-PV roof faces are part of the common scene, alongside its obstacles.
  // Their storage record is an implementation detail; they have no UI owner.
  const sharedRoofFaces = useMemo(() => (workspaceId ? (draftContextPlanes || []).flatMap((plane) =>
    (plane.siteGeometry?.obstacles || []).filter((obstacle) => obstacle.kind === "roof").map((obstacle) => ({
      planeId: plane.id, label: plane.label, obstacle,
    }))) : null), [workspaceId, draftContextPlanes]);
  const editContextRoof = (planeId, patch) => {
    const plane = draftContextPlanes?.find((p) => p.id === planeId);
    if (!plane?.siteGeometry?.roof) return;
    const next = { ...sharedSceneEditsRef.current, [planeId]: { ...plane.spacing,
      site_geometry: { ...plane.siteGeometry, roof: { ...plane.siteGeometry.roof, ...patch } } } };
    sharedSceneEditsRef.current = next;
    setSceneEdits(next);
  };
  const editContextObstacles = (planeId, edit) => {
    const plane = draftContextPlanes?.find((p) => p.id === planeId);
    if (!plane?.siteGeometry) return;
    const next = { ...sharedSceneEditsRef.current, [planeId]: { ...plane.spacing,
      site_geometry: { ...plane.siteGeometry, obstacles: edit(plane.siteGeometry.obstacles || []) } } };
    sharedSceneEditsRef.current = next;
    setSceneEdits(next);
  };
  const faceLabel = (key) => {
    const face = roofModel?.faces.find((f) => f.key === key);
    if (!face) return "";
    if (face.key === "active") return tr(`Πλευρά ${orientationLabel?.index ?? 1}`, `Face ${orientationLabel?.index ?? 1}`);
    if (face.kind === "plane") return tr(`Πλευρά ${face.label}`, `Face ${face.label}`);
    return face.obstacle?.name || tr("Πλευρά χωρίς Φ/Β", "Face without PV");
  };
  // ---- PV on a face, and a PV face removed --------------------------------
  // A face is drawn once, as a roof face; whether it carries modules is
  // chosen after. The first face given PV is this plane's own; a later one
  // goes to an orientation that has no face yet, or to a new one. A PV face
  // deleted takes its orientation with it (the first orientation stays and
  // only loses its face).
  const [pvChoice, setPvChoice] = useState(null);          // { obstacle }
  const [removeChoice, setRemoveChoice] = useState(null);  // { planeId, label, primary }
  // The strings refitted to the plan, shown before they are applied.
  const [stringFit, setStringFit] = useState(null);        // { busy, error, result }
  const stringFitKey = JSON.stringify([systemDesign, physicalCount, rowsForStrings, excludedSlots, form.module_assignments,
    moduleName, inverterName, designCellTemperatures, form.string_routing, form.parcel_tables, siteGeometry]);
  const requestStringFit = async () => {
    const key = stringFitKey;
    setStringFit({ busy: true, error: "", result: null, key });
    try {
      const result = await fitStringsToPlan({
        systemDesign, modules: physicalCount, module: moduleName || undefined, inverter: inverterName || undefined,
        minCellC: designCellTemperatures?.minCellC ?? undefined, maxCellC: designCellTemperatures?.maxCellC ?? undefined,
      }, accessToken);
      setStringFit({ busy: false, error: "", result, key });
    } catch (e) {
      setStringFit({ busy: false, error: stringFitErrorMessage(e, isGreek), result: null });
    }
  };
  const hasOutline = (roof) => Boolean(roof && (Array.isArray(roof.footprint)
    ? roof.footprint.length >= 3 : Number(roof.widthM) > 0 && Number(roof.depthM) > 0));
  const facelessPlanes = (draftContextPlanes || []).filter((p) => !hasOutline(p.siteGeometry?.roof));
  const r3m = (v) => Math.round(v * 1000) / 1000;
  // The face as a plane's roof, in a frame at the face's own centre.
  const faceAsRoof = (o) => {
    const f0 = siteGeometry?.frame || { eastM: 0, northM: 0, elevationM: 0 };
    const ex = toFiniteNumber(o.eastM) ?? 0, ny = toFiniteNumber(o.northM) ?? 0;
    const base = toFiniteNumber(o.baseHeightM) ?? 0;
    const ring = obstacleFootprint(o).map((p) => ({ x: r3m(p.x - ex), y: r3m(p.y - ny) }));
    const xs = ring.map((p) => p.x), ys = ring.map((p) => p.y);
    const pitch = toFiniteNumber(o.pitchDeg) ?? 0, azimuth = toFiniteNumber(o.rotationDeg) ?? 180;
    const eave = Number.isInteger(o.eaveEdge) && o.footprint ? o.eaveEdge : facingEdge(ring, azimuth);
    return { ex, ny,
      frame: { ...f0, eastM: r3m((toFiniteNumber(f0.eastM) ?? 0) + ex), northM: r3m((toFiniteNumber(f0.northM) ?? 0) + ny) },
      roof: { setbackM: toFiniteNumber(siteGeometry?.roof?.setbackM) ?? 0.5,
        footprint: ring, widthM: r3m(Math.max(...xs) - Math.min(...xs)), depthM: r3m(Math.max(...ys) - Math.min(...ys)),
        heightM: r3m(base + (toFiniteNumber(o.heightM) ?? 0)), pitchDeg: pitch, azimuthDeg: azimuth,
        ...(o.freeAzimuth ? { freeAzimuth: true } : {}),
        ...(o.azimuthSource ? { azimuthSource: o.azimuthSource } : {}),
        ...(eave != null ? { eaveEdge: eave } : {}),
        ...(toFiniteNumber(o.eaveHeightM) != null ? { eaveHeightM: toFiniteNumber(o.eaveHeightM) } : {}) } };
  };
  const withoutFace = (o) => (siteGeometry?.obstacles || []).filter((x) => x.id !== o.id);
  // This plane's own face: its frame moves to the face's centre, so its
  // height there is the roof's height, and its other objects are re-based.
  const takeFace = (o) => {
    const { ex, ny, frame, roof } = faceAsRoof(o);
    setForm((prev) => ({ ...prev, module_offsets: {}, module_array: null, excluded_slots: [], module_assignments: null, manual_layout_edited: false, array_offset_east_m: 0, array_offset_north_m: 0 }));
    handleField("site_geometry", { ...siteGeometry, version: 2, frame, roof,
      obstacles: withoutFace(o).map((x) => ({ ...x, eastM: r3m((toFiniteNumber(x.eastM) ?? 0) - ex), northM: r3m((toFiniteNumber(x.northM) ?? 0) - ny) })) });
    pushFaceAngles([{ planeId: null, pitchDeg: roof.pitchDeg, azimuthDeg: roof.azimuthDeg }]);
  };
  // An orientation that has no face yet takes this one, with its angles.
  const attachFace = (o, plane) => {
    const { frame, roof } = faceAsRoof(o);
    const g = plane.siteGeometry || {};
    const old = g.frame;
    const dx = old ? (toFiniteNumber(old.eastM) ?? 0) - frame.eastM : 0;
    const dy = old ? (toFiniteNumber(old.northM) ?? 0) - frame.northM : 0;
    const obstacles = (g.obstacles || []).filter((x) => x.base !== "roof")
      .map((x) => ({ ...x, eastM: r3m((toFiniteNumber(x.eastM) ?? 0) + dx), northM: r3m((toFiniteNumber(x.northM) ?? 0) + dy) }));
    const next = { ...sharedSceneEditsRef.current, [plane.id]: { ...(plane.spacing || {}),
      module_offsets: {}, module_array: null, excluded_slots: [], module_assignments: null, manual_layout_edited: false, array_offset_east_m: 0, array_offset_north_m: 0,
      site_geometry: { ...g, version: 2, frame: { ...frame, elevationM: frame.elevationM }, roof, obstacles } } };
    sharedSceneEditsRef.current = next;
    setSceneEdits(next);
    handleField("site_geometry", { ...siteGeometry, version: 2, frame: siteGeometry?.frame || { eastM: 0, northM: 0, elevationM: 0 }, obstacles: withoutFace(o) });
    pushFaceAngles([{ planeId: plane.id, pitchDeg: roof.pitchDeg, azimuthDeg: roof.azimuthDeg }]);
  };
  const promoteFace = (o) => {
    if (!onPromoteRoofFace) return;
    const frame = siteGeometry?.frame || null;
    // The page makes the plane; the face leaves this draft.
    const id = onPromoteRoofFace(o, frame);
    if (id) handleField("site_geometry", { ...siteGeometry, version: 2, frame: frame || { eastM: 0, northM: 0, elevationM: 0 }, obstacles: withoutFace(o) });
  };
  const handleFacePv = (ref, enabled) => {
    const frame = siteGeometry?.frame || null;
    if (enabled && ref.obstacle) {
      if (!hasOutline(siteGeometry?.roof)) takeFace(ref.obstacle);
      else if (facelessPlanes.length) setPvChoice({ obstacle: ref.obstacle });
      else promoteFace(ref.obstacle);
    } else if (!enabled && ref.planeId && onDemoteRoofFace) {
      // The page removes the plane and hands back its roof as a face for
      // this draft, with the objects that stood on it.
      const faces = onDemoteRoofFace(ref.planeId, frame);
      if (faces?.length) handleField("site_geometry", { ...siteGeometry, version: 2, frame: frame || { eastM: 0, northM: 0, elevationM: 0 },
        obstacles: [...(siteGeometry?.obstacles || []), ...faces] });
    }
  };
  const removeFace = ({ planeId, primary }) => {
    if (planeId == null && (primary || !onRemoveFace)) {
      // The first orientation keeps its place; its face, and what stood on it, go.
      setForm((prev) => ({ ...prev, module_offsets: {}, module_array: null, excluded_slots: [], module_assignments: null, manual_layout_edited: false, array_offset_east_m: 0, array_offset_north_m: 0 }));
      handleField("site_geometry", { ...siteGeometry, roof: null, obstacles: (siteGeometry?.obstacles || []).filter((o) => o.base !== "roof") });
      return;
    }
    if (planeId != null && primary) {
      const plane = draftContextPlanes?.find((p) => p.id === planeId);
      if (!plane) return;
      const g = plane.siteGeometry || {};
      const next = { ...sharedSceneEditsRef.current, [planeId]: { ...(plane.spacing || {}),
        module_offsets: {}, module_array: null, excluded_slots: [], module_assignments: null, manual_layout_edited: false, array_offset_east_m: 0, array_offset_north_m: 0,
        site_geometry: { ...g, roof: null, obstacles: (g.obstacles || []).filter((o) => o.base !== "roof") } } };
      sharedSceneEditsRef.current = next;
      setSceneEdits(next);
      return;
    }
    if (planeId != null && sharedSceneEditsRef.current[planeId]) {
      const { [planeId]: _gone, ...rest } = sharedSceneEditsRef.current;
      sharedSceneEditsRef.current = rest;
      setSceneEdits(rest);
    }
    onRemoveFace?.(planeId);
  };
  const activeIsPrimary = !orientationLabel || orientationLabel.index === 1;
  const groundGroupPicker = !onRoof && onSelectPlane && draftContextPlanes?.length ? (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mb: 1 }}>
      <Typography variant="body2">{tr("Ομάδα επεξεργασίας:", "Editing group:")}</Typography>
      <Button size="small" variant="contained" aria-pressed="true">
        {tr("Ομάδα", "Group")} {orientationLabel?.index || 1}
      </Button>
      {draftContextPlanes.map(plane => <Button key={plane.id} size="small" variant="outlined" aria-pressed="false"
        onClick={() => onSelectPlane(plane.id)}>{tr("Ομάδα", "Group")} {plane.label}</Button>)}
      <Typography variant="caption">{tr("Μπλε: ενεργή ομάδα · οι αλλαγές σειρών και βήματος αφορούν μόνο αυτή.",
        "Blue: active group · row and pitch edits apply only to this group.")}</Typography>
    </Stack>
  ) : null;
  const validationError = useMemo(() => {
    if (electricalLayoutChanged && !form.manual_layout_edited) return tr(
      "Η φυσική διάταξη δεν συμφωνεί με το ηλεκτρολογικό σχέδιο. Επιλέξτε «Ενημέρωση από ηλεκτρολογικό σχέδιο» και ελέγξτε τις θέσεις των πάνελ.",
      "The physical layout does not match the electrical design. Select “Update from electrical design” and review the module positions.");
    if (invalidSharedFaces.length) return tr(
      `Ελέγξτε τη διάταξη στις πλευρές ${invalidSharedFaces.join(", ")}: πλαίσια εκτός στέγης, επικαλύψεις ή μη έγκυρη γεωμετρία.`,
      `Check the layouts on surfaces ${invalidSharedFaces.join(", ")}: modules outside the roof, overlaps or invalid geometry.`);
    if (roofStatus.moduleOverlap) return tr("Υπάρχουν πάνελ που επικαλύπτονται. Μετακινήστε τα πριν αποθηκεύσετε.", "Some modules overlap. Move them before saving.");
    if (roofStatus.invalidOffsets) return tr("Η δομή των σειρών άλλαξε. Επαναφέρετε τις θέσεις πάνελ και διαμορφώστε ξανά τη διάταξη.", "The row structure changed. Reset module positions and arrange the layout again.");
    if (siteGeometryErrors(siteGeometry).length) return tr("Ελέγξτε τις διαστάσεις της στέγης και των εμποδίων.", "Check the roof and obstacle dimensions.");
    if (onRoof && !form.parcel_tables &&
      [form.array_offset_east_m, form.array_offset_north_m].some((value) =>
        String(value ?? "").trim() !== "" && !Number.isFinite(Number(value)))) {
      return tr("Η μετατόπιση της διάταξης πρέπει να είναι αριθμός σε μέτρα.", "The array offset must be a number in metres.");
    }
    if (siteGeometry?.roof && (roofStatus.outside || roofStatus.intersecting || roofStatus.belowRoof)) {
      // Which of the three, and how many rows: "does not fit" was one sentence
      // for a row inside the setback strip, a row through a chimney and a row
      // sunk below the roof, and the first of those looks like it fits.
      const setback = toFiniteNumber(siteGeometry.roof.setbackM) ?? 0;
      const parts = [];
      if (roofStatus.outside) parts.push(tr(
        `${roofStatus.outside} σειρά/ές βγαίνουν έξω από τη χρήσιμη επιφάνεια της στέγης (${setback} m από τις ακμές)`,
        `${roofStatus.outside} row(s) extend beyond the usable roof (${setback} m from the edges)`));
      if (roofStatus.intersecting) parts.push(tr(
        `${roofStatus.intersecting} σειρά/ές τέμνουν εμπόδιο`, `${roofStatus.intersecting} row(s) intersect an obstacle`));
      if (roofStatus.belowRoof) parts.push(tr(
        `${roofStatus.belowRoof} σειρά/ές βυθίζονται κάτω από την επιφάνεια της στέγης`, `${roofStatus.belowRoof} row(s) sink below the roof surface`));
      return `${parts.join("· ")}. ` + tr(
        "Προσαρμόστε τη θέση, τις σειρές, το βήμα, την απόσταση από τις ακμές ή τις διαστάσεις.",
        "Adjust the position, the rows, the pitch, the edge setback or the dimensions.");
    }
    if (groundGeometryInvalid) return tr("Οι σειρές πρέπει να βρίσκονται εντός του γεωτεμαχίου και να μην επικαλύπτονται. Διορθώστε τη θέση ή το μήκος τους στην κάτοψη.",
      "Rows must remain inside the parcel without overlapping. Adjust their positions or lengths in the plan.");
    if (!hasGeometryInput) return "";
    const pitch = toFiniteNumber(form.minimum_distance_m);
    const rows = toFiniteNumber(form.row_vertical_modules);
    if (pitch == null || pitch <= 0) return tr("Το βήμα πρέπει να είναι μεγαλύτερο από 0.", "Pitch must be greater than 0.");
    const clear = toFiniteNumber(form.ground_clearance_m);
    if (String(form.ground_clearance_m ?? "").trim() !== "" &&
        (clear == null || clear < 0 || clear > 10)) {
      // The engine rejects anything outside this range as a unit mistake, so
      // the form refuses it here rather than saving a number that will be
      // silently swapped for 0.5 m at run time.
      return tr("Το ύψος από το έδαφος πρέπει να είναι μεταξύ 0 και 10 m.", "Ground clearance must be between 0 and 10 m.");
    }
    const fieldRows = toFiniteNumber(form.field_rows);
    if (String(form.field_rows ?? "").trim() !== "" &&
        (fieldRows == null || !Number.isInteger(fieldRows) || fieldRows <= 0)) {
      return tr("Οι σειρές στο γήπεδο πρέπει να είναι ακέραιος αριθμός ή να μείνουν κενές.", "Rows in the field must be a whole number, or left blank.");
    }
    if (rows == null || !Number.isInteger(rows) || rows <= 0) {
      return tr("Τα πλαίσια καθ’ ύψος ανά σειρά πρέπει να είναι θετικός ακέραιος αριθμός.", "Row vertical modules must be a positive integer.");
    }
    // Say which number is missing. GCR is L / pitch, so an unknown module
    // length makes it zero - and reporting that as "GCR must be below 1" sends
    // the reader to change a pitch that was never the problem.
    const length = toFiniteNumber(moduleLength);
    if (length == null || length <= 0) {
      return tr(
        "Δεν υπάρχουν οι διαστάσεις του πλαισίου, οπότε δεν υπολογίζονται το ύψος σειράς και το GCR. Επιλέξτε πλαίσιο στον Εξοπλισμό ή ανοίξτε ξανά την καρτέλα όταν φορτωθούν τα τεχνικά στοιχεία του.",
        "Module dimensions are not available, so the row height and GCR cannot be computed. Select a module on the Equipment tab, or reopen this tab once its datasheet has loaded."
      );
    }
    if (previewSpacingInfo.gcr == null || !(previewSpacingInfo.gcr > 0)) {
      return tr("Μη έγκυρη γεωμετρία: το ύψος σειράς προκύπτει μηδέν. Ελέγξτε το μήκος πλαισίου και τα πλαίσια καθ’ ύψος.", "Invalid spacing geometry: the row height works out as zero. Check the module length and the number of stacked modules.");
    }
    const coplanar = installationType === "roof_flush";
    const projected = (modulePortrait === false ? Number(moduleWidth) : length) * rows * Math.cos(Number(draftSurfaceTilt) * Math.PI / 180);
    if (coplanar && pitch < projected - 1e-6) return tr("Το βήμα προκαλεί επικάλυψη πλαισίων πάνω στη στέγη.", "The pitch makes modules overlap on the roof.");
    if (!coplanar && !(previewSpacingInfo.gcr < 1)) {
      return tr("Μη έγκυρη γεωμετρία: το GCR πρέπει να είναι μικρότερο από 1. Αυξήστε το βήμα ή μειώστε τα πλαίσια καθ’ ύψος.", "Invalid spacing geometry: GCR must be below 1. Increase pitch or reduce row vertical modules.");
    }
    if (onRoof && electricalDesignKey && !stringLayout?.check?.consistent && !form.manual_layout_edited) return tr(
      "Η αντιστοίχιση strings δεν έχει ολοκληρωθεί. Αν υπάρχουν παλιές χειροκίνητες θέσεις, επιλέξτε «Ενημέρωση από ηλεκτρολογικό σχέδιο».",
      "String mapping is not complete. If previous manual string positions remain, select “Update from electrical design”.");
    return "";
  }, [
    groundGeometryInvalid, electricalLayoutChanged, electricalDesignKey, stringLayout, form.manual_layout_edited,
    invalidSharedFaces, form.minimum_distance_m,
    form.row_vertical_modules,
    hasGeometryInput,
    moduleLength,
    previewSpacingInfo.gcr,
    form.ground_clearance_m, form.field_rows, form.array_offset_east_m, form.array_offset_north_m,
    form.parcel_tables, onRoof, siteGeometry, roofStatus, tr, installationType, modulePortrait, moduleWidth, draftSurfaceTilt,
  ]);

  // Advisory only: `validationError` above still owns what blocks saving.
  const spacingAdvisory = useMemo(
    () =>
      hasGeometryInput && !validationError && installationType !== "roof_flush" && Number(form.field_rows) !== 1
        ? evaluateRowSpacing({
            gcr: previewSpacingInfo.gcr,
            pitchM: previewSpacingInfo.minimum_distance_m,
            tiltDeg: surfaceTilt,
            latitudeDeg: latitude,
            mountType,
            greek: isGreek,
          })
        : { notes: [], severity: null },
    [
      hasGeometryInput,
      validationError,
      form.field_rows,
      installationType,
      previewSpacingInfo.gcr,
      previewSpacingInfo.minimum_distance_m,
      surfaceTilt,
      latitude,
      mountType,
      isGreek,
    ]
  );

  const handleField = (key, value) => {
    if (!onRoof && groundRows && form.parcel_tables?.length && key === "field_rows" && Number.isInteger(Number(value)) && Number(value) > 0) {
      const count = Number(value);
      if (count > 2000) return;
      let layout = groundRows.layout;
      if (count < layout.tables.length) layout = { ...layout, tables: layout.tables.slice(0, count) };
      while (layout && count > layout.tables.length) {
        layout = editGroundRow(layout, { type: "add", index: layout.tables.length - 1 }, groundRows);
      }
      if (layout) applyGroundRows(layout);
      else setGroundEditError(tr("Οι επιπλέον σειρές δεν χωρούν δίπλα στην τελευταία. Μετακινήστε τις υπάρχουσες σειρές ή μειώστε το μήκος/βήμα.",
        "The additional rows do not fit beside the last row. Move existing rows or reduce length/pitch."));
      return;
    }
    if (!onRoof && groundRows && form.parcel_tables?.length && key === "minimum_distance_m" && Number(value) > 0) {
      changeGroundRow({ type: "pitch", pitch: Number(value) });
      return;
    }
    setForm((prev) => ({ ...prev, [key]: value,
      // A new total row count requests an evenly distributed layout. An
      // explicit row list or parcel placement would otherwise override it.
      ...(key === "field_rows" && Number.isInteger(Number(value)) && Number(value) > 0
        && Number(value) !== Number(prev.field_rows)
        ? { row_modules: null, row_labels: null, parcel_tables: null, parcel_outline: null } : {}),
      ...(isFlushRoof && key === "row_vertical_modules" && roofGapCm != null && Number(value) > 0
        ? { minimum_distance_m: String((roofModuleLength * Number(value) + roofGapCm / 100) * roofCos) } : {}),
      ...(isFlushRoof && prev.module_array && ["minimum_distance_m", "row_vertical_modules"].includes(key)
        ? { module_array: { ...prev.module_array, rowSpacingM: key === "minimum_distance_m"
          ? -Number(value) / roofCos : -(roofModuleLength * Number(value) + (roofGapCm ?? 0) / 100) } } : {}),
    }));
  };

  /**
   * The spacing as this panel currently has it, in the shape everything
   * downstream expects.
   *
   * ONE RECIPE. Save and the layout PDF both go through here, because the last
   * time a builder was shared but the recipe was not -- the loss ladder -- the
   * web page and the PDF drew different diagrams for months and nobody noticed
   * until a user asked why.
   */
  // Number(null) and Number("") are both 0, so a naive isFinite check treats a
  // project that has never been placed on the map as sitting at 0N 0E. Both
  // buttons below need real coordinates, so they ask the same question once.
  const hasCoordinates =
    toFiniteNumber(latitude) != null && toFiniteNumber(longitude) != null;

  // The sheet needs the module's own size, and it arrives late.
  //
  // The dimensions are fetched by an effect keyed on the module name, so for
  // the first moment after a project loads they are null. A sheet built in
  // that moment loses everything derived from them -- the site plan, the
  // collector width, the row length, the parcel area, the ground coverage
  // ratio -- and prints an average row computed from the totals instead of
  // from the fitted rows, which then disagrees with its own string table two
  // pages on. It is not a sheet with gaps; it is a sheet that contradicts
  // itself. So the button waits, and says what it is waiting for.
  const hasModuleGeometry =
    toFiniteNumber(moduleLength) > 0 && toFiniteNumber(moduleWidth) > 0;
  const sceneNeedsMeasurement = Boolean(form.parcel_tables?.length || siteGeometry?.obstacles?.length || contextPlanes?.length || Object.keys(form.module_offsets).length || excludedSlots.length);
  const sceneBlocked = sceneNeedsMeasurement && (!sceneShading?.table || sceneShading.stale || sceneShading.sceneVersion !== 2);
  const finalReportReady = hasCoordinates && hasModuleGeometry && !validationError && Boolean(stringLayout?.check?.consistent) && !sceneBlocked && !(reportLayouts || []).some((p) => p.orientation.index !== orientationLabel?.index && (!p.spacingInfo?.module_mapping_valid || p.obstacleShading?.stale || p.obstacleShading?.sceneVersion !== 2));
  const hasContextMoves = Object.keys(form.context_frames).length > 0 || Object.keys(sceneEdits).length > 0;
  const reportReady = hasCoordinates && hasModuleGeometry && !hasContextMoves;
  const reportBlockedBecause = !hasCoordinates
    ? tr("Ορίστε πρώτα τη θέση στον χάρτη.", "Place the site on the map first.")
    : !moduleName
      ? tr("Επιλέξτε πρώτα ΦΒ πλαίσιο.", "Select a PV module first.")
      : !hasModuleGeometry
        ? moduleGeometryLoading
          ? tr("Φορτώνονται οι διαστάσεις του ΦΒ πλαισίου.", "The module's dimensions are still loading.")
          : tr("Δεν υπάρχουν διαθέσιμες διαστάσεις για το επιλεγμένο ΦΒ πλαίσιο. Ελέγξτε την καταχώρισή του.", "The selected module has no usable dimensions. Check its catalogue entry.")
        : hasContextMoves
          ? tr("Αποθηκεύστε τη διάταξη για να περιληφθούν οι μετακινήσεις των άλλων πλευρών στην αναφορά.", "Save the layout to include the moved surfaces in the report.")
          : "";

  // Its STATE, pushed up instead of read off the ref.
  //
  // Whoever holds the button is a different component, and writing busy into a
  // ref does not re-render it: the button read `ref.current.busy` while
  // rendering, which is only ever the value from the last time something else
  // happened to re-render it. So a report that took four seconds to build gave
  // no sign of building at all, and an error never appeared. State goes up
  // through a callback, on the three values that matter and only when they
  // change.
  useEffect(() => {
    if (onReportState) {
      // A refusal and a "not yet" are different things and are reported
      // separately: one is red, the other is the button explaining itself.
      onReportState({
        busy: pdfBusy,
        ready: reportReady,
        error: pdfError,
        reason: pdfBusy ? "" : reportBlockedBecause,
      });
    }
  }, [onReportState, pdfBusy, reportReady, reportBlockedBecause, pdfError]);

  const pitchIsStillTheSolvedOne = () => {
    const stored = toFiniteNumber(
      initialSpacingInfo?.minimum_distance_m ?? initialSpacingInfo?.pitch_m
    );
    const typed = toFiniteNumber(form.minimum_distance_m);
    return stored != null && typed != null && Math.abs(stored - typed) < 1e-6;
  };

  const spacingInfoForExport = () => ({
    scene_review_required: true,
    string_routing: form.string_routing,
    shading_samples_per_module: form.shading_samples_per_module,
    module_positions: electricalPositions,
    module_offsets: resolvedModuleOffsets,
    module_array: effectiveModuleArray,
    excluded_slots: excludedSlots,
    module_assignments: form.module_assignments,
    manual_layout_edited: form.manual_layout_edited,
    module_mapping_valid: Boolean(stringLayout?.check?.consistent) && !electricalLayoutChanged,
    electrical_design_key: electricalDesignKey,
    ...(siteGeometry ? { site_geometry: siteGeometry } : {}),
    mode: previewSpacingInfo.mode,
    terrace_area_m2: previewSpacingInfo.terrace_area_m2,
    ...(!onRoof ? { terrace_area_mode: form.terrace_area_mode } : {}),
    minimum_distance_m: previewSpacingInfo.minimum_distance_m,
    pitch_m: previewSpacingInfo.pitch_m,
    row_vertical_modules: previewSpacingInfo.row_vertical_modules,
    gcr: previewSpacingInfo.gcr,
    // Not part of the spacing geometry, but it is set in the same panel and
    // saved by the same button; the caller writes it to panelOrientation.
    ground_clearance_m: toFiniteNumber(form.ground_clearance_m),
    // Blank stays blank: the viewer then assumes a square field rather than
    // being handed a row count nobody chose.
    field_rows: toFiniteNumber(form.field_rows),
    array_offset_east_m: toFiniteNumber(form.array_offset_east_m) ?? 0,
    array_offset_north_m: toFiniteNumber(form.array_offset_north_m) ?? 0,
    row_modules: rowsForStrings,
    // The rows as the parcel gave them, table by table. Saved with the layout
    // so the string map, the sheet and the 3D view all read one row list.
    ...(Array.isArray(form.row_modules) && form.row_modules.length
      ? { row_modules: form.row_modules, row_labels: form.row_labels || null,
          parcel_tables: form.parcel_tables || null,
          parcel_outline: form.parcel_outline || null,
          fit_to_modules: form.fit_to_design && Number(totalModules) > 0 ? Math.round(Number(totalModules)) : null,
          fit_to_strings: parcelFit ? parcelFit.limited_to_strings ?? null : initialSpacingInfo?.fit_to_strings ?? null,
          string_columns: parcelFit ? parcelFit.string_columns ?? null : initialSpacingInfo?.string_columns ?? null,
          setback_m: toFiniteNumber(form.setback_m) ?? 0,
          parcel_fit: parcelFit
            ? { area_m2: parcelFit.area_m2, usable_area_m2: parcelFit.usable_area_m2,
                tables: parcelFit.tables, modules: parcelFit.modules, strings: parcelFit.strings ?? null }
            : initialSpacingInfo?.parcel_fit || null }
      : {}),
    // Hand placements of strings on rows. Part of the layout, because the
    // wiring follows them.
    ...(form.string_overrides && Object.keys(form.string_overrides).length
      ? { string_overrides: form.string_overrides } : {}),
    increment_factor: incrementFactor,
    ...(previewSpacingInfo.source ? { source: previewSpacingInfo.source } : {}),
    // The criterion the pitch was SOLVED for, and only when it still applies.
    // previewSpacingInfo is always "manual" because it is rebuilt from the form,
    // so the criterion has to come from what was stored -- and it stops being
    // true the moment somebody types a different pitch. Printing "no shadow
    // between 09:00 and 15:00" over a hand-typed pitch would be a claim the
    // sheet cannot support.
    ...(pitchIsStillTheSolvedOne() && initialSpacingInfo?.pitch_source
      ? { pitch_source: initialSpacingInfo.pitch_source }
      : {}),
  });

  // What this face's Save hands over, and the key of the project it would leave.
  const spacingForSave = () => {
    const { increment_factor, pitch_source, ...saved } = spacingInfoForExport();
    return saved;
  };
  const otherSceneLayouts = () => {
    const others = { ...sharedSceneEditsRef.current };
    delete others[workspaceId];
    return Object.keys(others).length ? others : undefined;
  };
  const sceneDraftKeyNow = () => (sceneShadingDraft && sceneShadingKeyFor
    ? sceneShadingKeyFor(spacingForSave(), form.context_frames, otherSceneLayouts(), meshPlacement) : null);
  // After every render: the key reads the whole layout to save (form, strings,
  // shared moves, the page's other drafts), more than a dependency list can
  // name. The value is a boolean, so an unchanged answer does not render again.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const key = sceneDraftKeyNow();
    setSceneDraftApplies(Boolean(key) && key === sceneShadingDraft?.key);
  });
  const calculateAllFaces = onCalculateShadingAll
    ? async ({ placement, onProgress, shouldCancel }) => {
      if (electricalLayoutChanged || (form.manual_layout_edited && !stringLayout?.check?.consistent)) throw new Error(tr(
        "Συμφιλιώστε πρώτα την κάτοψη με τον ηλεκτρολογικό σχεδιασμό.", "Reconcile the plan and electrical design first."));
      await onCalculateShadingAll(spacingForSave(), form.context_frames, otherSceneLayouts(), placement, { onProgress, shouldCancel });
      setShadingSaveIssue(null);
    }
    : null;

  const [saving, setSaving] = useState(false);
  const [saveNotice, setSaveNotice] = useState(null);
  const [shadingSaveIssue, setShadingSaveIssue] = useState(null);
  useEffect(() => { setShadingSaveIssue(null); }, [workspaceId, open]);
  const savingRef = useRef(false);
  const saveActiveRef = useRef(true);
  useEffect(() => {
    saveActiveRef.current = true;
    return () => { saveActiveRef.current = false; };
  }, []);
  const handleSave = async () => {
    if (!onSave || validationError || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setSaveNotice(null);
    try {
      const saved = spacingForSave();
      // The four views travel with the saved layout so the feasibility report can
      // show the field as designed, with no layout tab open when the run happens.
      // Small JPEGs: four of them at 720px come to about a quarter of a megabyte.
      const payload = {
        ...saved,
        scene_views: captureSceneViews({ width: 720, height: 405, format: "jpeg", quality: 0.8 }),
      };
      // The shading calculated on this very layout goes with it (4th argument):
      // every face's, when they were calculated together and still hold, or
      // this face's own. A draft made on an earlier state of it is left behind.
      const sceneKey = sceneDraftKeyNow();
      const shading = sceneKey && sceneKey === sceneShadingDraft?.key ? { sceneKey }
        : draftShadingApplies ? draftShading.payload : undefined;
      let result;
      if (automaticShading) {
        result = await onSave(payload, form.context_frames, otherSceneLayouts(), shading, {
          placement: meshPlacement, shouldCancel: () => !saveActiveRef.current,
        });
        sharedSceneEditsRef.current = {};
        setSceneEdits({});
      } else if (Object.keys(sharedSceneEditsRef.current).length) {
        const otherLayouts = { ...sharedSceneEditsRef.current };
        delete otherLayouts[workspaceId];
        const pending = onSave(payload, form.context_frames, otherLayouts, ...(shading !== undefined ? [shading] : []));
        if (pending && typeof pending.then === "function") await pending;
        sharedSceneEditsRef.current = {};
        setSceneEdits({});
      } else {
        const pending = shading !== undefined
          ? onSave(payload, form.context_frames, undefined, shading)
          : Object.keys(form.context_frames).length
            ? onSave(payload, form.context_frames)
            : onSave(payload);
        if (pending && typeof pending.then === "function") await pending;
      }
      setDraftShading(null);
      if (result?.shadingError) {
        const issue = result.shadingIssue || scenePreparationIssue(result.shadingError);
        setShadingSaveIssue(issue);
        setSaveNotice({ severity: "warning", message: layoutSaveFailureMessage(issue, isGreek, true) });
      } else {
        setShadingSaveIssue(null);
        // The shared save either reused current shading or prepared it now.
        // A saved layout with pending shading must not be reported as complete.
        setSaveNotice(result?.shadingPending
          ? { severity: "warning", message: tr(
            "Η φυσική διάταξη αποθηκεύτηκε, αλλά οι σκιάσεις χρειάζονται ενημέρωση. Επιλέξτε ξανά «Αποθήκευση διάταξης».",
            "Physical layout saved, but shading needs updating. Select “Save layout” again.") }
          : { severity: "success", message: shading || result?.shadingUpdated
            ? tr("Αποθηκεύτηκαν η φυσική διάταξη και οι σκιάσεις της.", "Physical layout and its shading saved.")
            : tr("Η φυσική διάταξη αποθηκεύτηκε.", "Physical layout saved.") });
      }
    } catch (error) {
      setSaveNotice({ severity: "error", message: layoutSaveFailureMessage(error, isGreek) });
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  // Keep edits in memory when selecting another roof face. This is not a
  // project save: validation and the existing Save button remain authoritative.
  useEffect(() => {
    if (!workspaceDraftsRef || !workspaceId || loadedFormKey !== initialFormKey) return;
    if (sharedSceneEditsRef.current[workspaceId]) {
      sharedSceneEditsRef.current = { ...sharedSceneEditsRef.current,
        [workspaceId]: { ...sharedSceneEditsRef.current[workspaceId], ...spacingInfoForExport() } };
    }
    workspaceDraftsRef.current[workspaceId] = {
      initialFormKey, form, baseline: baselineRef.current,
      source: JSON.stringify(initialSpacingInfo || null),
      spacing: spacingInfoForExport(),
      dirty: JSON.stringify(effectiveForm) !== baselineRef.current || electricalLayoutChanged,
      // Kept with the face's draft so it survives selecting another face; only
      // this face's own Save stores it (its key is checked again on return).
      shading: draftShading,
      shadingDirty: draftShadingApplies,
      // The page saves every face's draft with one Save; one this face's own
      // validation refuses is left for the face to fix.
      invalid: Boolean(validationError),
    };
  });

  const alignmentScene = useMemo(() => onRoof && !form.parcel_tables && siteGeometry?.roof ? resolveSceneModel({
    latitude, longitude, totalModules, surfaceTilt: draftSurfaceTilt, surfaceAzimuth: draftSurfaceAzimuth,
    pitch: previewSpacingInfo.minimum_distance_m, rowVerticalModules: previewSpacingInfo.row_vertical_modules,
    moduleLength, moduleWidth, groundClearance: toFiniteNumber(form.ground_clearance_m),
    fieldRows: toFiniteNumber(form.field_rows), rowModules: form.row_modules,
    arrayOffsetEast: toFiniteNumber(form.array_offset_east_m) ?? 0, arrayOffsetNorth: toFiniteNumber(form.array_offset_north_m) ?? 0,
    portrait: modulePortrait, mountType, installationType, siteGeometry,
  }) : null, [onRoof, form.parcel_tables, siteGeometry, latitude, longitude, totalModules,
    draftSurfaceTilt, draftSurfaceAzimuth, previewSpacingInfo.minimum_distance_m,
    previewSpacingInfo.row_vertical_modules, moduleLength, moduleWidth, form.ground_clearance_m,
    form.field_rows, form.row_modules, form.array_offset_east_m, form.array_offset_north_m,
    modulePortrait, mountType, installationType]);
  const alignmentRows = useMemo(() => alignmentScene && !alignmentScene.tracker && alignmentScene.field
    ? buildRows(alignmentScene, draftSurfaceTilt, alignmentScene.field.rowsUsed,
      alignmentScene.field.rowLength, alignmentScene.field.rows, alignmentScene.field.rowModules).rows : [],
  [alignmentScene, draftSurfaceTilt]);
  const effectiveModuleArray = useMemo(() => isFlushRoof ? (form.module_array || (alignmentScene
    ? legacyRoofModuleArray(moduleQuads(alignmentRows, alignmentScene.verticalModules), form.module_offsets,
      alignmentScene.terrain.groundZ) : null)) : null, [isFlushRoof, form.module_array, form.module_offsets, alignmentRows, alignmentScene]);
  const resolvedModuleOffsets = useMemo(() => alignmentScene
    ? resolveRoofModuleOffsets(alignmentRows, alignmentScene.verticalModules,
      { ...form, module_array: effectiveModuleArray }, alignmentScene.terrain.groundZ) : form.module_offsets,
  [alignmentScene, alignmentRows, form, effectiveModuleArray]);
  const allPlanModules = useMemo(() => alignmentScene
    ? moduleQuads(alignmentRows, alignmentScene.verticalModules, resolvedModuleOffsets, alignmentScene.terrain.groundZ) : [],
  [alignmentScene, alignmentRows, resolvedModuleOffsets]);
  const planModules = useMemo(() => allPlanModules.filter(q => !excludedSlots.includes(q.slot)), [allPlanModules, excludedSlots]);
  const missingPlanModules = useMemo(() => {
    const assigned = new Set((form.module_assignments || []).map(p => `${p.row}:${p.column}:${p.tier}`));
    return allPlanModules.filter(q => excludedSlots.includes(q.slot) && assigned.has(q.slot));
  }, [allPlanModules, excludedSlots, form.module_assignments]);
  const [planUndo, setPlanUndo] = useState(null);
  useEffect(() => { setPlanUndo(null); }, [initialFormKey, workspaceId, open]);
  const canEditPanels = Boolean(alignmentScene && (form.manual_layout_edited || stringLayout?.check?.consistent));
  const applyPlanEdit = (operation) => {
    if (!canEditPanels) return null;
    const before = Object.fromEntries(planEditFields.map(k => [k, form[k]]));
    const buildQuads = candidate => {
      const rows = candidate.row_modules || rowsForStrings;
      if (!rows.length) return [];
      const model = { ...alignmentScene, emptyRoof: false, arrayOffset: {
        eastM: Number(candidate.array_offset_east_m) || 0, northM: Number(candidate.array_offset_north_m) || 0 } };
      const geometry = buildRows(model, draftSurfaceTilt, rows.length,
        Math.max(...rows) * model.moduleWidth, rows.length, rows).rows;
      return moduleQuads(geometry, model.verticalModules,
        resolveRoofModuleOffsets(geometry, model.verticalModules, candidate, model.terrain.groundZ), model.terrain.groundZ);
    };
    const result = editModulePlan({ ...form, module_array: effectiveModuleArray }, operation, {
      rowModules: rowsForStrings, positions: electricalPositions, buildQuads, roof: siteGeometry?.roof,
    });
    if (!result) return null;
    const undo = () => setForm(current => ({ ...current, ...before }));
    setPlanUndo(() => undo);
    setForm(result.form);
    setDraftShading(null);
    return { slot: result.slot, undo };
  };
  // One-time migration, not an angle-change repair effect. Both render and
  // export already resolve the canonical group against the CURRENT plane.
  useLayoutEffect(() => {
    if (!form.module_array && effectiveModuleArray) setForm((current) => ({ ...current, module_array: effectiveModuleArray }));
  }, [form.module_array, effectiveModuleArray]);
  const savePanelTurn = (offsets, axis = "length") => {
    const array = isFlushRoof ? roofModuleArray(moduleQuads(alignmentRows, alignmentScene.verticalModules), offsets,
      alignmentScene.terrain.groundZ, undefined, { ...effectiveModuleArray, axis }) : null;
    const candidate = { ...form, module_offsets: offsets, module_array: array };
    const resolved = resolveRoofModuleOffsets(alignmentRows, alignmentScene.verticalModules, candidate, alignmentScene.terrain.groundZ);
    const quads = moduleQuads(alignmentRows, alignmentScene.verticalModules, resolved, alignmentScene.terrain.groundZ, excludedSlots);
    if (!panelTurnAllowed(quads, siteGeometry)) return false;
    const before = Object.fromEntries(planEditFields.map(k => [k, form[k]]));
    setPlanUndo(() => () => setForm(current => ({ ...current, ...before })));
    setForm(candidate);
    setDraftShading(null);
    return true;
  };
  const alignPanels = planModules.length ? (edge, direction = "across") => savePanelTurn(
    alignModulesToEdge(allPlanModules, edge, resolvedModuleOffsets, alignmentScene.terrain.groundZ, direction, planModules, !isFlushRoof),
    direction === "along" ? "length" : "width") : null;
  const rotatePanels = planModules.length ? (bearing) => savePanelTurn(
    rotateModulesToAxis(allPlanModules, bearing, resolvedModuleOffsets, "length", planModules,
      isFlushRoof ? null : alignmentScene.terrain.groundZ)) : null;

  const updateElectricalLayout = () => {
    // A layout saved for a different module (or one that already overlaps)
    // gets its line spacing refitted to the module on the roof now.
    setForm(fitRoofRowSpacing(updateLayoutFromDesign({ ...form, row_modules: rowsForStrings }, {
      totalModules, moduleLength, moduleWidth, portrait: modulePortrait,
      designKey: electricalDesignKey, moduleArray: effectiveModuleArray,
    }), { installationType, alongSlope: roofModuleLength, tilt: draftSurfaceTilt }));
    setParcelFit(null);
    setDraftShading(null);
    // Do not restore the old slot offsets when this face is selected again.
    const edits = sharedSceneEditsRef.current[workspaceId];
    if (edits) {
      const next = { ...sharedSceneEditsRef.current };
      delete next[workspaceId];
      sharedSceneEditsRef.current = next;
      setSceneEdits(next);
    }
  };


  return (
    <PanelShell
      inline={inline}
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "min(1320px, calc(100vw - 40px))",
        },
      }}
    >
      <PanelShellTitle inline={inline}>
        Physical layout{showUnsavedMarker ? " *" : ""}
        {orientationLabel && orientationLabel.count > 1 ? (
          <Typography component="span" variant="body2" sx={{ ml: 1.25, color: "text.secondary", fontWeight: 600 }}>
            {`${orientationLabel.index} / ${orientationLabel.count}${orientationLabel.name ? ` · ${orientationLabel.name}` : ""}`}
          </Typography>
        ) : null}
      </PanelShellTitle>
      <PanelShellContent inline={inline} dividers>
        <Box inert={saving ? true : undefined} aria-busy={saving}>
        {showUnsavedMarker ? (
          <Alert
            severity="info"
            sx={{
              mb: 2,
              alignItems: "center",
              "& .MuiAlert-action": {
                alignItems: "center",
                pt: 0,
                mr: 0,
              },
            }}
            action={onlyElsewhere ? null :
              <Button
                size="small"
                variant="contained"
                onClick={() => { setDraftShading(null); if (onDiscardSceneShading) onDiscardSceneShading(); if (onDiscard) onDiscard(); }}
                sx={{
                  bgcolor: "error.main",
                  color: "#fff",
                  fontWeight: 600,
                  "&:hover": {
                    bgcolor: "error.dark",
                  },
                }}
              >
                Discard
              </Button>
            }
          >
            {onlyElsewhere
              ? (unsavedElsewhere.length === 1
                ? tr(`Η πλευρά ${unsavedElsewhere[0]} έχει μη αποθηκευμένες αλλαγές. Η αποθήκευση εδώ τις περιλαμβάνει· κλικ στην πλευρά για να τις ελέγξετε.`,
                  `Face ${unsavedElsewhere[0]} has unsaved changes. Saving here includes them; click the face to review them.`)
                : tr(`Οι πλευρές ${unsavedElsewhere.join(", ")} έχουν μη αποθηκευμένες αλλαγές. Η αποθήκευση εδώ τις περιλαμβάνει· κλικ σε μια πλευρά για να τις ελέγξετε.`,
                  `Faces ${unsavedElsewhere.join(", ")} have unsaved changes. Saving here includes them; click a face to review them.`))
              : onRoof
                ? tr("Η διάταξη έχει μη αποθηκευμένες αλλαγές. Ελέγξτε την τοποθέτηση και αποθηκεύστε τη διάταξη.",
                  "The layout has unsaved changes. Review the placement and save the layout.")
                : "Auto-design results loaded. Review and click Save design to keep them."}
          </Alert>
        ) : null}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {isFlushRoof ? tr("Χειροκίνητη τοποθέτηση στη στέγη", "Manual roof placement") : "Spacing overview"}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 720 }}>
              {isFlushRoof ? tr("Ορίστε τη στέγη και τοποθετήστε τα πάνελ στο 3D. Η αρχική κατανομή είναι σημείο εκκίνησης· ελέγξτε ότι τα πάνελ χωρούν και δεν επικαλύπτουν εμπόδια.", "Define the roof and position modules in 3D. The initial grid is a starting point; check that modules fit and avoid obstacles.") : tr("Οι τιμές μπορούν να προκύψουν από τον αυτόματο σχεδιασμό και να προσαρμοστούν εδώ χειροκίνητα. Οι αλλαγές ενημερώνουν την προεπισκόπηση διάταξης και τον λόγο GCR.", "These spacing values can be populated by auto-design and adjusted manually here. Changes update the physical layout preview and derived GCR dynamically.")}
            </Typography>
            {!isFlushRoof && <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 760, mt: 0.75 }}>
              Automatic spacing results are computed by the tool and should be treated as
              design outputs unless the user configures manually the spacing inputs.
            </Typography>}
          </Box>
          <Stack spacing={1} alignItems="flex-end">
            {/* The two outputs of this tab, at the top where the eye starts.
                Both need no simulation: the sun path is a question about the
                site, the sheet a description of the field decided here. */}
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setSunPathOpen(true)}
                disabled={!hasCoordinates}
              >
                {tr("Τροχιά ήλιου", "Sun path")}
              </Button>
              {!reportRef ? (
                <Button
                  size="small"
                  variant="contained"
                  onClick={downloadLayoutPdf}
                  disabled={pdfBusy || !reportReady}
                >
                  {pdfBusy ? tr("Δημιουργία…", "Building…") : tr("Αναφορά σχεδιασμού (.pdf)", "Design report (.pdf)")}
                </Button>
              ) : null}
            </Stack>
            {!reportRef && (pdfError || (!pdfBusy && reportBlockedBecause)) ? (
              <Typography variant="caption"
                          sx={{ color: pdfError ? "error.main" : "text.secondary" }}>
                {pdfError || reportBlockedBecause}
              </Typography>
            ) : null}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="flex-end">
              {moduleName ? <Chip label={`Module: ${displayName(moduleName)}`} size="small" /> : null}
              {inverterName ? <Chip label={`Inverter: ${displayName(inverterName)}`} size="small" /> : null}
            </Stack>
          </Stack>
        </Box>

        {(onRoof || onFitStringsToPlan) && totalModules != null && Number(totalModules) >= 0 && (Number(totalModules) > 0 || physicalCount > 0) ? (
          <Alert severity={layoutAgrees ? "success" : electricalLayoutChanged || physicalCount !== Number(totalModules) ? "warning" : "info"} sx={{ mb: 2 }}
            action={onRoof && !layoutAgrees ? <Tooltip arrow describeChild title={tr(
              "Αναδημιουργεί τις σειρές και την αντιστοίχιση strings. Αν αλλάξουν οι σειρές, επαναφέρει τις μεμονωμένες μετακινήσεις πάνελ. Διατηρεί στέγη, εμπόδια, θέση και άξονα ομάδας.",
              "Rebuilds rows and string mapping. Changed rows reset individual module moves. Roof, obstacles, group position and axis are retained.")}><Button color="inherit" size="small" onClick={updateElectricalLayout}>
              {tr("Ενημέρωση από ηλεκτρολογικό σχέδιο", "Update from electrical design")}
            </Button></Tooltip> : null}>
            {tr(layoutAgrees
              ? `Η φυσική διάταξη συμφωνεί με το ηλεκτρολογικό σχέδιο: ${Number(totalModules)} πλαίσια σε αυτή την πλευρά.`
              : `Φυσική διάταξη: ${physicalCount ?? 0} πλαίσια · Ηλεκτρολογικό σχέδιο: ${Number(totalModules)} πλαίσια σε αυτή την πλευρά.`,
              `Physical layout: ${physicalCount ?? 0} modules · Electrical design: ${Number(totalModules)} modules on this surface.`)}
            {!onRoof && physicalCount > Number(totalModules) && <Typography variant="caption" display="block">{tr(
              `Η κάτοψη έχει ${physicalCount - Number(totalModules)} επιπλέον θέσεις πάνελ. Η προσαρμογή χρησιμοποιεί τους ίδιους αντιστροφείς και τα ήδη συνδεδεμένα MPPT· δεν προσθέτει αυτόματα εξοπλισμό.`,
              `The plan has ${physicalCount - Number(totalModules)} additional module positions. Fitting uses the same inverters and already connected MPPTs; it does not add equipment automatically.`)}</Typography>}
            {electricalLayoutChanged && <Typography variant="caption" display="block">
              {form.manual_layout_edited ? tr("Η απόκλιση αποθηκεύεται, αλλά η εκτίμηση μπλοκάρεται μέχρι να συμφωνήσουν κάτοψη και strings.",
                "You can save the edited plan, but estimation is blocked until the plan and strings agree.") : tr("Η ενημέρωση προσαρμόζει τις σειρές και την αντιστοίχιση strings. Διατηρεί στέγη, εμπόδια, θέση και άξονα της ομάδας. Αν αλλάξουν οι σειρές, επαναφέρει τις μεμονωμένες μετακινήσεις πάνελ.",
                "Updating adjusts the rows and string mapping. Roof, obstacles, group position and axis are retained. Changed rows reset individual module moves.")}
            </Typography>}
            {/* The other way round: the plan is right and the design follows it.
                This is what a module taken out around a chimney wants - the
                strings shorten to what is actually on the roof - and it is an
                electrical change, so it is shown first and applied through the
                electrical save, with the inverter's voltage window checked. */}
            {onFitStringsToPlan && physicalCount > 0 && Number(totalModules) > 0 &&
              (physicalCount !== Number(totalModules) || (manualWiring && !manualWiring.check.consistent) || (!onRoof && stringLayout?.check?.consistent === false)) && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                <Button size="small" variant="outlined" color="inherit" disabled={Boolean(stringFit?.busy || groundGeometryInvalid)}
                  onClick={requestStringFit}
                  startIcon={stringFit?.busy ? <CircularProgress size={14} color="inherit" /> : null}>
                  {tr("Ενημέρωση ηλεκτρολογικού σχεδιασμού από την κάτοψη", "Update electrical design from plan")}
                </Button>
                <Typography variant="caption">{tr(
                  `Προτείνει μήκη και πλήθος strings για ${physicalCount} πάνελ, με τον ίδιο εξοπλισμό. Εφαρμόζεται μετά από ηλεκτρικό έλεγχο και επιβεβαίωση.`,
                  `Proposes string lengths and counts for ${physicalCount} modules, keeping the equipment. Apply after electrical validation and review.`)}</Typography>
                {stringFit?.error && <Typography variant="caption" color="error.main">{stringFit.error}</Typography>}
              </Stack>
            )}
          </Alert>
        ) : null}

        <Dialog open={Boolean(stringFit?.result)} onClose={() => { if (!stringFit?.busy) setStringFit(null); }} maxWidth="sm" fullWidth>
          <DialogTitle>{tr("Προσαρμογή strings στην κάτοψη", "Fit strings to the plan")}</DialogTitle>
          <DialogContent>
            {stringFit?.result && (() => {
              const r = stringFit.result;
              // Validation issues carry both languages; a string is shown as is.
              const issueText = (x) => (typeof x?.message === "object" ? (isGreek ? x.message.el : x.message.en) : (x?.message || x?.detail || String(x)));
              const errors = r.validation?.errors || [];
              const warnings = r.validation?.warnings || [];
              const unchanged = !r.changes?.length;
              return <Stack spacing={1.25}>
                <Typography variant="body2">{tr(
                  `Η κάτοψη έχει ${r.target} πάνελ. Τα strings προσαρμόζονται σε ${r.modules}${r.shortfall ? ` — ${r.shortfall} θέση(-εις) της κάτοψης μένουν χωρίς string, γιατί τα παράλληλα strings κάθε MPPT πρέπει να έχουν ίσο μήκος` : ""}.`,
                  `The plan holds ${r.target} modules. The strings are fitted to ${r.modules}${r.shortfall ? ` — ${r.shortfall} position(s) of the plan stay without a string, because the parallel strings of an MPPT must be equal in length` : ""}.`)}</Typography>
                {unchanged ? <Alert severity="info">{tr("Τα μήκη strings παραμένουν ίδια. Θα ενημερωθεί η αντιστοίχιση των θέσεων.", "String lengths stay the same. The slot assignments will be updated.")}</Alert> : (
                  <Box component="table" sx={{ borderCollapse: "collapse", "& td, & th": { py: 0.4, pr: 2, textAlign: "left", fontSize: 13 } }}>
                    <thead><tr><th>{tr("Αντιστροφέας", "Inverter")}</th><th>MPPT</th><th>{tr("Strings", "Strings")}</th><th>{tr("Πάνελ ανά string", "Modules per string")}</th></tr></thead>
                    <tbody>{r.changes.map((c) => <tr key={`${c.inverter_key}-${c.mppt}`}>
                      <td>{displayName(String(c.inverter_key).split("__")[0])} #{c.inverter}</td><td>{c.mppt}</td><td>{c.inputs_before != null && c.inputs_before !== c.inputs ? `${c.inputs_before} → ${c.inputs}` : c.inputs}</td>
                      <td>{c.before} → <b>{c.after}</b></td></tr>)}</tbody>
                  </Box>)}
                {errors.map((e, i) => <Alert key={`e${i}`} severity="error">{issueText(e)}</Alert>)}
                {warnings.map((w, i) => <Alert key={`w${i}`} severity="warning">{issueText(w)}</Alert>)}
                {r.validation?.valid === true && !errors.length && <Alert severity={warnings.length ? "info" : "success"}>{warnings.length
                  ? tr("Οι διαθέσιμοι ηλεκτρικοί έλεγχοι πέρασαν. Ελέγξτε και τα όρια που αναφέρονται στις προειδοποιήσεις.",
                    "The available electrical checks passed. Review the limits mentioned in the warnings as well.")
                  : tr("Τα νέα μήκη strings είναι εντός των ορίων τάσης του αντιστροφέα.",
                    "The new string lengths are within the inverter's voltage window.")}</Alert>}
                {!r.validation && <Typography variant="caption" color="text.secondary">{tr(
                  "Δεν έγινε έλεγχος ορίων τάσης: δεν έχει επιλεγεί πλαίσιο και αντιστροφέας.", "Voltage limits were not checked: no module and inverter are selected.")}</Typography>}
                {r.shortfall > 0 && <Typography variant="caption" color="error.main">{tr(
                  `Η πρόταση αφήνει ${r.shortfall} πάνελ χωρίς string και δεν μπορεί να εφαρμοστεί. Αλλάξτε τον ηλεκτρολογικό σχεδιασμό.`,
                  `The proposal leaves ${r.shortfall} modules unassigned and cannot be applied. Adjust the electrical design.`)}</Typography>}
              </Stack>;
            })()}
          </DialogContent>
          <DialogActions>
            <Button disabled={stringFit?.busy} onClick={() => setStringFit(null)}>{tr("Άκυρο", "Cancel")}</Button>
            <Button variant="contained" disabled={stringFit?.busy || !stringFit?.result || stringFit.key !== stringFitKey ||
              Boolean(groundGeometryInvalid) ||
              stringFit.result.validation?.valid !== true || Boolean(stringFit.result.validation?.errors?.length) ||
              Number(stringFit.result.shortfall) > 0 || Number(stringFit.result.modules) !== physicalCount}
              startIcon={stringFit?.busy ? <CircularProgress size={14} color="inherit" /> : null}
              onClick={async () => {
                const current = stringFit;
                setStringFit({ ...current, busy: true, error: "" });
                try { await onFitStringsToPlan?.(current.result, spacingInfoForExport()); setStringFit(null); }
                catch (e) { setStringFit({ ...current, busy: false, error: e?.message || tr("Η αποθήκευση απέτυχε.", "Saving failed.") }); }
              }}>
              {tr("Εφαρμογή στον ηλεκτρολογικό σχεδιασμό", "Apply to the electrical design")}
            </Button>
          </DialogActions>
          {stringFit?.error && <Alert severity="error" sx={{ mx: 2, mb: 1 }}>{stringFit.error}</Alert>}
          {stringFit?.result && stringFit.key !== stringFitKey && <Alert severity="warning" sx={{ mx: 2, mb: 1 }}>{tr(
            "Η κάτοψη άλλαξε. Ζητήστε νέα πρόταση.", "The plan changed. Request a new proposal.")}</Alert>}
        </Dialog>

        {validationError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {validationError}
          </Alert>
        ) : null}

        {spacingAdvisory.notes.length > 0 ? (
          <Alert severity={spacingAdvisory.severity || "info"} sx={{ mb: 2 }}>
            {spacingAdvisory.notes.map((note, index) => (
              <div key={index} style={{ marginTop: index ? 8 : 0 }}>
                {note}
              </div>
            ))}
          </Alert>
        ) : null}

        {/* One column, top to bottom: what you set, what it looks like, what it
            means. The earlier two-column arrangement parked the inputs in a
            narrow left rail that ran out after five fields while the right
            column went on for two screens -- a gap the eye kept falling into.
            The inputs now form a strip across the top and the 3D view gets the
            whole width, which is where the height it needs comes from. */}
        <Stack spacing={2.5}>
          {/* What you set beside what it means: the inputs on the left and the
              cross-section they produce on the right, close enough to read as
              one thing. The parcel controls sit under the inputs they depend on. */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) minmax(0, 1fr)" },
              gap: 2.5,
              alignItems: "start",
            }}
          >
          <Stack spacing={2.5}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {onRoof ? tr("Τοποθέτηση πλαισίων", "Module placement") : tr("Στοιχεία διάταξης", "Layout inputs")}
              </Typography>
              <Tooltip arrow describeChild title={
                installationType === "roof_flush"
                ? tr("Ορίστε την αρχική κατανομή και το διάκενο των σειρών. Στην «Επεξεργασία θέσεων» μετακινήστε τη διάταξη ή μεμονωμένα πλαίσια γύρω από τα εμπόδια. Η απόσταση από τις ακμές ορίζεται στα στοιχεία της στέγης.", "Set the initial grid and the gap between lines. In Edit positions, move the array or individual modules around obstacles. Set the edge setback in the roof geometry section.")
                : tr("Η εγκυρότητα της γεωμετρίας καθορίζεται από το βήμα και το ύψος της σειράς.", "Geometry validity is driven by pitch and row height.")
              }>
                <IconButton size="small" aria-label={tr("Πληροφορίες τοποθέτησης", "Placement information")} sx={{ p: 0.25 }}><InfoOutlinedIcon sx={{ fontSize: 16 }} /></IconButton>
              </Tooltip>
            </Stack>

            {/* A flush roof is laid out in 3D. The fields here only seed the first
                grid, so they are labelled as that; what the roof actually holds is
                read back from the scene, and the area is no longer a free number
                that could disagree with the roof drawn below. */}
            {isFlushRoof ? (<>
            <Typography variant="caption" sx={{ display: "block", fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", color: "text.secondary", mt: 0, mb: 0.4 }}>
              {tr("Αρχική διάταξη", "Starting grid")}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
              {tr("Στήνει το πρώτο πλέγμα πλαισίων στο 3D. Μετά μετακινείτε πλαίσια μεμονωμένα γύρω από τα εμπόδια.", "Builds the first grid of modules in 3D. After that, move modules individually around obstacles.")}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" },
                columnGap: 1,
                rowGap: 0.6,
                alignItems: "start",
              }}
            >
              <CompactLayoutField
                fullWidth
                label={isFlushRoof ? tr("Σειρές", "Rows") : onRoof ? tr("Σειρές στη στέγη", "Rows on the roof") : tr("Σειρές στο γήπεδο", "Rows in the field")}
                disabled={isFlushRoof && hasIndividualPositions}
                type="number"
                value={form.field_rows}
                onChange={(e) => handleField("field_rows", e.target.value)}
                inputProps={{ min: 1, step: 1 }}
                helperText={
                  isFlushRoof ? tr("Τα πλαίσια μοιράζονται σε αυτές τις σειρές. Στη συνέχεια μπορείτε να τα μετακινήσετε μεμονωμένα στο 3D.", "Modules are divided among these lines. You can then move them individually in 3D.") : onRoof ? tr("Ορίστε το πραγματικό πλήθος σειρών που χωρά στην επιφάνεια της στέγης.", "Enter the actual number of rows that fit on the roof surface.") : groundRowsHelp
                }
              />
              <CompactLayoutField
                fullWidth
                label={isFlushRoof ? tr("Πλαίσια καθ’ ύψος", "Modules up the slope") : tr("Πλαίσια καθ’ ύψος ανά σειρά", "Row vertical modules")}
                disabled={isFlushRoof && hasIndividualPositions}
                type="number"
                value={form.row_vertical_modules}
                onChange={(e) => handleField("row_vertical_modules", e.target.value)}
                inputProps={{ min: 1, step: 1 }}
                helperText={isFlushRoof ? tr("Πόσα πλαίσια τοποθετούνται διαδοχικά κατά την κλίση σε κάθε σειρά.", "Number of modules placed successively along the slope in each initial grid line.") : tr("Θετικός ακέραιος αριθμός πλαισίων τοποθετημένων καθ’ ύψος στη σειρά.", "Positive integer number of stacked modules in the row.")}
              />
              <Box id="field-pv-layout-pitch"><CompactLayoutField
                fullWidth
                label={isFlushRoof ? tr("Διάκενο σειρών", "Gap between rows") : tr("Βήμα (m)", "Pitch (m)")}
                unit={isFlushRoof ? "cm" : undefined}
                type="number"
                value={isFlushRoof
                  ? (Number(form.field_rows) === 1 || roofGapCm == null ? "" : Math.round(roofGapCm * 10) / 10)
                  : (!onRoof ? commonPitchDraft ?? form.minimum_distance_m : form.minimum_distance_m)}
                placeholder={isFlushRoof && Number(form.field_rows) === 1 ? "—" : undefined}
                disabled={Number(form.field_rows) === 1 || (isFlushRoof && (hasIndividualPositions || !(roofModuleLength > 0) || !(roofTiers > 0)))}
                onChange={(e) => {
                  if (!onRoof && groundRows) { setCommonPitchDraft(e.target.value); return; }
                  const gap = toFiniteNumber(e.target.value);
                  handleField("minimum_distance_m", isFlushRoof
                    ? (gap == null ? "" : String((roofModuleLength * roofTiers + gap / 100) * roofCos))
                    : e.target.value);
                }}
                inputProps={{ min: 0, step: isFlushRoof ? "0.1" : "0.01" }}
                helperText={Number(form.field_rows) === 1 ? tr("Δεν εφαρμόζεται σε μία σειρά.", "Not applicable to a single row.") : isFlushRoof
                  ? tr("Καθαρό κενό πάνω στη στέγη, από την ακμή μιας σειράς έως την επόμενη. 0 = χωρίς κενό.", "Clear distance along the roof from the last edge of one line to the next. 0 = touching edges.")
                  : tr("Απόσταση μεταξύ των μπροστινών ακμών δύο διαδοχικών σειρών Φ/Β.", "Spacing between two consecutive PV rows, measured from the front edge of one row to the front edge of the next.")}
              />{commonPitchAction}</Box>
            </Box>
            {hasIndividualPositions && <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
              <Typography variant="caption" color="text.secondary" sx={{ flex: "1 1 260px" }}>
                {tr("Τα πλαίσια έχουν μετακινηθεί στο 3D, οπότε τα πεδία της αρχικής διάταξης είναι κλειδωμένα. Μια νέα αρχική διάταξη σβήνει τις μεμονωμένες θέσεις.",
                  "Modules have been moved in 3D, so the starting-grid fields are locked. A new starting grid discards the individual positions.")}
              </Typography>
              <Button size="small" variant="outlined" onClick={() => {
                if (window.confirm(tr("Να σβηστούν οι μεμονωμένες θέσεις των πάνελ και να ξαναστηθεί το πλέγμα;", "Discard the individual module positions and rebuild the grid?"))) setForm((prev) => ({ ...prev, module_offsets: {}, module_array: null }));
              }}>
                {tr("Νέα αρχική διάταξη", "New starting grid")}
              </Button>
            </Stack>}
            <Typography variant="caption" sx={{ display: "block", fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", color: "text.secondary", mt: 1.75, mb: 0.4 }}>
              {tr("Από το 3D", "From the 3D scene")}
            </Typography>
            {roofUsableAreaM2 == null && <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" },
                columnGap: 1,
                rowGap: 0.6,
                alignItems: "start",
              }}
            >
              <CompactLayoutField
                fullWidth
                label={onRoof ? tr("Επιφάνεια στέγης", "Roof area") : tr("Έκταση Φ/Β πεδίου (m²)", "PV footprint (m²)")}
                unit={onRoof ? "m²" : undefined}
                placeholder={onRoof ? tr("Σχεδιάστε τη στέγη στο 3D", "Draw the roof in 3D") : undefined}
                type="number"
                value={form.terrace_area_m2}
                onChange={(e) => handleField("terrace_area_m2", e.target.value)}
                inputProps={{ min: 0, step: "0.01" }}
                helperText={
                  onRoof ? tr("Επιφάνεια της στέγης που διατίθεται για τα Φ/Β, μαζί με τα κενά μεταξύ σειρών.", "Roof area reserved for PV, including the gaps between rows.") : groundFootprintHelp
                }
              />
            </Box>
            </>}
            {roofUsableAreaM2 != null && <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1,
              "& .stat": { p: 1, borderRadius: 1.5, bgcolor: "action.hover" } }}>
              <Box className="stat">
                <Typography variant="caption" color="text.secondary" display="block">{tr("Χρήσιμη επιφάνεια στέγης", "Usable roof area")}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatMetric(roofUsableAreaM2, 1, " m²")}</Typography>
              </Box>
              <Box className="stat">
                <Typography variant="caption" color="text.secondary" display="block">{tr("Πάνελ", "Modules")}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{Number(totalModules) > 0 ? Math.round(Number(totalModules)) : "—"}</Typography>
              </Box>
              <Box className="stat">
                <Typography variant="caption" color="text.secondary" display="block">{tr("Κάλυψη από πάνελ", "Covered by modules")}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {roofCoveredAreaM2 != null ? formatMetric(roofCoveredAreaM2, 1, " m²") : "—"}
                  {roofCoveredAreaM2 != null && roofUsableAreaM2 > 0 ? ` · ${Math.round((100 * roofCoveredAreaM2) / roofUsableAreaM2)}%` : ""}
                </Typography>
              </Box>
            </Box>}
            {roofUsableAreaM2 != null && <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              {tr("Σε κάτοψη, από τη στέγη στο 3D μείον την απόσταση από τις ακμές. Αυτή η επιφάνεια αποθηκεύεται ως επιφάνεια του Φ/Β.",
                "In plan, from the roof in 3D less the edge setback. This is the area saved as the PV area.")}
            </Typography>}
            {allFacesArea && <Typography variant="body2" sx={{ mt: 0.5 }}>
              <b>{tr(`Όλες οι πλευρές (${allFacesArea.faces})`, `All faces (${allFacesArea.faces})`)}:</b>{" "}
              {formatMetric(allFacesArea.usable, 1, " m²")} {tr("χρήσιμη", "usable")} · {formatMetric(allFacesArea.covered, 1, " m²")} {tr("κάλυψη", "covered")}
            </Typography>}
            <Typography variant="caption" sx={{ display: "block", fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", color: "text.secondary", mt: 1.75, mb: 0.4 }}>
              {tr("Τοποθέτηση", "Mounting")}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" },
                columnGap: 1,
                rowGap: 0.6,
                alignItems: "start",
              }}
            >
              <CompactLayoutField
                fullWidth
                label={onRoof ? tr("Ύψος από τη στέγη", "Clearance above roof") : tr("Ύψος από το έδαφος (m)", "Ground clearance (m)")}
                unit={onRoof ? "m" : undefined}
                type="number"
                value={form.ground_clearance_m}
                onChange={(e) => handleField("ground_clearance_m", e.target.value)}
                inputProps={{ min: 0, max: 10, step: "0.05" }}
                helperText={onRoof
                  ? tr("Ύψος της χαμηλής ακμής των πλαισίων από την επιφάνεια της στέγης. Δεν είναι το ύψος του κτιρίου από τον δρόμο.", "Height of the modules' lower edge above the roof surface. This is not the building's height above the street.")
                  : tr("Ύψος της χαμηλής ακμής των πλαισίων από το έδαφος. Επηρεάζει το φως που φτάνει στην πίσω όψη των πλαισίων διπλής όψης και τη θέση της σκιάς στη σκηνή 3D.", "Height of the row's low edge above the ground. Sets how much ground-reflected light a bifacial rear side sees, and where each row's shadow falls in the 3D view. It does not change row-to-row shading, which depends on pitch and tilt alone.")}
              />
              {siteGeometry?.roof && (
                <CompactLayoutField
                fullWidth
                label={tr("Απόσταση από ακμές", "Edge setback")}
                unit="m"
                type="number"
                value={siteGeometry?.roof?.setbackM ?? ""}
                onChange={(e) => handleField("site_geometry", { ...siteGeometry,
                  roof: { ...siteGeometry.roof, setbackM: e.target.value === "" ? "" : Number(e.target.value) } })}
                inputProps={{ min: 0, step: "0.1" }}
                helperText={tr("Ελάχιστη απόσταση των πάνελ από το περίγραμμα της στέγης, ίδια σε όλες τις ακμές. Μειώνει τη χρήσιμη επιφάνεια και ελέγχεται στη διάταξη.",
                  "Minimum distance of the modules from the roof outline, the same on every edge. It reduces the usable area and is checked against the layout.")}
              />
              )}
            </Box>
            </>) : (<>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                columnGap: 1,
                rowGap: 0.6,
                alignItems: "start",
              }}
            >
              {!(onRoof && roofUsableAreaM2 != null) && (<>
              <Box sx={{ minWidth: 0 }}>
              <CompactLayoutField
                fullWidth
                label={onRoof ? tr("Επιφάνεια στέγης", "Roof area") : tr("Έκταση τρέχουσας διάταξης (m²)", "Current layout area (m²)")}
                unit={onRoof ? "m²" : undefined}
                type="number"
                value={!onRoof && form.terrace_area_mode === "auto" ? previewSpacingInfo.terrace_area_m2 ?? "" : form.terrace_area_m2}
                onChange={(e) => handleField("terrace_area_m2", e.target.value)}
                InputProps={!onRoof && form.terrace_area_mode === "auto" ? { readOnly: true } : undefined}
                inputProps={{ min: 0, step: "0.01" }}
                helperText={
                  onRoof ? tr("Επιφάνεια της στέγης που διατίθεται για τα Φ/Β, μαζί με τα κενά μεταξύ σειρών.", "Roof area reserved for PV, including the gaps between rows.") : groundFootprintHelp
                }
              />
              {!onRoof && <FormControlLabel
                control={<Checkbox size="small" checked={form.terrace_area_mode === "auto"}
                  onChange={(e) => setForm((prev) => ({ ...prev,
                    terrace_area_mode: e.target.checked ? "auto" : "manual",
                    terrace_area_m2: e.target.checked ? prev.terrace_area_m2 : toFieldValue(previewSpacingInfo.terrace_area_m2),
                  }))} />}
                label={tr("Αυτόματη έκταση από τη διάταξη", "Calculate area from layout")}
                sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: 12 } }}
              />}
              </Box>
              </>)}
              <Box id="field-pv-layout-pitch"><CompactLayoutField
                fullWidth
                label={isFlushRoof ? tr("Διάκενο σειρών", "Gap between rows") : tr("Βήμα (m)", "Pitch (m)")}
                unit={isFlushRoof ? "cm" : undefined}
                type="number"
                value={isFlushRoof
                  ? (Number(form.field_rows) === 1 || roofGapCm == null ? "" : Math.round(roofGapCm * 10) / 10)
                  : (!onRoof ? commonPitchDraft ?? form.minimum_distance_m : form.minimum_distance_m)}
                placeholder={isFlushRoof && Number(form.field_rows) === 1 ? "—" : undefined}
                disabled={Number(form.field_rows) === 1 || (isFlushRoof && (hasIndividualPositions || !(roofModuleLength > 0) || !(roofTiers > 0)))}
                onChange={(e) => {
                  if (!onRoof && groundRows) { setCommonPitchDraft(e.target.value); return; }
                  const gap = toFiniteNumber(e.target.value);
                  handleField("minimum_distance_m", isFlushRoof
                    ? (gap == null ? "" : String((roofModuleLength * roofTiers + gap / 100) * roofCos))
                    : e.target.value);
                }}
                inputProps={{ min: 0, step: isFlushRoof ? "0.1" : "0.01" }}
                helperText={Number(form.field_rows) === 1 ? tr("Δεν εφαρμόζεται σε μία σειρά.", "Not applicable to a single row.") : isFlushRoof
                  ? tr("Καθαρό κενό πάνω στη στέγη, από την ακμή μιας σειράς έως την επόμενη. 0 = χωρίς κενό.", "Clear distance along the roof from the last edge of one line to the next. 0 = touching edges.")
                  : tr("Απόσταση μεταξύ των μπροστινών ακμών δύο διαδοχικών σειρών Φ/Β.", "Spacing between two consecutive PV rows, measured from the front edge of one row to the front edge of the next.")}
              />{commonPitchAction}</Box>
              <CompactLayoutField
                fullWidth
                label={isFlushRoof ? tr("Πλαίσια καθ’ ύψος", "Modules up the slope") : tr("Πλαίσια καθ’ ύψος ανά σειρά", "Row vertical modules")}
                disabled={isFlushRoof && hasIndividualPositions}
                type="number"
                value={form.row_vertical_modules}
                onChange={(e) => handleField("row_vertical_modules", e.target.value)}
                inputProps={{ min: 1, step: 1 }}
                helperText={isFlushRoof ? tr("Πόσα πλαίσια τοποθετούνται διαδοχικά κατά την κλίση σε κάθε σειρά.", "Number of modules placed successively along the slope in each initial grid line.") : tr("Θετικός ακέραιος αριθμός πλαισίων τοποθετημένων καθ’ ύψος στη σειρά.", "Positive integer number of stacked modules in the row.")}
              />
              <CompactLayoutField
                fullWidth
                label={isFlushRoof ? tr("Σειρές", "Rows") : onRoof ? tr("Σειρές στη στέγη", "Rows on the roof") : tr("Σειρές στο γήπεδο", "Rows in the field")}
                disabled={isFlushRoof && hasIndividualPositions}
                type="number"
                value={form.field_rows}
                onChange={(e) => handleField("field_rows", e.target.value)}
                inputProps={{ min: 1, step: 1 }}
                helperText={
                  isFlushRoof ? tr("Τα πλαίσια μοιράζονται σε αυτές τις σειρές. Στη συνέχεια μπορείτε να τα μετακινήσετε μεμονωμένα στο 3D.", "Modules are divided among these lines. You can then move them individually in 3D.") : onRoof ? tr("Ορίστε το πραγματικό πλήθος σειρών που χωρά στην επιφάνεια της στέγης.", "Enter the actual number of rows that fit on the roof surface.") : groundRowsHelp
                }
              />
              <CompactLayoutField
                fullWidth
                label={onRoof ? tr("Ύψος από τη στέγη", "Clearance above roof") : tr("Ύψος από το έδαφος (m)", "Ground clearance (m)")}
                unit={onRoof ? "m" : undefined}
                type="number"
                value={form.ground_clearance_m}
                onChange={(e) => handleField("ground_clearance_m", e.target.value)}
                inputProps={{ min: 0, max: 10, step: "0.05" }}
                helperText={onRoof
                  ? tr("Ύψος της χαμηλής ακμής των πλαισίων από την επιφάνεια της στέγης. Δεν είναι το ύψος του κτιρίου από τον δρόμο.", "Height of the modules' lower edge above the roof surface. This is not the building's height above the street.")
                  : tr("Ύψος της χαμηλής ακμής των πλαισίων από το έδαφος. Επηρεάζει το φως που φτάνει στην πίσω όψη των πλαισίων διπλής όψης και τη θέση της σκιάς στη σκηνή 3D.", "Height of the row's low edge above the ground. Sets how much ground-reflected light a bifacial rear side sees, and where each row's shadow falls in the 3D view. It does not change row-to-row shading, which depends on pitch and tilt alone.")}
              />
              {onRoof && siteGeometry?.roof && (
              <CompactLayoutField
                fullWidth
                label={tr("Απόσταση από ακμές", "Edge setback")}
                unit="m"
                type="number"
                value={siteGeometry?.roof?.setbackM ?? ""}
                onChange={(e) => handleField("site_geometry", { ...siteGeometry,
                  roof: { ...siteGeometry.roof, setbackM: e.target.value === "" ? "" : Number(e.target.value) } })}
                inputProps={{ min: 0, step: "0.1" }}
                helperText={tr("Ελάχιστη απόσταση των πάνελ από το περίγραμμα της στέγης, ίδια σε όλες τις ακμές. Μειώνει τη χρήσιμη επιφάνεια και ελέγχεται στη διάταξη.",
                  "Minimum distance of the modules from the roof outline, the same on every edge. It reduces the usable area and is checked against the layout.")}
              />
              )}
              {/* The array's position is set by dragging it in the scene (arrow
                  keys for 10 cm steps) and read off the scene's corner; two
                  "offset" boxes here were a second place for the same thing. */}
              {!isFlushRoof && <CompactLayoutField
                fullWidth
                disabled={Number(form.field_rows) === 1}
                label={tr("GCR (μόνο για ανάγνωση)", "GCR (read-only)")}
                value={previewSpacingInfo.gcr != null ? previewSpacingInfo.gcr.toFixed(3) : ""}
                InputProps={{ readOnly: true }}
                helperText={isGreek ? "Γεωμετρικός λόγος L / βήμα, όπου L το συνολικό μήκος της σειράς κατά την κλίση. Υπολογίζεται αυτόματα από τις διαστάσεις και τα πλαίσια καθ’ ύψος." : `Read-only geometry check derived from L / pitch, where L = ${formatMetric(
                  modulePortrait === false ? moduleWidth : moduleLength,
                  2,
                  " m"
                )} × row vertical modules — the module ${
                  modulePortrait === false ? "width, lying down" : "length, standing up"
                }.`}
              />}
            </Box>
            {onRoof && roofUsableAreaM2 != null && <Box sx={{ mt: 1.25 }}>
            {roofUsableAreaM2 != null && <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1,
              "& .stat": { p: 1, borderRadius: 1.5, bgcolor: "action.hover" } }}>
              <Box className="stat">
                <Typography variant="caption" color="text.secondary" display="block">{tr("Χρήσιμη επιφάνεια στέγης", "Usable roof area")}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatMetric(roofUsableAreaM2, 1, " m²")}</Typography>
              </Box>
              <Box className="stat">
                <Typography variant="caption" color="text.secondary" display="block">{tr("Πάνελ", "Modules")}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{Number(totalModules) > 0 ? Math.round(Number(totalModules)) : "—"}</Typography>
              </Box>
              <Box className="stat">
                <Typography variant="caption" color="text.secondary" display="block">{tr("Κάλυψη από πάνελ", "Covered by modules")}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {roofCoveredAreaM2 != null ? formatMetric(roofCoveredAreaM2, 1, " m²") : "—"}
                  {roofCoveredAreaM2 != null && roofUsableAreaM2 > 0 ? ` · ${Math.round((100 * roofCoveredAreaM2) / roofUsableAreaM2)}%` : ""}
                </Typography>
              </Box>
            </Box>}
            {roofUsableAreaM2 != null && <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              {tr("Σε κάτοψη, από τη στέγη στο 3D μείον την απόσταση από τις ακμές. Αυτή η επιφάνεια αποθηκεύεται ως επιφάνεια του Φ/Β.",
                "In plan, from the roof in 3D less the edge setback. This is the area saved as the PV area.")}
            </Typography>}
            {allFacesArea && <Typography variant="body2" sx={{ mt: 0.5 }}>
              <b>{tr(`Όλες οι πλευρές (${allFacesArea.faces})`, `All faces (${allFacesArea.faces})`)}:</b>{" "}
              {formatMetric(allFacesArea.usable, 1, " m²")} {tr("χρήσιμη", "usable")} · {formatMetric(allFacesArea.covered, 1, " m²")} {tr("κάλυψη", "covered")}
            </Typography>}
            </Box>}
            </>)}
          </Paper>
          </Stack>

          <PhysicalLayoutSchema
            singleRow={Number(form.field_rows) === 1}
            onRoof={onRoof}
            flush={installationType === "roof_flush"}
            roofGapCm={roofGapCm}
            spacingInfo={previewSpacingInfo}
            moduleLength={moduleLength}
            totalModules={physicalCount ?? totalModules}
            groundClearance={toFiniteNumber(form.ground_clearance_m)}
          />
          </Box>

          {parcelRing ? (
            <Paper
              elevation={0}
              sx={{
                p: 2, borderRadius: 3, border: "1px solid", borderColor: "divider",
                backgroundColor: "background.paper",
              }}
            >
              {/* Three bands, each full width: what the land is, the controls
                  that fit rows into it, and what came out. Two columns side by
                  side left the sentences and the buttons drifting past each
                  other with nothing lining up. */}
              <Stack spacing={1.75}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Parcel
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {parcelFit
                      ? `${describeArea(parcelFit.area_m2)} boundary, ${describeArea(parcelFit.usable_area_m2)} inside the setback. `
                      : "Boundary drawn on the site map. "}
                    Rows are laid inside it at the pitch and azimuth above, and the row count
                    comes from the land rather than from a guess.
                  </Typography>
                </Box>

                <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap>
                  <TextField
                    size="small"
                    label="Setback from the boundary (m)"
                    type="number"
                    value={form.setback_m}
                    onChange={(e) => handleField("setback_m", e.target.value)}
                    inputProps={{ min: 0, step: "0.5" }}
                    helperText="Fence line, access track and any planning offset, kept clear of modules."
                    FormHelperTextProps={{ sx: { mx: 0 } }}
                    sx={{ width: 260 }}
                  />
                  {Number(totalModules) > 0 ? (
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={Boolean(form.fit_to_design)}
                          onChange={(e) => handleField("fit_to_design", e.target.checked)}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          Only as many rows as the design needs ({Number(totalModules).toLocaleString("en-GB")} modules)
                        </Typography>
                      }
                      sx={{ ml: -0.5, mt: 0.25 }}
                    />
                  ) : null}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={fitRowsToParcel}
                    disabled={parcelBusy || !(toFiniteNumber(form.minimum_distance_m) > 0)}
                    sx={{ whiteSpace: "nowrap", mt: 0.5 }}
                  >
                    {parcelBusy ? "Fitting…" : "Fit rows to parcel"}
                  </Button>
                </Stack>

                {parcelFit ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      flexWrap: "wrap",
                      pt: 1.5,
                      borderTop: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="body2" sx={{ flex: "1 1 320px" }}>
                      {parcelFit.string_columns ? (
                        <>
                          <b>
                            {parcelFit.row_count} rows, {parcelFit.tables} tables, holding{" "}
                            {Number(parcelFit.strings).toLocaleString("en-GB")} whole strings of{" "}
                            {parcelFit.string_columns} columns.
                          </b>{" "}
                          {Number(parcelFit.strings) >= Number(parcelFit.limited_to_strings)
                            ? `Every one of the design's ${Number(parcelFit.limited_to_strings).toLocaleString("en-GB")} strings has a row; the tables are trimmed to whole strings, so ${(
                                Number(parcelFit.modules) - Number(totalModules)
                              ).toLocaleString("en-GB")} positions stay empty where a string does not divide into the stacked rows.`
                            : `The design has ${Number(parcelFit.limited_to_strings).toLocaleString("en-GB")} strings; ${(
                                Number(parcelFit.limited_to_strings) - Number(parcelFit.strings)
                              ).toLocaleString("en-GB")} do not fit on this land at this pitch.`}
                        </>
                      ) : (
                        <>
                          <b>
                            {parcelFit.row_count} rows, {parcelFit.tables} tables,{" "}
                            {parcelFit.limited_to_modules ? "holding" : "room for"}{" "}
                            {Number(parcelFit.modules).toLocaleString("en-GB")} modules.
                          </b>{" "}
                          {Number(totalModules) > 0
                            ? Number(parcelFit.modules) >= Number(totalModules)
                              ? `The design's ${Number(totalModules).toLocaleString("en-GB")} modules fit, with ${(
                                  Number(parcelFit.modules) - Number(totalModules)
                                ).toLocaleString("en-GB")} positions to spare.`
                              : `The design has ${Number(totalModules).toLocaleString("en-GB")} modules; ${(
                                  Number(totalModules) - Number(parcelFit.modules)
                                ).toLocaleString("en-GB")} do not fit on this land at this pitch.`
                            : ""}
                        </>
                      )}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                      <Button size="small" variant="contained" onClick={applyParcelRows}>
                        Use these rows
                      </Button>
                      {form.row_modules ? (
                        <Button size="small" color="warning" onClick={clearParcelRows}>
                          Back to typed rows
                        </Button>
                      ) : null}
                    </Stack>
                  </Box>
                ) : null}
                {parcelError ? (
                  <Typography variant="caption" sx={{ color: "error.main", display: "block" }}>{parcelError}</Typography>
                ) : null}
              </Stack>
            </Paper>
          ) : null}

            {/* Pitch and row height are two numbers whose consequence is a
                shadow at a particular hour on a particular day. Reading it
                here, against the pitch being typed above, is the whole point:
                the geometry follows the draft, not the last saved layout. */}

            {!finalReportReady && <Alert severity="info" sx={{ mb: 1 }}>
              {tr("Η αναφορά σχεδίασης εκδίδεται ως προσχέδιο μέχρι να ολοκληρωθούν οι έλεγχοι γεωμετρίας, strings και σκιάσεων.", "The design report is marked as a draft until geometry, wiring and shading checks are complete.")}
            </Alert>}
            {roofModel?.steps.length > 0 && (() => {
              // Keep every drawn footprint intact; explain height mismatches
              // instead of silently moving ridge and hip vertices.
              const reference = roofModel.faces.find((f) => f.key === "active") || roofModel.faces[0];
              const differingEaveHeights = roofModel.faces.some((f) =>
                Math.abs(f.eaveHeightM - reference.eaveHeightM) > 0.01);
              return <Alert severity="warning" sx={{ mb: 1, borderRadius: "4px" }}
                action={<Stack direction="row" gap={0.5} flexWrap="wrap">
                  <Button size="small" color="inherit" variant="outlined" sx={{ borderRadius: "4px", textTransform: "none", whiteSpace: "nowrap" }}
                    onClick={closeRoofJoints}>{tr("Κλείσιμο ενώσεων", "Close roof joints")}</Button>
                  {differingEaveHeights && <Button size="small" color="inherit" variant="outlined" sx={{ borderRadius: "4px", textTransform: "none", whiteSpace: "nowrap" }}
                    onClick={() => applyRoofToAllFaces({ eaveHeightM: reference.eaveHeightM })}>
                    {tr(`Ύψος γείσου ${Math.round(reference.eaveHeightM * 100) / 100} m παντού`,
                      `Eave height ${Math.round(reference.eaveHeightM * 100) / 100} m everywhere`)}
                  </Button>}
                </Stack>}>
                {tr("Οι πλευρές της στέγης δεν συναντιούνται παντού: ", "The roof faces do not meet everywhere: ")}
                {roofModel.steps.map((m, i) => <span key={`${m.a}-${m.b}`}>{i > 0 ? "· " : ""}{faceLabel(m.a)} / {faceLabel(m.b)}: {tr("σκαλί", "step")} {Math.round(m.stepM * 100)} cm{" "}</span>)}
                <Typography variant="caption" display="block" sx={{ mt: 0.3 }}>
                  {tr("Ελέγξτε ύψος γείσου, κλίση και επιλεγμένο γείσο κάθε πλευράς. Κοινές κορυφές στην κάτοψη δεν εγγυώνται ότι οι κεκλιμένες επιφάνειες συναντιούνται και στο ίδιο ύψος (δεξί κλικ στην πλευρά → Βέλος κλίσης).",
                    "Check each face's eave height, pitch and selected eave. Shared plan vertices do not guarantee that sloped surfaces meet at the same height (right-click the face → Slope arrow).")}
                </Typography>
              </Alert>;
            })()}
            {roofRepairError && <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setRoofRepairError("")}>{roofRepairError}</Alert>}
            {/* Rows one by one. Collapsed: most fields are equal rows and the
                single count above is enough; opened, it is the truth the string
                map and the 3D view read. */}
            <Accordion
              disableGutters
              elevation={0}
              sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, "&:before": { display: "none" } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ flexWrap: "wrap" }} useFlexGap>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {tr("Πλαίσια ανά σειρά", "Rows one by one — modules along length")}
                  </Typography>
                  {/* One position per column when a row is one module high: the
                      columns, the positions and the modules were then the same
                      number three times over. */}
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {(() => {
                      const nRows = rowsForStrings.length;
                      const cols = rowsForStrings.reduce((a, c) => a + c, 0);
                      const tiersN = toFiniteNumber(form.row_vertical_modules) || 1;
                      const positions = cols * tiersN;
                      const mods = Number(totalModules) > 0 ? Number(totalModules) : null;
                      const n = (v) => v.toLocaleString(isGreek ? "el-GR" : "en-GB");
                      const counted = tiersN > 1
                        ? tr(`${n(cols)} στήλες · ${n(positions)} θέσεις`, `${n(cols)} columns · ${n(positions)} positions`)
                          + (mods != null ? tr(` για ${n(mods)} πλαίσια`, ` for ${n(mods)} modules`) : "")
                        : (mods == null || mods === positions
                          ? tr(`${n(positions)} πλαίσια`, `${n(positions)} modules`)
                          : tr(`${n(positions)} θέσεις για ${n(mods)} πλαίσια`, `${n(positions)} positions for ${n(mods)} modules`));
                      const source = form.row_modules
                        ? (form.parcel_tables ? tr("από το γεωτεμάχιο", "from the parcel") : tr("μήκη ορισμένα χειροκίνητα", "lengths set by hand"))
                        : tr("ίσες σειρές", "equal rows");
                      return `${tr(`${n(nRows)} ${nRows === 1 ? "σειρά" : "σειρές"}`, `${n(nRows)} ${nRows === 1 ? "row" : "rows"}`)} · ${counted} · ${source}`;
                    })()}
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, minmax(0, 1fr))", lg: "repeat(6, minmax(0, 1fr))", xl: "repeat(8, minmax(0, 1fr))" },
                    gap: 1,
                  }}
                >
                  {rowsForStrings.map((columns, index) => {
                    // What the number means on the ground, under each field:
                    // a bare "104" is a count of columns; "208 modules · 8
                    // strings · 118 m" is a row. Leftover columns that hold no
                    // whole string are called out, because that is the number
                    // a designer changes the field to fix.
                    const cols = Number(columns) || 0;
                    const tiersN = toFiniteNumber(form.row_vertical_modules) || 1;
                    const alongRow = modulePortrait === false ? toFiniteNumber(moduleLength) : toFiniteNumber(moduleWidth);
                    // Whole strings per row only matter when a string must stay
                    // on its own row. With continuous routing - the default, and
                    // the normal case on a roof - a string simply carries on into
                    // the next row, so "+7 columns" is not a leftover and must not
                    // be flagged on every row of a single array.
                    const perRowStrings = form.string_routing === "table" && rowSlots.columns > 0;
                    const stringsHere = perRowStrings ? Math.floor(cols / rowSlots.columns) : null;
                    const leftover = perRowStrings ? cols - stringsHere * rowSlots.columns : 0;
                    const bits = [tr(`${(cols * tiersN).toLocaleString("el-GR")} πλαίσια`, `${(cols * tiersN).toLocaleString("en-GB")} modules`)];
                    if (stringsHere != null) bits.push(`${stringsHere} string${stringsHere === 1 ? "" : "s"}${leftover ? tr(` +${leftover} στήλες`, ` +${leftover} col`) : ""}`);
                    // (helper text is a single node by MUI's design; the
                    // figures make it untranslatable either way)
                    if (alongRow) bits.push(`${Math.round(cols * alongRow).toLocaleString(isGreek ? "el-GR" : "en-GB")} m`);
                    const rowLabel = (form.row_labels && form.row_labels[index]) || `R${index + 1}`;
                    return (
                      <TextField
                        key={index}
                        size="small"
                        type="number"
                        label={rowLabel}
                        value={columns}
                        onChange={(e) => setRowColumns(index, e.target.value)}
                        inputProps={{ min: 1, step: 1, style: { padding: "6px 8px" } }}
                        InputLabelProps={{ shrink: true }}
                        InputProps={rowsForStrings.length > 1 ? { endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip arrow title={tr("Αφαίρεση σειράς", "Remove row")}>
                              <IconButton size="small" edge="end" aria-label={tr(`Αφαίρεση σειράς ${rowLabel}`, `Remove row ${rowLabel}`)}
                                          onClick={() => removeRow(index)} sx={{ p: 0.25 }}>
                                <CloseIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>) } : undefined}
                        helperText={
                          <Box component="span" sx={{ color: leftover ? "warning.main" : "text.secondary" }}>
                            {bits.join(" · ")}
                          </Box>
                        }
                        FormHelperTextProps={{ component: "div", sx: { mx: 0.5, mt: 0.25 } }}
                      />
                    );
                  })}
                </Box>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button size="small" variant="outlined" onClick={addRow}>{tr("Προσθήκη σειράς", "Add a row")}</Button>
                  {form.row_modules && !form.parcel_tables ? (
                    <Button size="small" color="warning" onClick={clearParcelRows}>
                      {tr("Επιστροφή σε ίσες σειρές", "Back to equal rows")}
                    </Button>
                  ) : null}
                </Stack>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
                  {tr("Κάθε τιμή ορίζει τα πλαίσια κατά μήκος της συγκεκριμένης σειράς. Το μήκος και το συνολικό πλήθος πλαισίων ενημερώνονται στην κάτοψη και στο 3D. Κάτω από το πεδίο εμφανίζονται τα πλαίσια και το μήκος της σειράς.", "Each value is the module columns in that row; the figures below show its modules, strings and length.")}
                  {rowSlots.columns > 0 && form.string_routing === "table" && tr(` Ένα string αυτού του σχεδιασμού χρειάζεται ${rowSlots.columns} στήλες. Οι επιπλέον στήλες δεν αρκούν για ένα πλήρες string.`, ` A string of this design takes ${rowSlots.columns} columns; extra columns are too few for a complete string.`)}
                  {tr(" Η αλλαγή μήκους κρατά σταθερό το πρώτο άκρο. Μπορείτε επίσης να σύρετε τη σειρά και τις λαβές της στην κάτοψη. Η προσθήκη σειράς γίνεται δίπλα στην τελευταία, εφόσον χωρά.", "Length edits keep the first end fixed. You can also drag the row and its handles in the plan. A new row is added beside the last one if it fits.")}
                </Typography>
              </AccordionDetails>
            </Accordion>
            {groundRows && <Box sx={{ height: 52, overflow: "auto", px: 1, py: 0.5 }} role="status">
              <Typography variant="body2" color={groundEditError || groundGeometryInvalid ? "warning.main" : "text.secondary"}>
              {groundEditError || (groundGeometryInvalid ? tr("Υπάρχουν σειρές έξω από τα όρια/την απόσταση ασφαλείας ή που επικαλύπτονται. Προσαρμόστε τις στην κάτοψη πριν αποθηκεύσετε.",
              "Some rows cross the boundary/setback or overlap. Adjust them in the plan before saving.") : groundStats?.variable ? tr(
              `Μεταβλητές αποστάσεις σειρών: ${groundStats.min.toFixed(2)}–${groundStats.max.toFixed(2)} m. Το πεδίο βήματος δείχνει τον μέσο όρο. Ορίστε κοινό βήμα και πατήστε «Εφαρμογή σε όλες τις σειρές» για ίσες αποστάσεις.`,
              `Variable row spacing: ${groundStats.min.toFixed(2)}–${groundStats.max.toFixed(2)} m. The pitch field shows the average. Set a common pitch and select “Apply to all rows” to restore equal spacing.`) : tr("Οι αλλαγές στην κάτοψη ενημερώνουν τη 3D σκηνή. Το μήκος αλλάζει ανά ολόκληρη στήλη πλαισίων.",
                "Plan edits update the 3D scene. Row length changes in whole module columns."))}
              </Typography></Box>}
            {groundGroupPicker}
            <RoofSiteEditor groundRows={groundRows} onGroundRowsChange={applyGroundRows} onAlignPanels={alignPanels} onRotatePanels={rotatePanels} panelQuads={planModules}
              onEditPanels={canEditPanels ? applyPlanEdit : null} onUndoPanelEdit={planUndo ? () => { planUndo(); setPlanUndo(null); } : null}
              missingPanels={missingPlanModules}
              unassignedSlots={manualWiring?.unassigned.map(p => `${p.row}:${p.column}:${p.tier}`) || []}
              value={siteGeometry} onChange={(value) => {
              const keys = ["heightM", "pitchDeg", "azimuthDeg", "rotationDeg", "eaveHeightM", "eaveEdge", "freeAzimuth", "footprint", "eastM", "northM"];
              const changedPlane = (a, b) => a && b && keys.some(key => JSON.stringify(a[key]) !== JSON.stringify(b[key]));
              if (changedPlane(siteGeometry?.roof, value.roof)) roofFitAnchorRef.current = "active";
              else {
                const edited = (value.obstacles || []).find(o => o.kind === "roof" && changedPlane(
                  siteGeometry?.obstacles?.find(old => old.id === o.id), o));
                if (edited) roofFitAnchorRef.current = `own:${edited.id}`;
              }
              handleField("site_geometry", value);
              if (value.roof && siteGeometry?.roof) {
                const next = value.roof, current = siteGeometry.roof;
                const changed = (key) => toFiniteNumber(next[key]) != null && Math.abs(Number(next[key]) - Number(current[key])) > 1e-7;
                if (changed("pitchDeg") || changed("azimuthDeg")) pushFaceAngles([{ planeId: null,
                  ...(changed("pitchDeg") ? { pitchDeg: Number(next.pitchDeg) } : {}),
                  ...(changed("azimuthDeg") ? { azimuthDeg: Number(next.azimuthDeg) } : {}) }]);
              }
            }}
              sharedObstacles={sharedObstacles}
              sharedFaces={sharedFaces}
              sharedRoofFaces={sharedRoofFaces}
              roofFaces={roofModel ? Object.fromEntries(roofModel.faces.map((f) => [f.key, f])) : null}
              activeLabel={orientationLabel?.index ?? 1}
              onSharedFaceEave={(planeId, eaveHeightM) => {
                if (eaveHeightM === "" || !Number.isFinite(Number(eaveHeightM))) return;
                roofFitAnchorRef.current = `plane:${planeId}`;
                const face = roofModel?.faces.find((f) => f.key === `plane:${planeId}`);
                editContextRoof(planeId, { eaveHeightM: Number(eaveHeightM), ...(face ? { eaveEdge: face.eaveEdge } : {}) });
              }}
              onFacePitch={(planeId, pitchDeg) => {
                if (pitchDeg === "" || !Number.isFinite(Number(pitchDeg))) return;
                roofFitAnchorRef.current = planeId == null ? "active" : `plane:${planeId}`;
                pinEave(planeId == null ? "active" : `plane:${planeId}`);
                pushFaceAngles([{ planeId, pitchDeg: Math.min(75, Math.max(0, Number(pitchDeg))) }]);
              }}
              onFaceAzimuth={(planeId, azimuthDeg) => {
                if (!Number.isFinite(Number(azimuthDeg))) return;
                roofFitAnchorRef.current = planeId == null ? "active" : `plane:${planeId}`;
                const key = planeId == null ? "active" : `plane:${planeId}`;
                const face = roofModel?.faces.find((f) => f.key === key);
                const azimuth = ((Number(azimuthDeg) % 360) + 360) % 360;
                const patch = { azimuthDeg: azimuth, freeAzimuth: true, azimuthSource: "manual",
                  ...(face ? { eaveEdge: face.eaveEdge, eaveHeightM: face.eaveHeightM } : {}) };
                if (planeId == null) {
                  const roof = siteGeometry.roof;
                  handleField("site_geometry", { ...siteGeometry, roof: { ...roof,
                    ...(!roof.footprint ? { footprint: roofFootprint(roof) } : {}), ...patch } });
                } else {
                  const roof = draftContextPlanes?.find((p) => p.id === planeId)?.siteGeometry?.roof;
                  if (!roof) return;
                  editContextRoof(planeId, { ...(!roof.footprint ? { footprint: roofFootprint(roof) } : {}), ...patch });
                }
                pushFaceAngles([{ planeId, azimuthDeg: azimuth }]);
              }}
              onApplyRoofToAll={applyRoofToAllFaces}
              onSharedObstacleChange={(planeId, id, patch) => {
                if (draftContextPlanes?.find(p => p.id === planeId)?.siteGeometry?.obstacles?.some(o => o.id === id && o.kind === "roof"))
                  roofFitAnchorRef.current = `pob:${planeId}:${id}`;
                editContextObstacles(planeId, (list) => list.map((o) => o.id === id ? { ...o, ...patch } : o));
              }}
              onSharedObstacleRemove={(planeId, id) => editContextObstacles(planeId, (list) => list.filter((o) => o.id !== id))}
              onSharedObstacleMove={(planeId, id, eastM, northM) => {
                // Back into the owning face's frame.
                const off = sceneOffset({ siteGeometry }, draftContextPlanes?.find((p) => p.id === planeId) || {});
                if (!off) return;
                const metres = (v) => Math.round(v * 1000) / 1000;
                editContextObstacles(planeId, (list) => list.map((o) => o.id === id
                  ? { ...o, eastM: metres(Number(eastM) - off.x), northM: metres(Number(northM) - off.y) } : o));
              }}
              onRoofAzimuthChange={onRoofAzimuthChange}
              onSelectPlane={onSelectPlane}
              viewRef={workspaceViewRef}
              onFacePv={onPromoteRoofFace || onDemoteRoofFace || onRoof ? handleFacePv : null}
              onRemovePvFace={onRoof ? (planeId) => {
                const plane = planeId == null ? null : draftContextPlanes?.find((p) => p.id === planeId);
                setRemoveChoice({ planeId, primary: planeId == null ? activeIsPrimary : Boolean(plane?.primary),
                  label: planeId == null ? (orientationLabel?.index ?? 1) : plane?.label });
              } : null}
              contextPlanes={draftContextPlanes}
              onContextFrameChange={(id, frame) => {
                if (workspaceId) {
                  const plane = draftContextPlanes?.find((p) => p.id === id);
                  if (!plane) return;
                  const layout = { ...plane.spacing, site_geometry: { ...plane.siteGeometry, frame } };
                  const next = { ...sharedSceneEditsRef.current, [id]: layout };
                  sharedSceneEditsRef.current = next;
                  setSceneEdits(next);
                } else setForm((prev) => ({ ...prev, context_frames: { ...prev.context_frames, [id]: frame } }));
              }}
              onRoof={onRoof} flush={installationType === "roof_flush"}
              hasMapPosition={Boolean(tablesOrigin(form.parcel_tables))}
              mapOrigin={groundOrigin}
              roofPitch={installationType === "roof_flush" ? surfaceTilt : roofPitch}
              roofAzimuth={installationType === "roof_flush" ? surfaceAzimuth : roofAzimuth} />
            <Dialog open={Boolean(pvChoice)} onClose={() => setPvChoice(null)} PaperProps={{ sx: { borderRadius: "6px", maxWidth: 460 } }}>
              <DialogTitle sx={{ pb: 0.5 }}>{tr("Φ/Β σε αυτή την πλευρά", "PV on this face")}</DialogTitle>
              <DialogContent>
                <Typography variant="body2" color="text.secondary">
                  {tr("Σε ποιον προσανατολισμό ανήκει; Ο προσανατολισμός παίρνει την κλίση και το αζιμούθιο της πλευράς.",
                    "Which orientation does it belong to? The orientation takes the face's pitch and azimuth.")}
                </Typography>
                <Stack gap={1} sx={{ mt: 1.5 }}>
                  {facelessPlanes.map((p) => <Button key={p.id} variant="outlined" sx={{ justifyContent: "flex-start", borderRadius: "4px", textTransform: "none" }}
                    onClick={() => { attachFace(pvChoice.obstacle, p); setPvChoice(null); }}>
                    {tr(`Προσανατολισμός ${p.label} (δεν έχει ακόμη πλευρά)`, `Orientation ${p.label} (no face yet)`)}
                  </Button>)}
                  {onPromoteRoofFace && <Button variant="contained" disableElevation sx={{ justifyContent: "flex-start", borderRadius: "4px", textTransform: "none" }}
                    onClick={() => { promoteFace(pvChoice.obstacle); setPvChoice(null); }}>
                    {tr("Νέος προσανατολισμός", "New orientation")}
                  </Button>}
                </Stack>
              </DialogContent>
              <DialogActions><Button onClick={() => setPvChoice(null)} sx={{ textTransform: "none" }}>{tr("Ακύρωση", "Cancel")}</Button></DialogActions>
            </Dialog>
            <Dialog open={Boolean(removeChoice)} onClose={() => setRemoveChoice(null)} PaperProps={{ sx: { borderRadius: "6px", maxWidth: 460 } }}>
              <DialogTitle sx={{ pb: 0.5 }}>{tr(`Διαγραφή της πλευράς ${removeChoice?.label ?? ""}`, `Delete face ${removeChoice?.label ?? ""}`)}</DialogTitle>
              <DialogContent>
                <Typography variant="body2" color="text.secondary">
                  {removeChoice?.primary
                    ? tr("Η πλευρά και ό,τι στέκεται επάνω της διαγράφονται. Ο προσανατολισμός 1 μένει, χωρίς πλευρά: μπορεί να πάρει άλλη με «Φ/Β».",
                      "The face and whatever stands on it are deleted. Orientation 1 stays, without a face: another face can be given to it with “PV”.")
                    : tr(`Διαγράφεται και ο προσανατολισμός ${removeChoice?.label ?? ""}· τα MPPT του επιστρέφουν στον προσανατολισμό 1. Για να μείνει η πλευρά ως σκίαση χωρίς πάνελ, ξετσεκάρετε μόνο το Φ/Β.`,
                      `Orientation ${removeChoice?.label ?? ""} is deleted too; its MPPTs return to orientation 1. To keep the face as shading without modules, untick its PV instead.`)}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setRemoveChoice(null)} sx={{ textTransform: "none" }}>{tr("Ακύρωση", "Cancel")}</Button>
                <Button color="error" variant="contained" disableElevation sx={{ textTransform: "none", borderRadius: "4px" }}
                  onClick={() => { const choice = removeChoice; setRemoveChoice(null); removeFace(choice); }}>{tr("Διαγραφή", "Delete")}</Button>
              </DialogActions>
            </Dialog>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: "background.paper", border: "1px solid", borderColor: "divider" }}>
              {groundGroupPicker}
              {onRoof && Number(totalModules) > 0 && !savedRoofLayout && (
                <Stack direction="row" spacing={0.75} alignItems="flex-start" role="status"
                  sx={{ mb: 1, color: "text.secondary" }}>
                  <InfoOutlinedIcon sx={{ fontSize: 16, mt: "2px", flexShrink: 0 }} />
                  <Typography variant="caption">
                    {tr("Δεν έχει αποθηκευτεί διάταξη στη στέγη. Τοποθετήστε τα πάνελ στο 3D και αποθηκεύστε τη διάταξη.",
                      "No roof layout has been saved. Position the modules in 3D and save the layout.")}
                  </Typography>
                </Stack>
              )}
              {draftMountingPlane?.changed ? (
                <Alert severity="info" variant="outlined" sx={{ mb: 1.5, py: 0.25 }}>
                  Rows drawn at {draftMountingPlane.tilt.toFixed(1)}° from the horizontal facing{" "}
                  {draftMountingPlane.azimuth.toFixed(1)}°: the design tilt of {draftMountingPlane.designTilt.toFixed(1)}° is
                  measured from the support surface, and the racking follows its {draftMountingPlane.slopeTilt.toFixed(1)}° slope.
                  This is also the plane the simulation models.
                </Alert>
              ) : null}
              <ShadingScene3D
                calculateOnSave={automaticShading}
                workspaceViewRef={workspaceViewRef}
                samplesPerModule={form.shading_samples_per_module}
                onSamplesPerModuleChange={(value) => handleField("shading_samples_per_module", value)}
                moduleOffsets={resolvedModuleOffsets}
                onModuleOffsetsChange={onRoof && !form.parcel_tables ? (offsets) => {
                  if (!effectiveModuleArray || !alignmentScene) handleField("module_offsets", offsets);
                  else setForm((current) => ({ ...current, module_offsets: offsets,
                    module_array: roofModuleArray(moduleQuads(alignmentRows, alignmentScene.verticalModules), offsets,
                      alignmentScene.terrain.groundZ, effectiveModuleArray.axisBearingDeg, effectiveModuleArray) }));
                } : null}
                onArrayOffsetChange={onRoof && !form.parcel_tables ? (offset) => setForm((prev) => ({ ...prev,
                  array_offset_east_m: toFieldValue(offset.eastM), array_offset_north_m: toFieldValue(offset.northM),
                })) : null}
                onSiteGeometryChange={(value) => handleField("site_geometry", value)}
                onSelectPlane={workspaceId ? onSelectPlane : null}
                onContextLayoutChange={workspaceId ? (id, layout) => {
                  const next = { ...sharedSceneEditsRef.current, [id]: layout };
                  const plane = contextPlanes?.find((p) => p.id === id);
                  const original = plane && { ...plane.spacing, site_geometry: plane.siteGeometry };
                  if (original && sceneEditFields.every((key) => JSON.stringify(layout[key]) === JSON.stringify(original[key]))) delete next[id];
                  sharedSceneEditsRef.current = next;
                  setSceneEdits(next);
                } : null}
                onContextFrameChange={workspaceId ? null : (id, frame) => setForm((prev) => {
                  const context_frames = { ...prev.context_frames };
                  const original = contextPlanes?.find((p) => p.id === id)?.siteGeometry?.frame;
                  if (original && ["eastM", "northM", "elevationM"].every((key) => Number(original[key]) === Number(frame[key]))) delete context_frames[id];
                  else context_frames[id] = frame;
                  return { ...prev, context_frames };
                })}
                snapshotRef={sceneSnapshotRef}
                modulePositions={electricalPositions}
                moduleMappingValid={!electricalLayoutChanged && Boolean(stringLayout?.check?.consistent)}
                onEditPlanModules={canEditPanels ? applyPlanEdit : null}
                excludedSlots={excludedSlots}
                missingModuleCount={missingModules}
                latitude={latitude}
                longitude={longitude}
                surfaceTilt={draftSurfaceTilt}
                surfaceAzimuth={draftSurfaceAzimuth}
                surfaceType={surfaceType}
                // The parcel's size, roughly, so the horizon and the sun path
                // stand beyond the plot rather than round the building.
                siteRadiusM={parcelRing ? Math.sqrt(ringAreaM2(parcelRing) / Math.PI) : null}
                pitch={previewSpacingInfo.minimum_distance_m}
                rowVerticalModules={previewSpacingInfo.row_vertical_modules}
                groundClearance={toFiniteNumber(form.ground_clearance_m)}
                fieldRows={toFiniteNumber(form.field_rows)}
                rowModules={form.row_modules}
                parcelTables={form.parcel_tables}
                parcelOutline={form.parcel_outline}
                arrayOffsetEast={toFiniteNumber(form.array_offset_east_m) ?? 0}
                arrayOffsetNorth={toFiniteNumber(form.array_offset_north_m) ?? 0}
                moduleLength={moduleLength}
                moduleWidth={moduleWidth}
                totalModules={totalModules}
                slopeTilt={slopeTilt}
                slopeAzimuth={slopeAzimuth}
                portrait={modulePortrait}
                halfCut={moduleHalfCut}
                bypassDiodes={moduleBypassDiodes}
                moduleTopology={moduleTopology}
                detailedMode={detailedShadingEnabled}
                scenePrepared={detailedSceneReady && !measurementDraftChanged && !contextHasDrafts && !shadingUnsaved}
                nearShadingModel={nearShadingModel}
                electricalEffect={nearShadingElectricalEffect}
                mountType={mountType}
                installationType={installationType}
                siteGeometry={siteGeometry}
                roofPitch={installationType === "roof_flush" ? surfaceTilt : roofPitch}
                roofAzimuth={installationType === "roof_flush" ? surfaceAzimuth : roofAzimuth}
                onRoofStatus={setRoofStatus}
                axisOrientation={axisOrientation}
                maxAngle={maxAngle}
                backTrack={backTrack}
                utcOffsetHours={utcOffsetHours}
                timeZoneLabel={timeZoneLabel}
                savedTable={sceneShading?.stale ? null : sceneShading}
                savedTableUnsaved={shadingUnsaved}
                preparationIssue={shadingSaveIssue || sceneShading?.preparationIssue}
                shadingRequired={!detailedShadingEnabled}
                shadingOutdated={shadingOutdated}
                projectId={projectId}
                // Into the draft, not into the project: the layout's Save
                // stores it, with the layout it was calculated on.
                onObstacleTableChange={onSave
                  ? (payload) => {
                    if (payload) setShadingSaveIssue(null);
                    // A model removed: what was calculated with it no longer holds.
                    if (!payload && sceneShadingDraft && onDiscardSceneShading) onDiscardSceneShading();
                    setDraftShading(payload || obstacleShading
                      ? { payload: payload || null, key: shadingDraftKey } : null);
                  }
                  : null}
                onMeasureAll={onSave ? calculateAllFaces : null}
                measureAllCount={shadingFaceCount}
                onPlacementChange={setMeshPlacement}
                horizonProfile={horizonProfile}
                horizonEnabled={horizonEnabled}
                onHorizonEnabledChange={onHorizonEnabledChange}
                contextPlanes={draftContextPlanes}
                onLayoutFromModel={(m) => {
                  // Straight into the form, not into the project: the fields
                  // light up as changed and the layout's own Save commits them.
                  setForm((prev) => ({
                    ...prev,
                    ...(m.pitch > 0 ? { minimum_distance_m: toFieldValue(Math.round(m.pitch * 100) / 100) } : {}),
                    ...(m.rows > 0 ? { field_rows: toFieldValue(m.rows) } : {}),
                    ...(m.footprint > 0 ? { terrace_area_m2: toFieldValue(Math.round(m.footprint * 10) / 10), terrace_area_mode: "manual" } : {}),
                  }));
                }}
              />
            </Paper>




            {/* The strings on the rows -- the join between the electrical design
                and the field. Reads the same row list the scene above draws. */}
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: "background.paper", border: "1px solid", borderColor: "divider" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Strings on the rows
              </Typography>
              <AccessibleSelectTextField size="small" label={tr("Διαδρομή strings", "String routing")} value={form.string_routing}
                onChange={(e) => handleField("string_routing", e.target.value)} sx={{ mb: 2, minWidth: 270, maxWidth: "100%" }}
                helperText={installationType === "roof_flush"
                  ? tr("Συνέχεια: string σε πολλές γραμμές. Μία γραμμή: κάθε string χωρά ολόκληρο σε μία. Δεν υπολογίζονται μήκη καλωδίων.", "Continue: a string may span grid lines. One line: each string must fit entirely on one. Cable lengths are not calculated.")
                  : form.string_routing === "continuous"
                    ? tr("Ένα string μπορεί να συνεχίζεται στην επόμενη σειρά. Οι θέσεις γεμίζουν διαδοχικά· ελέγξτε τη διαδρομή καλωδίων μεταξύ σειρών.", "A string may continue onto the next row. Positions are filled consecutively; check cable routes between rows.")
                    : tr("Κάθε string μένει ολόκληρο σε μία σειρά και δεσμεύει ολόκληρες στήλες. Μία σειρά μπορεί να φιλοξενεί πολλά strings. Οι ελεύθερες θέσεις μπορεί να μην αρκούν για άλλο πλήρες string.", "Each string stays on one row and reserves whole columns. A row can hold several strings. Spare positions may be too fragmented for another complete string.")}>
                <MenuItem value="continuous">{installationType === "roof_flush" ? tr("Συνέχεια μεταξύ γραμμών", "Continue across grid lines") : tr("Συνέχεια μεταξύ σειρών", "Continue across rows")}</MenuItem>
                <MenuItem value="table">{installationType === "roof_flush" ? tr("Κάθε string σε μία γραμμή", "Each string on one grid line") : tr("Κάθε string σε μία σειρά", "Each string on one row")}</MenuItem>
              </AccessibleSelectTextField>
              <StringLayoutMap
                systemDesign={systemDesign}
                allowCrossRows={form.string_routing === "continuous"}
                onLayoutChange={setStringLayout}
                rowModules={rowsForStrings}
                excludedSlots={excludedSlots}
                assignments={form.manual_layout_edited ? form.module_assignments || [] : null}
                rowLabels={form.row_modules ? form.row_labels : null}
                tiers={toFiniteNumber(form.row_vertical_modules) || 1}
                overrides={form.string_overrides}
                onOverridesChange={form.manual_layout_edited ? null : (next) => setForm((prev) => ({ ...prev, string_overrides: next }))}
                accessToken={accessToken}
              />
            </Paper>

            {/* Reference material last: the side-view sketch and the spacing
                method. Useful, consulted rarely, so it does not sit between the
                inputs and the picture they produce. */}
            {installationType === "roof_flush" ? <Alert severity="info">{tr(
              "Τα συνεπίπεδα πλαίσια δεν σκιάζουν το ένα το άλλο. Εμπόδια και άλλες επιφάνειες μπορούν να τα σκιάζουν. Η αδιαφανής στέγη αποκλείει το κέρδος πίσω όψης.",
              "Coplanar modules do not shade each other. Obstacles and other surfaces can still shade them. The opaque supporting roof excludes rear-side gain.")}</Alert> : null}
        </Stack>
        </Box>
      </PanelShellContent>
      <Snackbar open={Boolean(saveNotice)} anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={6000} onClose={() => setSaveNotice(null)}>
        <Alert onClose={() => setSaveNotice(null)} severity={saveNotice?.severity || "info"}
          variant="filled" sx={{ borderRadius: 2, fontWeight: 700 }}>
          {saveNotice?.message}
        </Alert>
      </Snackbar>
      <PanelShellActions inline={inline}>
        {!inline && <Button size="small" disabled={saving} onClick={onClose}>Cancel</Button>}
        <Button
          size="small"
          variant="contained"
          onClick={handleSave}
          disabled={saving || !hasGeometryInput || Boolean(validationError)}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {saving && detailedShadingEnabled
            ? tr("Προετοιμασία και αποθήκευση…", "Preparing and saving…")
            : onRoof && unsavedElsewhere.length
            ? tr("Αποθήκευση όλων των πλευρών", "Save all faces")
            : tr("Αποθήκευση διάταξης", "Save layout")}
        </Button>
      </PanelShellActions>
      <SunPathDialog
        open={sunPathOpen}
        onClose={() => setSunPathOpen(false)}
        lat={latitude}
        lon={longitude}
        profile={horizonEnabled === false ? null : horizonProfile}
        projectName={projectName}
      />
    </PanelShell>
  );
}
